import { useRef, useState, useCallback, useContext, useEffect } from "react";
import Box from "@mui/joy/Box";
import Stack from "@mui/joy/Stack";
import Sheet from "@mui/joy/Sheet";
import Button from "@mui/joy/Button";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import Divider from "@mui/joy/Divider";
import Slider from "@mui/joy/Slider";
import Tabs from "@mui/joy/Tabs";
import TabList from "@mui/joy/TabList";
import Tab from "@mui/joy/Tab";
import TabPanel from "@mui/joy/TabPanel";
import Dropdown from "@mui/joy/Dropdown";
import Menu from "@mui/joy/Menu";
import MenuButton from "@mui/joy/MenuButton";
import MenuItem from "@mui/joy/MenuItem";
import Tooltip from "@mui/joy/Tooltip";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ViewSidebarRoundedIcon from "@mui/icons-material/ViewSidebarRounded";
import StyleRoundedIcon from "@mui/icons-material/StyleRounded";
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import StickerPlaceholderIcon from "../components/StickerPlaceholderIcon.jsx";

import { arrayMove } from "@dnd-kit/sortable";

import ModalBookSize from "../components/models/ModalBookSize.jsx";
import ModalBookPages from "../components/models/ModalBookPages.jsx";
import useBookSync from "../hooks/useBookSync.js";
import ModalExport from "../components/models/ModalExport.jsx";
import { bookApi } from "../api.js";
import BottomPagePanelMini from "../components/BottomPagePanelMini/BottomPagePanelMini.jsx";
import { FIT_OPTIONS, LAYOUTS, POSITION_OPTIONS, getSlotEdges, countSlots } from "../layouts.config.jsx";
import { getSpineFraction, getSlotBleedFractions, getPageHiddenFractions, getVisiblePageSize, getSlotWidthFraction, getSlotHeightFraction, getFramePageInsets, getFrameWorkSize } from "../spine.config.js";
import Header from "../components/Header/Header.jsx";
import { LayoutContext } from "../context.jsx";
import Sidebar from "../components/Sidebar/Sidebar.jsx";
import BottomNav from "../components/BottomNav.jsx";
import { closeSidebar } from "../utils.jsx";
import BottomSheetTemplates from "../components/BottomSheet/BottomSheetTemplates.jsx";
import BottomSheetSettings from "../components/BottomSheet/BottomSheetSettings.jsx";
import BottomSheetMyBooks from "../components/BottomSheet/BottomSheetMyBooks.jsx";
import ModalMyBooks, { MiniCoverPreview } from "../components/models/ModalMyBooks.jsx";
import { useIsMobile } from "../hooks/useBreakpoint.js";
import ModalPhotoEditor from "../components/models/ModalPhotoEditor.jsx";
import { coverPhotoGeometry, normalizeCoverShape, coverContentLayout } from "../coverPhotoGeometry";
import PdfCoverText from "../components/PdfCoverText.jsx";
import { validateBook } from "../hooks/useBookValidation.js";
import ModalCart from "../components/models/ModalCart.jsx";
import ModalCoverPicker from "../components/models/ModalCoverPicker.jsx";
import BottomSheetCoverPicker from "../components/BottomSheet/BottomSheetCoverPicker.jsx";
import ModalGift from "../components/models/ModalGift.jsx";
import BookLifetimeChip from "../components/BookLifetimeChip.jsx";
import SaveStatusChip from "../components/SaveStatusChip.jsx";
import { CircularProgress, Modal, ModalDialog } from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";
import ModalTemplates from "../components/models/ModalTemplates.jsx";
import ModalPreviewHint from "../components/models/ModalPreviewHint.jsx";
import CoverDateControls from "../components/CoverDateControls.jsx";
import { computeCoverDateDisplay, resolveCoverDateMode } from "../coverDate.js";
import CoverLayoutPicker from "../components/models/CoverLayoutPicker.jsx";
import { pdfPageCells, cellStyle } from "../pageGeometry.js";
import PageMiniPreview from "../components/PageMiniPreview.jsx";
import CoverPresetGallery from "../components/models/CoverPresetGallery.jsx";
import { getBooksProblems } from "../hooks/checkBooksReady.js";
import { logUi, logEvent } from "../logger.js";
import { fileMeta, imageMeta } from "../photoDiag.js";
import { confirmLayoutModal } from "../components/confirmLayoutModal";
import { confirmRetryPaymentModal } from "../components/confirmRetryPaymentModal";
import { confirmDuplicatePhotoModal } from "../components/confirmDuplicatePhotoModal";
import { pendingOrderModal } from "../components/pendingOrderModal.jsx";
import { confirmLeaveBookModal } from "../components/confirmLeaveBookModal.jsx";
import { coverDateModePatch } from "../coverDateDefaults.js";
import { sanitizeText, sanitizeName  } from "../sanitize.js";
import { APPROVED_COVER_INCOMPATIBLE, APPROVED_COVER_TEXT_COLORS } from "../coverPaletteMatrix";


// ─── Константы обложки (настраиваемые параметры для разработчиков) ────────────
export const COVER_CONFIG = {
  // Шрифт обложки (подключить через @font-face, должен совпадать с сервером)
  fontFamily: "'HalvarBreit', 'Helvetica Neue', Arial, sans-serif",
  capRatio: 0.72, // доля cap height от font-size для данного шрифта

  // Safe zone = 8мм от края (8/210 по высоте, 8/140 по ширине)
  safeZoneSidePercent: 5.71,     // 8/140

  // Блок заголовка (жёлтая зона из разметки)
  titleBlockTopPercent: 7.5,    // 8/210 — от верха
  titleBlockHeightPercent: 12.62, // 26.5/210 — ФИКСИРОВАНА

  // Cap height заголовка (в долях от высоты блока заголовка 26.5мм)
  titleCapHeightMaxRatio: 0.566, // 15/26.5
  titleCapHeightMinRatio: 0.302, // 8/26.5
  titleLineGapRatio: 0.075,      // 2/26.5

  // Блок года
  yearGapPercent: 1.8,          // 7.17/210
  yearHeightPercent: 2.57,       // 5.4/210
  yearTargetWidthPercent: 17.14, // 24/140

  // Сетка 2×2 фото на задней части
  photoGridTopPercent: 30.5,    // (8+26.5+7.17+5.4+6.92)/210
  photoGridWidthPercent: 65.71,  // 92/140
  photoGridHeightPercent: 58.10, // 122/210
  photoGridGap: 1.43,            // 2/140

  // Стикер (передняя часть) — та же зона что и фото-грид
  stickerMaxHeightPercent: 53,
  stickerSidePercent: 10,

  // Фото на передней обложке (макет "photo") — зона ≈ стикера, с отступами
  // сверху (от года) и снизу (над лого). Держать в синхроне с бэком
  // (CoverGeometry.front_photo_*_pad_mm). Значения — ПЛЕЙСХОЛДЕР.
  frontPhotoIndex: 4,
  frontPhotoTopPercent: 58.6,        // центр по вертикали — как у стикера
  frontPhotoSidePercent: 26,         // боковой отступ (было 10) → зона уже, квадратнее
  frontPhotoHeightPercent: 40,       // высота зоны (было 46) → квадрат

  // Корешок
  spineInnerPadding: 0,

  // Логотип
  logoHeightPercent: 5,
  logoImageMaxHeightPx: 10,
  logoImageMaxHeightDesktopPx: 20,
  logoBottomPercent: 4,
  logoText: "MAGIC BOARD",
  logoImageSrc: "/logo.png",
};

export const COVER_BACK_CELL_WIDTH_MM = 45;
export const COVER_BACK_CELL_HEIGHT_MM = 60;

// Пропорция кропа фото задней обложки берётся под РЕАЛЬНУЮ ячейку грида в
// конструкторе, а не под «чистые» 45/60. Причина — CSS Grid: и columnGap, и
// rowGap считаются в % от ШИРИНЫ контейнера, поэтому по вертикали зазор
// физически меньше, и ячейка получается чуть выше номинала.
// Грид (COVER_CONFIG): ширина ≈ 65.71% стороны 140 мм = 92 мм,
// высота ≈ 58.10% стороны 210 мм = 122 мм, гэп photoGridGap = 1.43% от ширины.
// Считаем аспект ячейки ровно так, как её строит браузер:
const _COVER_GRID_W_MM = 140 * 0.6571;                 // ≈ 92.0
const _COVER_GRID_H_MM = 210 * 0.5810;                 // ≈ 122.0
const _COVER_GRID_GAP_MM = _COVER_GRID_W_MM * 0.0143;  // гэп в % от ШИРИНЫ (CSS)
export const COVER_BACK_CELL_ASPECT =
  ((_COVER_GRID_W_MM - _COVER_GRID_GAP_MM) / 2) /
  ((_COVER_GRID_H_MM - _COVER_GRID_GAP_MM) / 2);        // ≈ 0.7514

// Аспект зоны фото ПЕРЕДНЕЙ обложки (макет "photo"). Считаем из COVER_CONFIG,
// чтобы кроп в редакторе точно совпадал с тем, как фото рисуется на обложке.
// Ширина стороны = 140 мм, высота = 210 мм.
export const COVER_FRONT_PHOTO_ASPECT =
  (((100 - 2 * COVER_CONFIG.frontPhotoSidePercent) / 100) * 140) /
  ((COVER_CONFIG.frontPhotoHeightPercent / 100) * 210);   // ≈ 0.81

// ─── Формат A4: конфиг обложки (синхронно с A4_COVER_GEOMETRY в cover_core.py) ─
// Переопределяем ТОЛЬКО то, что задано разметкой A4: сторона 195×250, safe zone
// 18 мм, задняя сетка 54×72/зазор 3 (грид 111×147), ширина заголовка (=safe zone)
// и цель года. Вертикальный ритм (позиции блоков) оставлен как у A5 — это ПРЕВЬЮ,
// печатный файл раскладывает ядро cover_core само. ⚠ при желании подстроить по
// эталону.
export const COVER_CONFIG_A4 = {
  ...COVER_CONFIG,
  safeZoneSidePercent: 9.23,       // 18/195
  titleBlockHeightPercent: 10.60,  // 26.5/250 — блок заголовка = те же 26.5 мм
  yearTargetWidthPercent: 12.31,   // 24/195
  photoGridWidthPercent: 56.92,    // 111/195
  photoGridHeightPercent: 58.80,   // 147/250
  photoGridGap: 1.54,              // 3/195 (в % от ШИРИНЫ, как CSS grid gap)
};

export const COVER_BACK_CELL_WIDTH_MM_A4 = 54;
export const COVER_BACK_CELL_HEIGHT_MM_A4 = 72;

// A4-кроп-аспекты (как их строит браузер) — по стороне 195×250 и A4-конфигу.
const _A4_SIDE_W_MM = 195, _A4_SIDE_H_MM = 250;
const _A4_GRID_W_MM = _A4_SIDE_W_MM * (COVER_CONFIG_A4.photoGridWidthPercent / 100);   // ≈ 111
const _A4_GRID_H_MM = _A4_SIDE_H_MM * (COVER_CONFIG_A4.photoGridHeightPercent / 100);  // ≈ 147
const _A4_GRID_GAP_MM = _A4_GRID_W_MM * (COVER_CONFIG_A4.photoGridGap / 100);          // гэп в % от ШИРИНЫ (CSS)
export const COVER_BACK_CELL_ASPECT_A4 =
  ((_A4_GRID_W_MM - _A4_GRID_GAP_MM) / 2) /
  ((_A4_GRID_H_MM - _A4_GRID_GAP_MM) / 2);   // ≈ 0.752 (как у A5 — 54:72 = 45:60)
export const COVER_FRONT_PHOTO_ASPECT_A4 =
  (((100 - 2 * COVER_CONFIG_A4.frontPhotoSidePercent) / 100) * _A4_SIDE_W_MM) /
  ((COVER_CONFIG_A4.frontPhotoHeightPercent / 100) * _A4_SIDE_H_MM);   // ≈ 0.95

// A4, если высота стороны ≈ 250 мм (A5 ≈ 210). Порог 230 — как is_a4_book на бэке.
export function isA4CoverSize(bookSize) {
  return (bookSize?.heightMm || 0) >= 230;
}
export function getCoverConfig(bookSize) {
  return isA4CoverSize(bookSize) ? COVER_CONFIG_A4 : COVER_CONFIG;
}
export function getCoverCropAspects(bookSize) {
  return isA4CoverSize(bookSize)
    ? { front: COVER_FRONT_PHOTO_ASPECT_A4, back: COVER_BACK_CELL_ASPECT_A4 }
    : { front: COVER_FRONT_PHOTO_ASPECT,    back: COVER_BACK_CELL_ASPECT };
}

// ─── Палитры цветов для обложки ───────────────────────────────────────────────
export const COVER_PALETTES = {
  bg: [
    // Пастельные светлые
    "#F7F1E7", "#FBF3D8", "#F2E7F2", "#C4DCC7", "#EDC9B5", "#BCD8F1",
    "#F7E4AC", "#BCBC94", "#ECCADD", "#A6CFCF",
    // Яркие тёплые
    "#EFD72C", "#E9B97E", "#D9877E", "#D8862B", "#D783B0",
    // Средние холодные
    "#82C1DE", "#219EDA", "#5BAEB8", "#C6D571", "#B5C93B",
    "#7AB47D", "#41A298", "#37984E", "#2C4F35",
    // Насыщенные и тёмные
    "#C42B2B", "#834A90", "#5B2744", "#67462A", "#30364E", "#3E5098",
    // Нейтральный
    "#ECECEC",
  ],
  title: [
    "#FFFFFF",
    "#EFD72C", "#D8862B", "#C42B2B",
    "#834A90", "#3E5098", "#219EDA",
    "#41A298", "#37984E", "#B5C93B",
    "#30364E", "#5B2744", "#67462A", "#2C4F35",
    "#E9B97E", "#D9877E", "#D783B0", "#82C1DE", "#7AB47D", "#C6D571",
    "#BCBC94", "#C4DCC7", "#F7F1E7", "#EDC9B5", "#A6CFCF",
    "#BCD8F1", "#5BAEB8", "#F2E7F2", "#ECCADD", "#FBF3D8", "#F7E4AC",
  ],
  accent: [
    "#FFFFFF",
    "#EFD72C", "#D8862B", "#C42B2B",
    "#834A90", "#3E5098", "#219EDA",
    "#41A298", "#37984E", "#B5C93B",
    "#30364E", "#5B2744", "#67462A", "#2C4F35",
    "#E9B97E", "#D9877E", "#D783B0", "#82C1DE", "#7AB47D", "#C6D571",
    "#BCBC94", "#C4DCC7", "#F7F1E7", "#EDC9B5", "#A6CFCF",
    "#BCD8F1", "#5BAEB8", "#F2E7F2", "#ECCADD", "#FBF3D8", "#F7E4AC",
  ],
};

// ─── Несочетаемые пары цветов (цвет фона → цвета текста, которые нельзя выбрать) ──
export const COLOR_INCOMPATIBLE = {};

const INCOMPATIBLE_PAIRS = [
  // ── Фон #D8862B (2) ──
  ["#D8862B", "#D9877E"], ["#D8862B", "#D783B0"], ["#D8862B", "#219EDA"],
  ["#D8862B", "#41A298"], ["#D8862B", "#37984E"], ["#D8862B", "#82C1DE"],
  ["#D8862B", "#7AB47D"],

  // ── Фон #C42B2B (3) ──
  ["#C42B2B", "#D8862B"], ["#C42B2B", "#D9877E"],

  // ── Фон #D783B0 (7) ──
  ["#D783B0", "#EDC9B5"], ["#D783B0", "#A6CFCF"], ["#D783B0", "#5BAEB8"],

  // ── Фон #834A90 (8) ──
  ["#834A90", "#5BAEB8"],

  // ── Фон #219EDA (10) ──
  ["#219EDA", "#BCBC94"], ["#219EDA", "#EDC9B5"], ["#219EDA", "#BCD8F1"],
  ["#219EDA", "#5BAEB8"], ["#219EDA", "#ECCADD"], ["#219EDA", "#F7E4AC"],

  // ── Фон #41A298 (11) ──
  ["#41A298", "#BCBC94"], ["#41A298", "#EDC9B5"], ["#41A298", "#A6CFCF"],
  ["#41A298", "#5BAEB8"], ["#41A298", "#ECCADD"],

  // ── Фон #37984E (12) ──
  ["#37984E", "#EDC9B5"], ["#37984E", "#5BAEB8"], ["#37984E", "#F2E7F2"],
  ["#37984E", "#ECCADD"], ["#37984E", "#F7E4AC"],

  // ── Фон #82C1DE (13) ──
  ["#82C1DE", "#BCBC94"], ["#82C1DE", "#C4DCC7"], ["#82C1DE", "#EDC9B5"],
  ["#82C1DE", "#A6CFCF"], ["#82C1DE", "#BCD8F1"], ["#82C1DE", "#5BAEB8"],
  ["#82C1DE", "#ECCADD"],

  // ── Фон #7AB47D (14) ──
  ["#7AB47D", "#BCBC94"], ["#7AB47D", "#C4DCC7"], ["#7AB47D", "#EDC9B5"],
  ["#7AB47D", "#A6CFCF"], ["#7AB47D", "#BCD8F1"], ["#7AB47D", "#5BAEB8"],
  ["#7AB47D", "#ECCADD"], ["#7AB47D", "#F7E4AC"],

  // ── Фон #B5C93B (16) ──
  ["#B5C93B", "#BCBC94"], ["#B5C93B", "#C4DCC7"], ["#B5C93B", "#F7F1E7"],
  ["#B5C93B", "#EDC9B5"], ["#B5C93B", "#A6CFCF"], ["#B5C93B", "#BCD8F1"],
  ["#B5C93B", "#5BAEB8"], ["#B5C93B", "#F2E7F2"], ["#B5C93B", "#ECCADD"],
  ["#B5C93B", "#F7E4AC"],

  // ── Фон #C6D571 (19) ──
  ["#C6D571", "#BCBC94"], ["#C6D571", "#C4DCC7"], ["#C6D571", "#F7F1E7"],
  ["#C6D571", "#EDC9B5"], ["#C6D571", "#A6CFCF"], ["#C6D571", "#BCD8F1"],
  ["#C6D571", "#F2E7F2"], ["#C6D571", "#ECCADD"], ["#C6D571", "#FBF3D8"],
  ["#C6D571", "#F7E4AC"],

  // ── Фон #BCBC94 (25) ──
  ["#BCBC94", "#E9B97E"], ["#BCBC94", "#D9877E"], ["#BCBC94", "#D783B0"],
  ["#BCBC94", "#82C1DE"], ["#BCBC94", "#7AB47D"], ["#BCBC94", "#B5C93B"],
  ["#BCBC94", "#C6D571"], ["#BCBC94", "#BCBC94"], ["#BCBC94", "#EDC9B5"],
  ["#BCBC94", "#A6CFCF"], ["#BCBC94", "#BCD8F1"], ["#BCBC94", "#ECCADD"],
  ["#BCBC94", "#F7E4AC"],

  // ── Фон #C4DCC7 (26) ──
  ["#C4DCC7", "#EFD72C"], ["#C4DCC7", "#E9B97E"], ["#C4DCC7", "#82C1DE"],
  ["#C4DCC7", "#7AB47D"], ["#C4DCC7", "#B5C93B"], ["#C4DCC7", "#C6D571"],
  ["#C4DCC7", "#BCBC94"], ["#C4DCC7", "#C4DCC7"], ["#C4DCC7", "#F7F1E7"],
  ["#C4DCC7", "#EDC9B5"], ["#C4DCC7", "#A6CFCF"], ["#C4DCC7", "#BCD8F1"],
  ["#C4DCC7", "#F2E7F2"], ["#C4DCC7", "#ECCADD"], ["#C4DCC7", "#FBF3D8"],
  ["#C4DCC7", "#F7E4AC"],

  // ── Фон #F7F1E7 (29) ──
  ["#F7F1E7", "#EFD72C"], ["#F7F1E7", "#E9B97E"], ["#F7F1E7", "#C4DCC7"],
  ["#F7F1E7", "#F7F1E7"], ["#F7F1E7", "#EDC9B5"], ["#F7F1E7", "#A6CFCF"],
  ["#F7F1E7", "#BCD8F1"], ["#F7F1E7", "#F2E7F2"], ["#F7F1E7", "#ECCADD"],
  ["#F7F1E7", "#FBF3D8"], ["#F7F1E7", "#F7E4AC"],

  // ── Фон #EDC9B5 (33) ──
  ["#EDC9B5", "#EFD72C"], ["#EDC9B5", "#E9B97E"], ["#EDC9B5", "#D9877E"],
  ["#EDC9B5", "#D783B0"], ["#EDC9B5", "#219EDA"], ["#EDC9B5", "#41A298"],
  ["#EDC9B5", "#37984E"], ["#EDC9B5", "#82C1DE"], ["#EDC9B5", "#7AB47D"],
  ["#EDC9B5", "#B5C93B"], ["#EDC9B5", "#C6D571"], ["#EDC9B5", "#BCBC94"],
  ["#EDC9B5", "#C4DCC7"], ["#EDC9B5", "#EDC9B5"], ["#EDC9B5", "#A6CFCF"],
  ["#EDC9B5", "#BCD8F1"], ["#EDC9B5", "#5BAEB8"], ["#EDC9B5", "#ECCADD"],
  ["#EDC9B5", "#F7E4AC"],

  // ── Фон #A6CFCF (38) ──
  ["#A6CFCF", "#E9B97E"], ["#A6CFCF", "#82C1DE"], ["#A6CFCF", "#7AB47D"],
  ["#A6CFCF", "#B5C93B"], ["#A6CFCF", "#C6D571"], ["#A6CFCF", "#BCBC94"],
  ["#A6CFCF", "#C4DCC7"], ["#A6CFCF", "#EDC9B5"], ["#A6CFCF", "#A6CFCF"],
  ["#A6CFCF", "#BCD8F1"], ["#A6CFCF", "#5BAEB8"], ["#A6CFCF", "#F2E7F2"],
  ["#A6CFCF", "#ECCADD"], ["#A6CFCF", "#FBF3D8"], ["#A6CFCF", "#F7E4AC"],

  // ── Фон #BCD8F1 (39) ──
  ["#BCD8F1", "#EFD72C"], ["#BCD8F1", "#E9B97E"], ["#BCD8F1", "#82C1DE"],
  ["#BCD8F1", "#7AB47D"], ["#BCD8F1", "#B5C93B"], ["#BCD8F1", "#C6D571"],
  ["#BCD8F1", "#BCBC94"], ["#BCD8F1", "#C4DCC7"], ["#BCD8F1", "#F7F1E7"],
  ["#BCD8F1", "#EDC9B5"], ["#BCD8F1", "#A6CFCF"], ["#BCD8F1", "#BCD8F1"],
  ["#BCD8F1", "#5BAEB8"], ["#BCD8F1", "#F2E7F2"], ["#BCD8F1", "#ECCADD"],
  ["#BCD8F1", "#FBF3D8"], ["#BCD8F1", "#F7E4AC"],

  // ── Фон #5BAEB8 (41) ──
  ["#5BAEB8", "#EFD72C"], ["#5BAEB8", "#D8862B"], ["#5BAEB8", "#C42B2B"],
  ["#5BAEB8", "#E9B97E"], ["#5BAEB8", "#D9877E"], ["#5BAEB8", "#D783B0"],
  ["#5BAEB8", "#219EDA"], ["#5BAEB8", "#41A298"], ["#5BAEB8", "#82C1DE"],
  ["#5BAEB8", "#7AB47D"], ["#5BAEB8", "#67462A"], ["#5BAEB8", "#BCBC94"],
  ["#5BAEB8", "#EDC9B5"], ["#5BAEB8", "#A6CFCF"], ["#5BAEB8", "#BCD8F1"],
  ["#5BAEB8", "#5BAEB8"], ["#5BAEB8", "#ECCADD"], ["#5BAEB8", "#FBF3D8"],
  ["#5BAEB8", "#F7E4AC"],

  // ── Фон #F2E7F2 (51) ──
  ["#F2E7F2", "#EFD72C"], ["#F2E7F2", "#E9B97E"], ["#F2E7F2", "#41A298"],
  ["#F2E7F2", "#82C1DE"], ["#F2E7F2", "#B5C93B"], ["#F2E7F2", "#C6D571"],
  ["#F2E7F2", "#BCBC94"], ["#F2E7F2", "#C4DCC7"], ["#F2E7F2", "#F7F1E7"],
  ["#F2E7F2", "#EDC9B5"], ["#F2E7F2", "#A6CFCF"], ["#F2E7F2", "#BCD8F1"],
  ["#F2E7F2", "#F2E7F2"], ["#F2E7F2", "#ECCADD"], ["#F2E7F2", "#FBF3D8"],
  ["#F2E7F2", "#F7E4AC"],

  // ── Фон #ECCADD (52) ──
  ["#ECCADD", "#EFD72C"], ["#ECCADD", "#E9B97E"], ["#ECCADD", "#D9877E"],
  ["#ECCADD", "#D783B0"], ["#ECCADD", "#219EDA"], ["#ECCADD", "#41A298"],
  ["#ECCADD", "#37984E"], ["#ECCADD", "#82C1DE"], ["#ECCADD", "#7AB47D"],
  ["#ECCADD", "#B5C93B"], ["#ECCADD", "#C6D571"], ["#ECCADD", "#BCBC94"],
  ["#ECCADD", "#C4DCC7"], ["#ECCADD", "#F7F1E7"], ["#ECCADD", "#EDC9B5"],
  ["#ECCADD", "#A6CFCF"], ["#ECCADD", "#BCD8F1"], ["#ECCADD", "#5BAEB8"],
  ["#ECCADD", "#F2E7F2"], ["#ECCADD", "#ECCADD"], ["#ECCADD", "#F7E4AC"],

  // ── Фон #FBF3D8 (54) ──
  ["#FBF3D8", "#E9B97E"], ["#FBF3D8", "#B5C93B"], ["#FBF3D8", "#C6D571"],
  ["#FBF3D8", "#BCBC94"], ["#FBF3D8", "#C4DCC7"], ["#FBF3D8", "#F7F1E7"],
  ["#FBF3D8", "#EDC9B5"], ["#FBF3D8", "#A6CFCF"], ["#FBF3D8", "#BCD8F1"],
  ["#FBF3D8", "#F2E7F2"], ["#FBF3D8", "#ECCADD"], ["#FBF3D8", "#FBF3D8"],
  ["#FBF3D8", "#F7E4AC"],

  // ── Фон #F7E4AC (55) ──
  ["#F7E4AC", "#EFD72C"], ["#F7E4AC", "#E9B97E"], ["#F7E4AC", "#D783B0"],
  ["#F7E4AC", "#219EDA"], ["#F7E4AC", "#41A298"], ["#F7E4AC", "#82C1DE"],
  ["#F7E4AC", "#7AB47D"], ["#F7E4AC", "#B5C93B"], ["#F7E4AC", "#C6D571"],
  ["#F7E4AC", "#BCBC94"], ["#F7E4AC", "#C4DCC7"], ["#F7E4AC", "#F7F1E7"],
  ["#F7E4AC", "#EDC9B5"], ["#F7E4AC", "#A6CFCF"], ["#F7E4AC", "#BCD8F1"],
  ["#F7E4AC", "#5BAEB8"], ["#F7E4AC", "#F2E7F2"], ["#F7E4AC", "#ECCADD"],
  ["#F7E4AC", "#FBF3D8"], ["#F7E4AC", "#F7E4AC"],

  // ── Фон #ECECEC (нейтральный, не из таблицы — оставляем как было) ──
  ["#ECECEC", "#EFD72C"], ["#ECECEC", "#E9B97E"], ["#ECECEC", "#D9877E"],
  ["#ECECEC", "#82C1DE"], ["#ECECEC", "#C6D571"], ["#ECECEC", "#FFFFFF"],
  // + добавляем новые пастельные на ECECEC (они тоже будут сливаться)
  ["#ECECEC", "#F7F1E7"], ["#ECECEC", "#FBF3D8"], ["#ECECEC", "#F2E7F2"],
  ["#ECECEC", "#F7E4AC"], ["#ECECEC", "#EDC9B5"], ["#ECECEC", "#ECCADD"],
  ["#ECECEC", "#BCD8F1"], ["#ECECEC", "#C4DCC7"], ["#ECECEC", "#BCBC94"],
  ["#ECECEC", "#A6CFCF"],
];

// Заполняем словарь
INCOMPATIBLE_PAIRS.forEach(([bg, text]) => {
  if (!COLOR_INCOMPATIBLE[bg]) COLOR_INCOMPATIBLE[bg] = [];
  if (!COLOR_INCOMPATIBLE[bg].includes(text)) COLOR_INCOMPATIBLE[bg].push(text);
});

// Хелпер: проверить, доступен ли цвет текста для текущего фона
export const MIN_COVER_CONTRAST = 3;

export function normalizeCoverColor(value) {
  if (typeof value !== "string") return null;
  let hex = value.trim().toUpperCase();
  if (/^#[0-9A-F]{3}$/.test(hex)) {
    hex = "#" + [...hex.slice(1)].map(char => char + char).join("");
  }
  return /^#[0-9A-F]{6}$/.test(hex) ? hex : null;
}

export function coverColorContrast(background, text) {
  const bg = normalizeCoverColor(background), fg = normalizeCoverColor(text);
  if (!bg || !fg) return null;
  const luminance = hex => {
    const rgb = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255);
    const linear = rgb.map(v => v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
    return linear[0] * 0.2126 + linear[1] * 0.7152 + linear[2] * 0.0722;
  };
  const a = luminance(bg), b = luminance(fg);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

export function isColorAllowed(bgColor, textColor) {
  const bg = normalizeCoverColor(bgColor), text = normalizeCoverColor(textColor);
  if (!bg || !text || bg === text) return false;
  // For the approved palette only the source matrix decides compatibility.
  if (Object.prototype.hasOwnProperty.call(APPROVED_COVER_INCOMPATIBLE, bg)
      && APPROVED_COVER_TEXT_COLORS.includes(text)) {
    return !APPROVED_COVER_INCOMPATIBLE[bg].includes(text);
  }
  // Preserve existing behavior for the extra gray background and unknown HEX.
  if ((COLOR_INCOMPATIBLE[bg] ?? []).includes(text)) return false;
  return coverColorContrast(bg, text) >= MIN_COVER_CONTRAST;
}

export function pickCoverTextColor(background, palette) {
  return palette.filter(color => isColorAllowed(background, color))
    .reduce((best, color) => !best ||
      coverColorContrast(background, color) > coverColorContrast(background, best)
      ? normalizeCoverColor(color) : best, null);
}

// Дополняем действующий словарь по текущей палитре; ручные запреты сохраняем.
// Тот же расчёт в isColorAllowed защищает и новые/старые HEX вне палитры.
COVER_PALETTES.bg.forEach(background => {
  if (Object.prototype.hasOwnProperty.call(APPROVED_COVER_INCOMPATIBLE, background)) {
    // Do not add contrast-based prohibitions over the approved matrix.
    COLOR_INCOMPATIBLE[background] = [...APPROVED_COVER_INCOMPATIBLE[background]];
    return;
  }
  const blocked = COLOR_INCOMPATIBLE[background] || (COLOR_INCOMPATIBLE[background] = []);
  [...new Set([...COVER_PALETTES.title, ...COVER_PALETTES.accent])].forEach(text => {
    if (coverColorContrast(background, text) < MIN_COVER_CONTRAST && !blocked.includes(text)) {
      blocked.push(text);
    }
  });
});

// Конвертирует HEX-цвет в CSS filter для перекраски белого PNG
function hexToFilter(hex) {
  // Парсим hex
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  // Переводим в HSL
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  // CSS filter: сначала делаем чёрным, потом нужный цвет
  return `brightness(0) saturate(100%) invert(${l > 50 ? 1 : 0}) sepia(1) saturate(5) hue-rotate(${h - 30}deg) brightness(${l > 50 ? 1.2 : 0.8})`;
}

// Генерация списка годов для селекта
const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [];
for (let y = currentYear; y >= 1970; y--) {
  YEAR_OPTIONS.push(y);
}

// ─── Расчёт заголовка по правилам ядра обложки ────────────────────────────────

const _measureCanvas = typeof document !== 'undefined' ? document.createElement("canvas") : null;

function measureTextWidth(text, fontSizePx, fontFamily) {
  if (!_measureCanvas) return text.length * fontSizePx * 0.6;
  const ctx = _measureCanvas.getContext("2d");
  ctx.font = `800 ${fontSizePx}px ${fontFamily}`;
  return ctx.measureText(text).width;
}

function splitIntoWords(text) {
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return parts;
  if (text.includes("-")) {
    const segs = text.split("-");
    return segs.map((s, i) => i < segs.length - 1 ? s + "-" : s).filter(Boolean);
  }
  return [text];
}

/**
 * Вычисляет layout заголовка: финальные строки + fontSize в px.
 * Повторяет логику _calc_title_layout из cover_core.py.
 *
 * @param {string} text — текст заголовка (может содержать \n)
 * @param {number} containerWidthPx — ширина области заголовка в px
 * @param {number} blockHeightPx — высота блока заголовка в px
 * @param {string} fontFamily — CSS font-family
 * @param {number} capRatio — доля cap height от font-size (обычно ~0.72)
 * @returns {{ lines: string[], fontSizePx: number, capHeightPx: number, lineGapPx: number }}
 */
function calcCoverTitleLayout(text, containerWidthPx, blockHeightPx, fontFamily, capRatio = 0.72) {
  const cfg = COVER_CONFIG;
  const maxCapPx = blockHeightPx * cfg.titleCapHeightMaxRatio;
  const minCapPx = blockHeightPx * cfg.titleCapHeightMinRatio;
  const lineGapPx = blockHeightPx * cfg.titleLineGapRatio;

  let lines = text.split("\n").map(l => l.trim().toUpperCase()).filter(Boolean);
  if (!lines.length) lines = [text.toUpperCase()];

  // Какая capHeight нужна чтобы строка заняла всю ширину?
  function capForLine(str) {
    const testFontSize = 100;
    const testCap = testFontSize * capRatio;
    const w = measureTextWidth(str, testFontSize, fontFamily);
    if (w <= 0) return maxCapPx;
    return (containerWidthPx / w) * testCap;
  }

  function bestCap(lineList) {
    // Определяем cap height по самой длинной строке (она ограничивает)
    return Math.min(...lineList.map(capForLine));
  }

  let capH = Math.min(bestCap(lines), maxCapPx);

  // Автоперенос: если одна строка и capH < min
  if (capH < minCapPx && lines.length === 1) {
    const words = splitIntoWords(lines[0]);
    if (words.length >= 2) {
      let bestLines = null, bestCapVal = 0;
      for (let i = 1; i < words.length; i++) {
        const l1 = words.slice(0, i).join(" ");
        const l2 = words.slice(i).join(" ");
        let ch = Math.min(bestCap([l1, l2]), maxCapPx);
        if (ch > bestCapVal) {
          bestCapVal = ch;
          bestLines = [l1, l2];
        }
      }
      if (bestLines) {
        lines = bestLines;
        capH = Math.min(bestCapVal, maxCapPx);
      }
    }
  }

  // Не даём стать меньше минимума (для читаемости), но только если после этого
  // текст не вылезет за блок — иначе приоритет отдаём вмещению
  const maxCapByHeight = lines.length > 0
    ? (blockHeightPx - (lines.length - 1) * lineGapPx) / lines.length
    : blockHeightPx;

  // Жёсткий лимит: текст обязан полностью поместиться в блок по высоте
  if (capH > maxCapByHeight) capH = maxCapByHeight;

  // minCapPx применяем только если он не ломает вмещение
  if (capH < minCapPx && minCapPx <= maxCapByHeight) capH = minCapPx;

  // Защита от отрицательных/нулевых значений
  if (capH < 1) capH = 1;

  const fontSizePx = capH / capRatio;

  // ── Финальная проверка по X: если текст шире контейнера, сжимаем по X ──
  let scaleX = 1;
  if (lines.length > 0 && containerWidthPx > 0) {
    const maxLineWidth = Math.max(
      ...lines.map(l => measureTextWidth(l, fontSizePx, fontFamily))
    );
    if (maxLineWidth > containerWidthPx) {
      scaleX = containerWidthPx / maxLineWidth;
    }
  }

  // ── Финальная проверка по Y ──
  // ВАЖНО: при lineHeight:1 реальная визуальная высота строки ≈ fontSizePx,
  // а не capH. Считаем именно по fontSizePx, иначе многострочный текст
  // вылезает за верхнюю границу блока.
  let scaleY = 1;
  const totalHeight = lines.length * fontSizePx + (lines.length - 1) * lineGapPx;
  if (totalHeight > blockHeightPx && blockHeightPx > 0) {
    scaleY = blockHeightPx / totalHeight;
  }

  // Если по Y сжимаем, имеет смысл синхронно сжать и по X в тех же пропорциях,
  // иначе текст деформируется (узкий и высокий). Но только если scaleX ещё не
  // меньше scaleY — берём более сильное сжатие и применяем его по обеим осям.
  if (scaleY < 1) {
    const uniform = Math.min(scaleX, scaleY);
    scaleX = uniform;
    scaleY = uniform;
  }

  return { lines, fontSizePx, capHeightPx: capH, lineGapPx, scaleX, scaleY };
}


// ─── Слот загрузки фото для обложки (4 блока на задней части) ────────────────

function CoverPhotoSlot({ photo, index, onFileSelect, onRemove, onEdit }) {
  const fileInputRef = useRef(null);

  const handleClick = () => {
    if (photo) {
      // Если фото уже есть — открываем редактор
      onEdit?.(index);
      return;
    }
    // Нет фото — открываем файловый диалог
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    // Вместо прямой вставки — открываем редактор
    onFileSelect(index, { url, name: file.name, file, fit: "cover", position: "center" });
    e.target.value = "";
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        bgcolor: photo ? "transparent" : "#fff",
        border: "none",
        borderRadius: 0,
        cursor: "pointer",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "border-color 0.15s, background 0.15s",
        "&:hover": {
          bgcolor: photo ? "rgba(0,0,0,0.08)" : "#f3eef6",
          borderColor: photo ? "transparent" : "rgba(255,255,255,0.75)",
        },
        "&:hover .cover-slot-edit-overlay": { opacity: 1 },
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      {!photo && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, pointerEvents: "none" }}>
          <PhotoCameraRoundedIcon sx={{ color: "#8f8498", fontSize: 20 }} />
          <Typography sx={{ color: "#807389", fontSize: "9px", fontWeight: 500, textAlign: "center" }}>
            Ваше фото
          </Typography>
        </Box>
      )}
      {photo && (
        <>
          <Box
            component="img"
            src={photo.url}
            draggable={false}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
          {/* Загрузка: матовый блюр + текст, скрывает старый кадр и момент подмены */}
          {photo.uploading && (
            <Box sx={{
              position: "absolute", inset: 0, zIndex: 4,
              display: "flex", flexDirection: "column", gap: 0.75,
              alignItems: "center", justifyContent: "center",
              backdropFilter: "blur(10px)",
              bgcolor: "rgba(255,255,255,0.45)",
              transition: "opacity 0.25s",
            }}>
              <Box sx={{
                width: 22, height: 22,
                border: "3px solid rgba(120,120,140,0.35)",
                borderTopColor: "rgba(90,90,110,0.9)",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }} />
              <Typography sx={{ color: "#555", fontSize: "10px", fontWeight: 600 }}>
                Добавляется…
              </Typography>
            </Box>
          )}
          {/* Оверлей редактирования при наведении */}
          <Box
            className="cover-slot-edit-overlay"
            sx={{
              position: "absolute", inset: 0,
              bgcolor: "rgba(0,0,0,0.32)",
              display: "flex", alignItems: "center", justifyContent: "center",
              opacity: 0, transition: "opacity 0.15s",
            }}
          >
            <Typography sx={{ color: "#fff", fontSize: "10px", fontWeight: 600, letterSpacing: "0.05em" }}>Изменить</Typography>
          </Box>
          {/* Кнопка удаления */}
          <Box
            onClick={(e) => { e.stopPropagation(); onRemove(index); }}
            sx={{
              position: "absolute",
              top: 3, right: 3,
              width: 18, height: 18,
              borderRadius: "50%",
              bgcolor: "rgba(200,0,0,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 2,
              opacity: 0,
              transition: "opacity 0.15s",
              ".MuiBox-root:hover > &": { opacity: 1 },
              "&:hover": { opacity: "1 !important", bgcolor: "rgba(220,0,0,0.9)" },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 12, color: "#fff" }} />
          </Box>
        </>
      )}
    </Box>
  );
}


// ─── Мини-пикер цвета для обложки ─────────────────────────────────────────────
function CoverColorPicker({ label, colors, value, onChange, bgColor }) {
  return (
    <>
      {label && (
        <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "text.secondary", letterSpacing: "0.05em", textTransform: "uppercase" }}>
          {label}
        </Typography>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxWidth: { xs: 'auto', sm: 300 }, overflowX: "scroll" }}>
        <Box sx={{
          display: "flex", flexDirection: "row", gap: "8px",
          overflowX: "auto", p: 1,
          scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" },
        }}>
          {[...colors].sort((a, b) => {
            const aBlocked = bgColor ? !isColorAllowed(bgColor, a) : false;
            const bBlocked = bgColor ? !isColorAllowed(bgColor, b) : false;
            return aBlocked - bBlocked; // false(0) < true(1) → доступные первые
          }).map(color => {
            const blocked = bgColor ? !isColorAllowed(bgColor, color) : false;
            return (
              <Box
                key={color}
                onClick={() => !blocked && onChange(color)}
                title={blocked ? "Не сочетается с цветом обложки" : color}
                sx={{
                  width: 28, height: 28, flexShrink: 0,
                  borderRadius: "50%", bgcolor: color,
                  border: value === color
                    ? "2.5px solid var(--joy-palette-primary-500)"
                    : "2px solid rgba(0,0,0,0.12)",
                  cursor: blocked ? "not-allowed" : "pointer",
                  transition: "transform 0.12s, box-shadow 0.12s",
                  boxShadow: value === color ? "0 0 0 2px rgba(43,92,230,0.25)" : "none",
                  opacity: blocked ? 0.3 : 1,
                  position: "relative",
                  "&:hover": blocked ? {} : { transform: "scale(1.18)", boxShadow: "0 2px 8px rgba(0,0,0,0.18)" },
                }}
              >
                {blocked && (
                  <Box sx={{
                    position: "absolute", inset: 0, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    borderRadius: "50%", fontSize: "10px",
                    color: "rgba(0,0,0,0.4)", fontWeight: 700, lineHeight: 1,
                  }}>✕</Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );
}


// ─── Горизонтальный пикер цвета фона обложки (большие кружки, скролл) ────────
function CoverBgPicker({ colors, value, onChange }) {
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 80, behavior: "smooth" });
    }
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {/* Стрелка влево */}
      <Box
        onClick={() => scroll(-1)}
        sx={{
          flexShrink: 0, width: 24, height: 24,
          display: "flex", alignItems: "center", justifyContent: "center",
          borderRadius: "50%",
          bgcolor: "rgba(0,0,0,0.06)",
          cursor: "pointer",
          fontSize: "12px",
          color: "text.secondary",
          userSelect: "none",
          transition: "background 0.15s",
          "&:hover": { bgcolor: "rgba(0,0,0,0.13)" },
          "&:active": { bgcolor: "rgba(0,0,0,0.2)" },
        }}
      >
        <ArrowBackIosRoundedIcon/>
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          overflowX: "auto",
          p: 1,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {colors.map(color => (
          <Box
            key={color}
            onClick={() => onChange(color)}
            sx={{
              width: 36, height: 36, flexShrink: 0,
              borderRadius: "50%", bgcolor: color,
              border: value === color
                ? "3px solid var(--joy-palette-primary-500)"
                : "2px solid rgba(0,0,0,0.12)",
              cursor: "pointer",
              transition: "transform 0.14s, box-shadow 0.14s",
              boxShadow: value === color ? "0 0 0 3px rgba(43,92,230,0.28)" : "none",
              "&:hover": { transform: "scale(1.15)", boxShadow: "0 3px 10px rgba(0,0,0,0.22)" },
            }}
          />
        ))}
      </Box>

      {/* Стрелка вправо */}
      <Box
        onClick={() => scroll(1)}
        sx={{
          flexShrink: 0, width: 24, height: 24,
          display: "flex", alignItems: "center", justifyContent: "center",
          borderRadius: "50%",
          bgcolor: "rgba(0,0,0,0.06)",
          cursor: "pointer",
          fontSize: "12px",
          color: "text.secondary",
          userSelect: "none",
          transition: "background 0.15s",
          "&:hover": { bgcolor: "rgba(0,0,0,0.13)" },
          "&:active": { bgcolor: "rgba(0,0,0,0.2)" },
        }}
      >
        <ArrowForwardIosRoundedIcon/>
      </Box>
    </Box>
  );
}


// ─── Тултип настроек текста обложки ───────────────────────────────────────────
function TextSettingsTooltip({ open, anchorEl, onClose, type, titleColor, accentColor, titleSize, titleBreak, coverBgColor, onTitleColorChange, onAccentColorChange, onTitleSizeChange, onTitleBreakChange }) {
  const ref = useRef(null);

  const isMobileDevice = typeof window !== "undefined" && window.innerWidth < 600;
  const selectAndClose = (fn) => (...args) => {
    fn(...args);
    if (isMobileDevice) onClose();
  };

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target) && anchorEl && !anchorEl.contains(e.target)) {
        onClose();
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, anchorEl, onClose]);

  if (!open) return null;
  const isTitle = type === "title";

  return (
    <>
      {/* Затемнение фона на мобиле */}
      <Box
        onClick={onClose}
        sx={{
          display: { xs: "block", sm: "none" },
          position: "fixed", inset: 0,
          bgcolor: "rgba(0,0,0,0.35)",
          zIndex: 1499,
        }}
      />
      <Box
        ref={ref}
        sx={{
          // На мобиле — фиксированная панель снизу экрана
          position: { xs: "fixed", sm: "absolute" },
          bottom: { xs: 0, sm: "calc(100% + 8px)" },
          left: { xs: 0, sm: isTitle ? 0 : "auto" },
          right: { xs: 0, sm: isTitle ? "auto" : 0 },
          zIndex: 1500,
          bgcolor: "background.surface",
          border: "1px solid", borderColor: "divider",
          borderRadius: { xs: "20px 20px 0 0", sm: "14px" },
          boxShadow: "0 -4px 32px rgba(0,0,0,0.15)",
          p: 2,
          pb: { xs: 3, sm: 2 },
          minWidth: { xs: "unset", sm: 220 },
          width: { xs: "auto", sm: "auto" },
          display: "flex", flexDirection: "column", gap: 1.5,
          // Небольшая анимация появления снизу
          animation: "slideUp 0.2s ease",
          "@keyframes slideUp": {
            from: { transform: "translateY(100%)" },
            to:   { transform: "translateY(0)" },
          },
        }}
      >
        {/* Крестик закрытия — только на мобиле */}
        <Box sx={{ display: { xs: "flex", sm: "none" }, justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "text.primary" }}>
            {type === "title" ? "Настройки заголовка" : "Настройки года"}
          </Typography>
          <Box
            onClick={onClose}
            sx={{
              width: 28, height: 28,
              display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: "50%",
              bgcolor: "rgba(0,0,0,0.07)",
              cursor: "pointer",
              fontSize: "16px",
              "&:hover": { bgcolor: "rgba(0,0,0,0.13)" },
            }}
          >
            <CloseRoundedIcon/>
          </Box>
        </Box>

        <CoverColorPicker
          label={isTitle ? "Цвет заголовка" : "Цвет года"}
          colors={isTitle ? COVER_PALETTES.title : COVER_PALETTES.accent}
          value={isTitle ? titleColor : accentColor}
          onChange={isTitle ? selectAndClose(onTitleColorChange) : selectAndClose(onAccentColorChange)}
          bgColor={coverBgColor}
        />
      </Box>
    </>
  );
}


// ─── Блок заголовка + года обложки (адаптивный, по правилам ядра) ─────────────

function CoverTextBlock({ coverText, coverYear, coverTitleColor, coverAccentColor, cfg, isPlaceholder, showYear = true }) {
  const titleBlockRef = useRef(null);
  const yearBlockRef = useRef(null);
  const [titleLayout, setTitleLayout] = useState(null);
  const [yearScale, setYearScale] = useState({ fontSizePx: 10, scaleX: 1 });

  const displayText = coverText || "Тут текст";
  const displayYear = coverYear || String(new Date().getFullYear());
  const textOpacity = coverText ? 1 : 0.45;
  const yearOpacity = coverYear ? 1 : 0.45;

  // Пересчёт заголовка при изменении текста или размера
  useEffect(() => {
    const el = titleBlockRef.current;
    if (!el) return;
    const recalc = () => {
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      setTitleLayout(calcCoverTitleLayout(displayText, r.width, r.height, cfg.fontFamily, cfg.capRatio));
    };
    recalc();

    if (typeof document !== "undefined" && document.fonts) {
      document.fonts.ready.then(recalc);
      document.fonts.load(`800 16px ${cfg.fontFamily}`).then(recalc).catch(() => {});
    }

    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [displayText, cfg.fontFamily, cfg.capRatio]);

  // Пересчёт года
  useEffect(() => {
    const el = yearBlockRef.current;
    if (!el) return;
    const recalc = () => {
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      let fSize = r.height / cfg.capRatio;
      // Дату/год НЕ сжимаем по горизонтали. Блок года занимает всю безопасную зону
      // и центрируется по горизонтали. Если строка шире доступной ширины — уменьшаем
      // размер шрифта пропорционально (сохраняя пропорции), иначе оставляем как есть.
      const availW = r.width;
      const naturalW = measureTextWidth(displayYear, fSize, cfg.fontFamily);
      if (naturalW > availW && naturalW > 0) {
        fSize = fSize * (availW / naturalW);
      }
      setYearScale({ fontSizePx: fSize, scaleX: 1 });
    };
    recalc();

    // Дожидаемся загрузки кастомного шрифта и пересчитываем
    if (typeof document !== "undefined" && document.fonts) {
      document.fonts.ready.then(recalc);
      // Дополнительно — конкретный шрифт обложки
      document.fonts.load(`800 16px ${cfg.fontFamily}`).then(recalc).catch(() => {});
    }

    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [displayYear, cfg.fontFamily, cfg.capRatio, cfg.yearTargetWidthPercent]);

  const lines = titleLayout?.lines ?? [displayText.toUpperCase()];
  const fontSize = titleLayout?.fontSizePx ?? 14;
  const lineGap = titleLayout?.lineGapPx ?? 2;
  const scaleX = titleLayout?.scaleX ?? 1;
  const scaleY = titleLayout?.scaleY ?? 1;

  return (
    <>
      {/* Блок заголовка — фиксированная высота, текст прижат к низу */}
      <Box
        ref={titleBlockRef}
        sx={{
          position: "absolute",
          top: `${cfg.titleBlockTopPercent}%`,
          left: `${cfg.safeZoneSidePercent}%`,
          right: `${cfg.safeZoneSidePercent}%`,
          height: `${cfg.titleBlockHeightPercent}%`,
          pointerEvents: "none",
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            transform: (scaleX < 1 || scaleY < 1)
              ? `scale(${scaleX}, ${scaleY})`
              : undefined,
            transformOrigin: "center bottom",
          }}
        >
          {lines.map((line, i) => (
            <div
              key={i}
              style={{
                fontFamily: cfg.fontFamily,
                fontWeight: 800,
                fontSize: `${fontSize}px`,
                lineHeight: 1,
                color: coverTitleColor ?? "#fff",
                textAlign: "center",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                opacity: textOpacity,
                marginTop: i > 0 ? `${lineGap}px` : 0,
              }}
            >
              {line}
            </div>
          ))}
        </div>
      </Box>

      {/* Блок года (скрыт в режиме «Без даты») */}
      {showYear && (
        <Box
          ref={yearBlockRef}
          sx={{
            position: "absolute",
            top: `${cfg.titleBlockTopPercent + cfg.titleBlockHeightPercent + cfg.yearGapPercent}%`,
            left: `${cfg.safeZoneSidePercent}%`,
            right: `${cfg.safeZoneSidePercent}%`,
            height: `${cfg.yearHeightPercent}%`,
            pointerEvents: "none",
            zIndex: 2,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          <span style={{
            fontFamily: cfg.fontFamily,
            fontWeight: 800,
            fontSize: `${yearScale.fontSizePx}px`,
            lineHeight: 1,
            color: coverAccentColor ?? "#fff",
            maxWidth: "100%",
            overflow: "hidden",
            opacity: yearOpacity,
            transform: `scaleX(${yearScale.scaleX})`,
            transformOrigin: "center top",
            display: "inline-block",
            whiteSpace: "nowrap",
          }}>
            {displayYear}
          </span>
        </Box>
      )}
    </>
  );
}

// ─── Блок обложки (полная ширина: задняя + передняя часть) ───────────────────

function CoverBlock({
  frontLayoutTag, backLayoutTag,
  frontPhotoShape, backPhotoShape, coverPageCount = 0,
  bookSize, isActive, coverText, coverYear,
  coverBackPhotos, onBackPhotoSelect, onBackPhotoRemove, onBackPhotoEdit,
  coverBgColor, coverTitleColor, coverAccentColor,
  coverTitleSize, coverTitleBreak,
  coverStickerImage, onStickerClick,
  readOnly, showYear = true, coverDateMode = "year",
}) {
  const cfg = getCoverConfig(bookSize);
  const frontBox = frontLayoutTag === "photo" ? coverPhotoGeometry(bookSize, frontPhotoShape) : null;
  const backBox = backLayoutTag === "photo"
    ? coverPhotoGeometry(bookSize, backPhotoShape) : null;
  const frontContent = coverContentLayout(bookSize, frontLayoutTag === "photo"
    ? normalizeCoverShape(frontPhotoShape) : "sticker");
  const backContent = coverContentLayout(bookSize, backLayoutTag === "photo"
    ? normalizeCoverShape(backPhotoShape) : "grid");
  const fixed = frontContent || backContent || frontBox || backBox;
  const spineMm = coverPageCount >= 96 ? 8 : 5;
  const ar = fixed ? `${fixed.sideW * 2 + spineMm} / ${fixed.sideH}`
    // Legacy обложка имеет собственную ширину; widthMm книги — лист.
    : (Number(bookSize?.heightMm || 210) >= 230 ? "390 / 250" : "320 / 210");
  const backLogoBox = backContent || backBox || coverContentLayout(bookSize, "grid");
  const frontLogoBox = frontContent || frontBox || coverContentLayout(bookSize, "grid");

  return (
    <Box
      sx={{
        width: "100%",
        aspectRatio: ar,
        // borderRadius: "8px",
        overflow: "hidden",
        boxShadow: isActive
          ? "0 0 0 2.5px var(--joy-palette-primary-400), 0 12px 48px rgba(0,0,0,0.15)"
          : "0 4px 24px rgba(0,0,0,0.10)",
        transition: "box-shadow 0.2s",
        position: "relative",
        display: "flex",
        flexDirection: "row",
        gap: fixed ? 0 : `${cfg.spineInnerPadding}%`,
      }}
    >
      {/* ─── Левая часть (Задняя обложка) ─── */}
      <Box data-cover-side="right" sx={{
        flex: 1, minWidth: 0, position: "relative",
        bgcolor: coverBgColor ?? "#834A90",
        overflow: "hidden",
        zIndex: 1,
      }}>
        {/* Заголовок + год на задней части (скрыт для макета "minimal") */}
        {!["minimal", "empty"].includes(backLayoutTag) && (
          backContent ? (
          <PdfCoverText layout={backContent} title={coverText} year={coverYear}
            dateMode={coverDateMode} showYear={showYear}
            titleColor={coverTitleColor} yearColor={coverAccentColor} />
        ) : (
          <CoverTextBlock
            coverText={coverText}
            coverYear={coverYear}
            coverTitleColor={coverTitleColor ?? "#fff"}
            coverAccentColor={coverAccentColor ?? "#fff"}
            cfg={cfg}
            showYear={showYear}
          />
        )
        )}

        {/* Сетка 2×2 — только для grid-макетов */}
        {(backLayoutTag === "classic" || backLayoutTag === "grid_photo" || backLayoutTag === "grid" || !backLayoutTag) && (
        <Box sx={{
          position: "absolute",
          top: `${cfg.photoGridTopPercent}%`,
          left: "50%",
          transform: "translateX(-50%)",
          width: `${cfg.photoGridWidthPercent}%`,
          height: `${cfg.photoGridHeightPercent}%`,
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gridTemplateRows: "repeat(2, minmax(0, 1fr))",
          columnGap: `${(2 / 92) * 100}%`,
          rowGap: `${(2 / 122) * 100}%`,
          ...(backContent?.gridSx || {}),
          zIndex: 2,
        }}>
          {[0, 1, 2, 3].map(idx => (
            readOnly ? (
              <Box key={idx} sx={{
                width: "100%", height: "100%",
                bgcolor: coverBackPhotos[idx] ? "transparent" : "#fff",
                borderRadius: 0, overflow: "hidden",
              }}>
                {coverBackPhotos[idx] && (
                  <Box component="img" src={coverBackPhotos[idx].url} draggable={false}
                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none", userSelect: "none" }}
                  />
                )}
              </Box>
            ) : (
              <CoverPhotoSlot
                key={idx}
                index={idx}
                photo={coverBackPhotos[idx]}
                onFileSelect={onBackPhotoSelect}
                onRemove={onBackPhotoRemove}
                onEdit={onBackPhotoEdit}
              />
            )
          ))}
        </Box>
        )}

        {/* Фото задней — макет "photo" (свой снимок, слот 5). Зона = зоне фото лица */}
        {backLayoutTag === "photo" && (
          <Box sx={{
            position: "absolute",
            top: `${cfg.frontPhotoTopPercent}%`,
            left: `${cfg.frontPhotoSidePercent}%`,
            right: `${cfg.frontPhotoSidePercent}%`,
            height: `${cfg.frontPhotoHeightPercent}%`,
            transform: "translateY(-50%)",
            zIndex: 2, overflow: "hidden", borderRadius: 0,
            ...(backBox?.sx || {}),
          }}>
            {readOnly ? (
              <Box sx={{ width: "100%", height: "100%",
                bgcolor: coverBackPhotos[5] ? "transparent" : "rgba(180,180,190,0.35)" }}>
                {coverBackPhotos[5] && (
                  <Box component="img" src={coverBackPhotos[5].url} draggable={false}
                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none" }} />
                )}
              </Box>
            ) : (
              <CoverPhotoSlot index={5} photo={coverBackPhotos[5]}
                onFileSelect={onBackPhotoSelect} onRemove={onBackPhotoRemove} onEdit={onBackPhotoEdit} />
            )}
          </Box>
        )}

        {/* Логотип внизу левой части */}
        {backLogoBox ? (
          <Box component="img" src={cfg.logoImageSrc || "/logo.png"} draggable={false} sx={backLogoBox.logoSx} />
        ) : (
        <Box sx={{
          position: "absolute",
          bottom: `${cfg.logoBottomPercent}%`,
          left: 0, right: 0,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
          zIndex: 2,
        }}>
          {cfg.logoImageSrc ? (
            <Box
              component="img"
              src={cfg.logoImageSrc}
              draggable={false}
              sx={{
                height: `${cfg.logoHeightPercent}%`,
                maxHeight: { xs: cfg.logoImageMaxHeightPx, sm: cfg.logoImageMaxHeightDesktopPx },
                minHeight: 8,
                width: "auto",
                maxWidth: "70%",
                display: "block",
                pointerEvents: "none",
                userSelect: "none",
                opacity: 0.85,
              }}
            />
          ) : (
            <Typography sx={{
              fontSize: { xs: "7px", sm: "10px" },
              fontWeight: 700,
              color: coverAccentColor ?? "rgba(255,255,255,0.7)",
              letterSpacing: "0.15em",
            }}>
              {cfg.logoText}
            </Typography>
          )}
        </Box>
        )}
      </Box>

      {/* ─── Пунктирная линия-корешок по центру ─── */}
      <Box sx={{
        width: fixed ? `${spineMm / (fixed.sideW * 2 + spineMm) * 100}%` : "7px",
        flexShrink: 0,
        position: "relative",
        bgcolor: coverBgColor ?? "#834A90",
        background: "none",
        filter: "brightness(0.85)",
      }}>
        <Box sx={{
          position: "absolute",
          top: "5%",
          bottom: "5%",
          left: "50%",
          transform: "translateX(-50%)",
          borderLeft: "1.5px dashed rgba(255,255,255,0.35)",
        }} />
      </Box>

      {/* ─── Правая часть (Передняя обложка) ─── */}
      <Box data-cover-side="right" sx={{
          flex: 1, minWidth: 0, position: "relative",
          bgcolor: coverBgColor ?? "#834A90",
          overflow: "hidden",
          zIndex: 1,
        }}
      >
        {/* Заголовок + год на передней части */}
        {frontContent ? (
          <PdfCoverText layout={frontContent} title={coverText} year={coverYear}
            dateMode={coverDateMode} showYear={showYear}
            titleColor={coverTitleColor} yearColor={coverAccentColor} />
        ) : (
          <CoverTextBlock
          coverText={coverText}
          coverYear={coverYear}
          coverTitleColor={coverTitleColor ?? "rgba(40,50,80,0.85)"}
          coverAccentColor={coverAccentColor ?? "rgba(40,50,80,0.65)"}
          cfg={cfg}
          showYear={showYear}
        />
        )}

        {/* Зона фото (передняя) — макет "photo": как стикер, но фото с фоторедактором */}
        {frontLayoutTag === "photo" && (
          <Box sx={{
            position: "absolute",
            top: `${cfg.frontPhotoTopPercent}%`,
            left: `${cfg.frontPhotoSidePercent}%`,
            right: `${cfg.frontPhotoSidePercent}%`,
            height: `${cfg.frontPhotoHeightPercent}%`,
            transform: "translateY(-50%)",
            zIndex: 2, overflow: "hidden", borderRadius: 0,
            ...(frontBox?.sx || {}),
          }}>
            {readOnly ? (
              <Box sx={{
                width: "100%", height: "100%",
                bgcolor: coverBackPhotos[cfg.frontPhotoIndex] ? "transparent" : "rgba(255,255,255,0.10)",
                border: coverBackPhotos[cfg.frontPhotoIndex] ? "none" : "1.5px dashed rgba(255,255,255,0.35)",
              }}>
                {coverBackPhotos[cfg.frontPhotoIndex] && (
                  <Box component="img" src={coverBackPhotos[cfg.frontPhotoIndex].url} draggable={false}
                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none", userSelect: "none" }}
                  />
                )}
              </Box>
            ) : (
              <CoverPhotoSlot
                index={cfg.frontPhotoIndex}
                photo={coverBackPhotos[cfg.frontPhotoIndex]}
                onFileSelect={onBackPhotoSelect}
                onRemove={onBackPhotoRemove}
                onEdit={onBackPhotoEdit}
              />
            )}
          </Box>
        )}

        {/* Зона стикера (центр правой части) — макет "sticker" */}
        {frontLayoutTag !== "photo" && (
          <Box
            onClick={readOnly ? undefined : onStickerClick}
            sx={{
              position: "absolute",
              top: "58.6%",
              left: `${cfg.stickerSidePercent}%`,
              right: `${cfg.stickerSidePercent}%`,
              height: `${cfg.stickerMaxHeightPercent}%`,
              transform: "translateY(-50%)",
              ...(frontContent?.stickerSx || {}),
              zIndex: 2,
              cursor: readOnly ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              borderRadius: frontContent ? 0 : "4px",
              border: coverStickerImage ? "none" : (readOnly ? "none" : "1.5px dashed rgba(255,255,255,0.45)"),
              bgcolor: coverStickerImage ? "transparent" : (readOnly ? "transparent" : "rgba(255,255,255,0.12)"),
              transition: "border-color 0.15s, background 0.15s",
              "&:hover": readOnly ? {} : {
                border: coverStickerImage ? "none" : "1.5px dashed rgba(255,255,255,0.75)",
                bgcolor: coverStickerImage ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.22)",
              },
            }}
          >
            {coverStickerImage ? (
              <>
                <Box
                  component="img"
                  src={coverStickerImage}
                  draggable={false}
                  sx={{
                    width: "100%", height: "100%",
                    objectFit: "contain",
                    objectPosition: frontContent ? "center bottom" : "center",
                    display: "block",
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                />
                {/* hover overlay */}
                <Box sx={{
                  position: "absolute", inset: 0,
                  bgcolor: "rgba(0,0,0,0)",
                  transition: "background 0.15s",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.12)" },
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Typography className="sticker-hint" sx={{ color: "#fff", fontSize: "9px", fontWeight: 600, opacity: 0, transition: "opacity 0.15s", ".MuiBox-root:hover &": { opacity: 1 } }}>
                    Изменить
                  </Typography>
                </Box>
              </>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, pointerEvents: "none" }}>
                <Box sx={{ color: "rgba(255,255,255,.85)" }}><StickerPlaceholderIcon size={28} /></Box>
                <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "9px", fontWeight: 500, textAlign: "center" }}>
                  Стикер
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Логотип внизу правой части */}
        {frontLogoBox ? (
          <Box component="img" src={cfg.logoImageSrc || "/logo.png"} draggable={false} sx={frontLogoBox.logoSx} />
        ) : (
        <Box sx={{
          position: "absolute",
          bottom: `${cfg.logoBottomPercent}%`,
          left: 0, right: 0,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
          zIndex: 2,
        }}>
          {cfg.logoImageSrc ? (
            <Box
              component="img"
              src={cfg.logoImageSrc}
              draggable={false}
              sx={{
                height: `${cfg.logoHeightPercent}%`,
                maxHeight: { xs: cfg.logoImageMaxHeightPx, sm: cfg.logoImageMaxHeightDesktopPx },
                minHeight: 8,
                width: "auto",
                maxWidth: "70%",
                display: "block",
                pointerEvents: "none",
                userSelect: "none",
                opacity: 0.85,
              }}
            />
          ) : (
            <Typography sx={{
              fontSize: { xs: "7px", sm: "10px" },
              fontWeight: 700,
              color: coverAccentColor ?? "rgba(255,255,255,0.7)",
              letterSpacing: "0.15em",
            }}>
              {cfg.logoText}
            </Typography>
          )}
        </Box>
        )}
      </Box>
    </Box>
  );
}


// Догрузка картинки в кэш браузера. Промис резолвится, когда изображение
// декодировано и готово к МГНОВЕННОЙ отрисовке (чтобы не мигать исходником при
// снятии блюра). Никогда не реджектится: при ошибке блюр всё равно снимется.
function preloadImage(src) {
  return new Promise((resolve) => {
    if (!src) { resolve(); return; }
    const img = new Image();
    const done = () => resolve();
    img.onload = () => {
      // decode() дожидается полного декодирования (важно для тяжёлых фото)
      if (img.decode) { img.decode().then(done).catch(done); }
      else done();
    };
    img.onerror = done;   // не блокируем UI, если картинка не загрузилась
    img.src = src;
  });
}

// ─── Рендер фото в слоте с учётом кадрирования из редактора ─────────────────

function SlotImage({ photo, side, slotEdges, spineFraction }) {
  const slotRef = useRef(null);
  const [slotSize, setSlotSize] = useState({ w: 0, h: 0 });
  const [natSize,  setNatSize]  = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (!photo?.url) return;
    const img = new Image();
    img.onload = () => setNatSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = photo.url;
  }, [photo?.url]);

  useEffect(() => {
    const el = slotRef.current;
    if (!el) return;
    const sync = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0) setSlotSize({ w: r.width, h: r.height });
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Bleed (желтые внешние поля) — должны уйти за край слота ──
  const BLEED_VIS_FRACTION = 0.025;
  const edges = slotEdges ?? { top: false, right: false, bottom: false, left: false };

  const bleedPx = slotSize.w > 0
    ? Math.min(slotSize.w, slotSize.h) * BLEED_VIS_FRACTION
    : 0;

  const bleedTop    = edges.top    ? bleedPx : 0;
  const bleedBottom = edges.bottom ? bleedPx : 0;
  const bleedLeft   = (edges.left  && side !== "right") ? bleedPx : 0;
  const bleedRight  = (edges.right && side !== "left")  ? bleedPx : 0;

  // ── Корешок (spine) — ВИЗУАЛЬНАЯ имитация среза при переплёте ──
  // На фабрике 8% от ширины уйдёт под переплёт. В файле фото полное,
  // но в редакторе/превью имитируем, как оно будет выглядеть в книге:
  // слот overflow:hidden, а фото мы рендерим с небольшим уходом за край
  // в сторону корешка (left для правой страницы, right для левой).
  // Фолбэк, если spineFraction не передан. В норме приходит из BookPage:
  // реальная толщина корешка (мм) / ширина этого слота (мм).
  const SPINE_VIS_FRACTION = 0.08;
  const effSpineFraction = (spineFraction ?? SPINE_VIS_FRACTION);
  const spinePx = (side === "left" || side === "right") && slotSize.w > 0
    ? slotSize.w * effSpineFraction
    : 0;

  const spineLeft  = side === "right" ? spinePx : 0; // правая страница — корешок слева
  const spineRight = side === "left"  ? spinePx : 0; // левая страница — корешок справа

  // "Расширенный" слот: фото рисуется в области slot + bleed + spine,
  // а overflow:hidden у самого слота скрывает то, что уходит за край.
  const effW = slotSize.w + bleedLeft + bleedRight + spineLeft + spineRight;
  const effH = slotSize.h + bleedTop  + bleedBottom;

  const scale    = photo.scale    ?? 1;
  const rotation = photo.rotation ?? 0;

  const ready = slotSize.w > 0 && natSize.w > 0;

  // Cover-размер фото в расширенной области
  let imgW = effW;
  let imgH = effH;
  if (ready) {
    const imgAr  = natSize.w / natSize.h;
    const slotAr = effW / effH;
    if (imgAr > slotAr) {
      imgH = effH;
      imgW = effH * imgAr;
    } else {
      imgW = effW;
      imgH = effW / imgAr;
    }
    imgW *= scale;
    imgH *= scale;
  }

  // Сдвиг центра фото, чтобы лишние bleed/spine ушли именно за нужные края.
  // Положительный X → фото сдвигается вправо (закрывает левый край).
  const centerOffsetX = (bleedLeft + spineLeft - bleedRight - spineRight) / 2;
  const centerOffsetY = (bleedTop  - bleedBottom) / 2;

  const offsetX = (photo.offsetX ?? 0) * imgW + centerOffsetX;
  const offsetY = (photo.offsetY ?? 0) * imgH + centerOffsetY;

  // Кропнутое (или уже загруженное на сервер) фото обрезано РОВНО под пропорции
  // слота — показываем 1:1 на весь слот. Корешок и вылеты под обрез скрываются
  // выше, на уровне всей страницы (crop в корне BookPage), поэтому здесь
  // никакой спец-обработки не нужно.
  if (photo?.isCropped || photo?.serverUrl) {
    return (
      <Box
        ref={slotRef}
        sx={{
          position: "absolute", inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
          borderRadius: "inherit",
        }}
      >
        <Box
          component="img"
          src={photo.url}
          draggable={false}
          sx={{
            position: "absolute", inset: 0,
            width: "100%",
            height: "100%",
            // cover, а не fill: файл кропнут под пропорцию ячейки, в норме
            // cover == 1:1. Файлы, кропнутые при старом баге рамки (пропорция
            // шире ячейки), cover аккуратно подрежет по бокам, а fill
            // РАСТЯГИВАЛ их по вертикали — это и был симптом «вытянутая фотка».
            objectFit: "cover",
            display: "block",
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      ref={slotRef}
      sx={{
        position: "absolute", inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        borderRadius: "inherit",
      }}
    >
      <Box
        component="img"
        src={photo.url}
        draggable={false}
        sx={{
          position: "absolute",
          top: "50%", left: "50%",
          width:     ready ? `${imgW}px` : "100%",
          height:    ready ? `${imgH}px` : "100%",
          objectFit: ready ? "unset"     : "cover",
          transform: ready
            ? `translate(-50%, -50%) translate(${offsetX}px, ${offsetY}px) rotate(${rotation}deg)`
            : "translate(-50%, -50%)",
          transformOrigin: "center center",
          display: "block",
          pointerEvents: "none",
          userSelect: "none",
        }}
      />
    </Box>
  );
}

// ─── Слот ─────────────────────────────────────────────────────────────────────

function PhotoSlot({ photo, printGeometry = false, cellRadius = 5, onFileSelect, onRemove, onFitChange, onPositionChange, readOnly, onEdit, side, slotEdges, spineFraction, visibleInsets, isCoverSlot }) {
  const fileInputRef = useRef(null);

  const handleClick = () => {
    if (readOnly) return;
    if (isCoverSlot) {
      // Обложка — открываем пикер, не файловый диалог
      onFileSelect?.();
      return;
    }
    if (!photo) fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    onFileSelect({ url, name: file.name, fit: "cover", position: "center", file });
    e.target.value = "";
  };

  return (
    <Box
      sx={{
        flex: 1, minHeight: 0, minWidth: 0,
        position: "relative",
        borderRadius: `${cellRadius}px`,
        cursor: readOnly ? "default" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
        transition: "all 0.15s",
        bgcolor: "transparent",
        boxShadow: photo && !printGeometry ? "0 2px 10px rgba(0,0,0,0.18)" : "none",
        "&:hover .empty-slot-inner": !photo ? {
          bgcolor: "color-mix(in srgb, var(--joy-palette-primary-500) 14%, transparent)",
          borderColor: "color-mix(in srgb, var(--joy-palette-primary-400) 55%, transparent)",
        } : {},
      }}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {!photo && (
        <Box
          className="empty-slot-inner"
          sx={{
            position: "absolute",
            left:   `${(visibleInsets?.left   ?? 0) * 100}%`,
            right:  `${(visibleInsets?.right  ?? 0) * 100}%`,
            top:    `${(visibleInsets?.top    ?? 0) * 100}%`,
            bottom: `${(visibleInsets?.bottom ?? 0) * 100}%`,
            border: "1.5px dashed color-mix(in srgb, var(--joy-palette-primary-400) 35%, transparent)",
            borderRadius: `${cellRadius}px`,
            bgcolor: "color-mix(in srgb, var(--joy-palette-primary-500) 8%, transparent)",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.15s",
          }}
        >
          {!readOnly && (
            <Typography
              level="h3"
              sx={{ color: "#6f6f6f", userSelect: "none" }}
            >
              +
            </Typography>
          )}
        </Box>
      )}

      {photo && <SlotImage photo={photo} side={side} slotEdges={slotEdges} spineFraction={spineFraction} />}

      <AnimatePresence>
        {photo?.isUploading && (
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            sx={{
              position: "absolute", inset: 0,
              display: "flex", flexDirection: "column", gap: 1,
              alignItems: "center", justifyContent: "center",
              backdropFilter: "blur(10px)",
              bgcolor: "rgba(255,255,255,0.45)",
              borderRadius: `${cellRadius}px`,
              zIndex: 5,
            }}
          >
            <Box sx={{
              width: 28, height: 28,
              border: "3px solid var(--joy-palette-primary-300)",
              borderTopColor: "var(--joy-palette-primary-600)",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }} />
            <Typography level="body-xs" sx={{ color: "var(--joy-palette-primary-700)", fontWeight: 600 }}>
              Добавляется…
            </Typography>
          </Box>
        )}
      </AnimatePresence>

      {photo && !readOnly && (
        <Box
          onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
          sx={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(0,0,0,0)",
            transition: "background 0.15s",
            borderRadius: `${cellRadius}px`,
            cursor: "pointer",
            "&:hover": { bgcolor: "rgba(0,0,0,0.20)" },
          }}
        />
      )}

      {/* ARCHIVED OLD CONTROLS {photo && (
        <>
          <Box
            component="img"
            src={photo.url}
            sx={{
              width: "100%", height: "100%",
              objectFit: photo.fit ?? "cover",
              objectPosition: photo.position ?? "center",
              display: "block",
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
          {!readOnly && (
            <Box
              sx={{
                position: "absolute", top: 5, left: 5,
                display: "flex", gap: 0.5,
              }}
              className="photo-controls"
            >
              <Dropdown>
                <MenuButton
                  slots={{ root: IconButton }}
                  slotProps={{
                    root: {
                      size: "sm",
                      variant: "solid",
                      color: "neutral",
                      sx: {
                        minWidth: 30, minHeight: 30, p: 0,
                        bgcolor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)",
                        color: "rgba(255,255,255,0.9)",
                        fontSize: "13px",
                        border: "none",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                      },
                    },
                  }}
                >
                  <SettingsSuggestRoundedIcon />
                </MenuButton>
                <Menu size="sm" placement="bottom-start" sx={{ minWidth: 200, zIndex: 1200 }}>
                  <Typography level="body-xs" sx={{ px: 1.5, py: 0.5, fontWeight: 600, opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "10px" }}>
                    Заполнение
                  </Typography>
                  {FIT_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} selected={photo.fit === opt.value} onClick={() => onFitChange(opt.value)} sx={{ fontSize: "12px" }}>
                      {photo.fit === opt.value ? "✓ " : "   "}{opt.label}
                    </MenuItem>
                  ))}
                  <Divider sx={{ my: 0.5 }} />
                  <Typography level="body-xs" sx={{ px: 1.5, py: 0.5, fontWeight: 600, opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "10px" }}>
                    Позиция
                  </Typography>
                  {POSITION_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} selected={photo.position === opt.value} onClick={() => onPositionChange(opt.value)} sx={{ fontSize: "12px" }}>
                      {photo.position === opt.value ? "✓ " : "   "}{opt.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Dropdown>

              <IconButton
                size="sm"
                onClick={(e) => { e.stopPropagation(); onRemove(); }}
                sx={{
                  minWidth: 30, minHeight: 30, p: 0,
                  bgcolor: "rgba(180,0,0,0.6)", backdropFilter: "blur(4px)",
                  color: "rgba(255,255,255,0.9)",
                  "&:hover": { bgcolor: "rgba(200,0,0,0.85)" },
                }}
              >
                <CloseRoundedIcon sx={{ color: "white" }} />
              </IconButton>
            </Box>
          )}
        </>
      )} */}
    </Box>
  );
}

// ─── Страница (полный разворот) ───────────────────────────────────────────────

function BookPage({ layout, photos, onSlotFileSelect, onSlotRemove, onSlotFitChange, onSlotPositionChange, pagePadding, cellGap, cellRadius, readOnly, side, onSlotEdit, isCoverSlot, pageWidthMm, pageHeightMm, pagesCount, fullBleed = false }) {
  let slotIndex = 0;
  const isFramed = !!layout?.framed;

  const renderSlot = (key, flex = 1, rect = null) => {
    const idx = slotIndex++;
    // Корешок и вылеты под обрез скрываются на уровне ВСЕЙ страницы (crop в
    // корне BookPage). Для ПУСТЫХ слотов дополнительно считаем, какая часть
    // слота скрыта, чтобы пунктирная рамка и «+» рисовались по центру
    // ВИДИМОЙ части ячейки, а не полной (иначе «+» визуально уезжает).
        const pageHid = (!fullBleed && !isFramed && !isCoverSlot && (side === "left" || side === "right"))
      ? getPageHiddenFractions(side, pagesCount, pageWidthMm, pageHeightMm)
      : null;
    let visibleInsets = null;
    if (pageHid) {
      const edges = getSlotEdges(layout, idx);
      const wf = getSlotWidthFraction(layout, idx) || 1;
      const hf = getSlotHeightFraction(layout, idx) || 1;
      visibleInsets = {
        left:   edges.left   ? Math.min(pageHid.left / wf, 0.45)   : 0,
        right:  edges.right  ? Math.min(pageHid.right / wf, 0.45)  : 0,
        top:    edges.top    ? Math.min(pageHid.top / hf, 0.45)    : 0,
        bottom: edges.bottom ? Math.min(pageHid.bottom / hf, 0.45) : 0,
      };
    }
    return (
      <Box key={key} className="photo-slot-wrap" sx={{ flex, minHeight: 0, minWidth: 0, display: "flex", position: "relative", ...(rect ? cellStyle(rect) : {}) }}>
        <PhotoSlot
          photo={photos[idx]}
          cellRadius={layout?.pdf ? 0 : cellRadius}
          printGeometry={!!layout?.pdf}
          onFileSelect={(data) => onSlotFileSelect(idx, data)}
          onRemove={() => onSlotRemove(idx)}
          onFitChange={(fit) => onSlotFitChange(idx, fit)}
          onPositionChange={(pos) => onSlotPositionChange(idx, pos)}
          readOnly={readOnly}
          onEdit={() => onSlotEdit?.(idx)}
          side={null}
          slotEdges={null}
          spineFraction={0}
          visibleInsets={visibleInsets}
          isCoverSlot={isCoverSlot}
        />
      </Box>
    );
  };

  const renderRow = (row, rIdx) => (
    <Stack
      key={rIdx}
      direction="row"
      sx={{
        // flex-доля из height конфига ("50%" → 0.5). Делит высоту точно,
        // в отличие от CSS height:% во flex-колонке (давало растяжение фото).
        flexGrow: (parseFloat(row.height) || 0) / 100,
        flexShrink: 1,
        flexBasis: 0,
        minHeight: 0,
        gap: 0,
      }}
    >
      {row.slots.map((slot, sIdx) => {
        if (slot.columnSlots) {
          return (
            <Stack key={sIdx} direction="column" sx={{ flex: slot.flex ?? 1, minHeight: 0, minWidth: 0, gap: 0 }}>
              {slot.columnSlots.map((col, cIdx) => renderSlot(`${rIdx}-${sIdx}-${cIdx}`, col.flex ?? 1))}
            </Stack>
          );
        }
        return renderSlot(`${rIdx}-${sIdx}`, slot.flex ?? 1);
      })}
    </Stack>
  );

    // В редакторе fullBleed показывает полный лист. В предпросмотре
  // скрываем припуски, сохраняя единый масштаб всей страницы и её полей.
  const crop = (!fullBleed && !isCoverSlot && (side === "left" || side === "right"))
    ? getPageHiddenFractions(side, pagesCount, pageWidthMm, pageHeightMm)
    : null;
  const fixedCells = pdfPageCells(layout, side);
  const frameInsets = isFramed && !fixedCells
    ? getFramePageInsets(side, pagesCount, pageWidthMm, pageHeightMm)
    : null;


  return (
        <Box sx={{ width: "100%", height: "100%", bgcolor: isCoverSlot ? "transparent" : "#fff", overflow: "hidden", boxSizing: "border-box", position: "relative" }}>
      <Box
        sx={crop ? {
          position: "absolute",
          left:   `${(-crop.left / crop.visW) * 100}%`,
          top:    `${(-crop.top / crop.visH) * 100}%`,
          width:  `${(1 / crop.visW) * 100}%`,
          height: `${(1 / crop.visH) * 100}%`,
                } : { position: "relative", width: "100%", height: "100%" }}
      >
        <Stack direction="column" sx={{
          gap: 0, boxSizing: "border-box",
          ...(frameInsets ? {
            position: "absolute",
            left: `${frameInsets.left * 100}%`,
            right: `${frameInsets.right * 100}%`,
            top: `${frameInsets.top * 100}%`,
            bottom: `${frameInsets.bottom * 100}%`,
          } : { position: "relative", width: "100%", height: "100%", p: 0 }),
        }}>
          {fixedCells
            ? fixedCells.map((cell, idx) => renderSlot(`pdf-${idx}`, 0, cell))
            : layout.rows.map((row, rIdx) => renderRow(row, rIdx))}
        </Stack>
      </Box>
    </Box>
  );
}


function getSpreadLabel(i, totalPages) {
  if (i === 0) return "Обложка";
  if (i === 1) return "1";  // Форзац — только правая стр = стр 1
  const leftNum = (i - 2) * 2 + 2;
  const rightNum = leftNum + 1;
  if (i === totalPages - 1) return `${leftNum}`; // Последний — только левая (правая = задний форзац)
  return `${leftNum} – ${rightNum}`;
}


// ─── Вертикально скроллируемый канвас всех разворотов ────────────────────────

function PagesScrollCanvas({ frontPhotoShape, backPhotoShape, children, bookId, onPickCoverLayout, onCoverPresetApplied, frontLayoutTag, backLayoutTag, coverDateMode, coverDateFrom, coverDateTo, saveCoverDateMode, saveCoverDateFrom, saveCoverDateTo, setPhotoEditor,
  pages, activePage, setActivePage, activeSide, setActiveSide, bookSize,
  pagePadding, cellGap, cellRadius, setShowCoverPicker,
  handleSlotFileSelect, handleSlotRemove, handleSlotFitChange, handleSlotPositionChange,
  isMobile, deletePage, setSheetTemplates,
  coverText, saveCoverText,
  coverYear, saveCoverYear,
  coverBackPhotos, onBackPhotoSelect, onBackPhotoRemove, onBackPhotoEdit,
  coverBgColor, setCoverBgColor,
  coverTitleColor, setCoverTitleColor, saveCoverAccentColor,
  coverAccentColor, setCoverAccentColor, coverDateError,
  coverTitleSize, setCoverTitleSize,
  coverTitleBreak, setCoverTitleBreak,
  coverStickerImage, onStickerClick,
  titleTooltipOpen, setTitleTooltipOpen,
  yearTooltipOpen, setYearTooltipOpen,
  coverTextError, coverTemplateObj,
  handleCoverBgColorChange
}) {
  const pageRefs = useRef([]);
  const scrollbarSx = {
    // "&::-webkit-scrollbar": { width: "6px" },
    // "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
    // "&::-webkit-scrollbar-thumb": {
    //   bgcolor: "color-mix(in srgb, var(--joy-palette-neutral-500) 20%, transparent)",
    //   borderRadius: "4px",
    // },
  };

  // Скроллим к активной странице при переключении через миниатюры
  const prevActivePage = useRef(activePage);
  const containerRef = useRef(null);
  if (prevActivePage.current !== activePage) {
    prevActivePage.current = activePage;
    setTimeout(() => {
      pageRefs.current[activePage]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  } 

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1, overflowY: "auto", overflowX: "hidden",
        bgcolor: "var(--joy-palette-background-body)", 
        ...scrollbarSx,
      }}
    >
      <Stack direction="column" alignItems="center" gap={0} sx={{ pt: 1, pb: 10, px: 3 }}>
        {children}
        {pages.map((pg, i) => {
          const isActive   = activePage === i;
          const isEP       = i === 1;
          const isCover    = i === 0;
          const leftLayout  = LAYOUTS.find(l => l.id === pg.leftLayoutId)  ?? LAYOUTS[0];
          const rightLayout = LAYOUTS.find(l => l.id === pg.rightLayoutId) ?? LAYOUTS[0];
          const isLastSpread = i === pages.length - 1;
          // let label = i === 0 ? "Обложка" : isEP ? "Форзац" : `${(i - 1) * 2 + 1} – ${(i - 1) * 2 + 2}`;
          let label = getSpreadLabel(i, pages.length);
          // Пропорция разворота в КОНСТРУКТОРЕ = ПОЛНАЯ страница (с корешком и
          // вылетами, «плюсование»). Предпросмотр остаётся по видимой области.
          const fullW = bookSize?.widthMm  ?? 155;
          const fullH = bookSize?.heightMm ?? 210;
          const ar = `${fullW * 2} / ${fullH}`;

          return (
            <Box
              key={i}
              ref={el => pageRefs.current[i] = el}
              sx={{
                width: "100%", maxWidth: 820,
                mb: 5,
              }}
            >
              {/* Подпись над разворотом + кнопки шаблонов */}
              <Box
                sx={{
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  minHeight: 24,
                }}
              >
                {/* Кнопка шаблон — левая страница */}
                {!isCover && !isEP && (
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivePage(i);
                      setActiveSide("left");
                      if (isMobile) {
                        setSheetTemplates(true);
                      } else {
                        setSheetTemplates(true);
                      }
                    }}
                    sx={{
                      position: "absolute", left: 0,
                      display: "flex", alignItems: "center", gap: 0.5,
                      px: 1, py: 0.4,
                      borderRadius: "8px",
                      cursor: "pointer",
                      color: (isActive && activeSide === "left") ? "primary.500" : "text.tertiary",
                      bgcolor: (isActive && activeSide === "left")
                        ? "color-mix(in srgb, var(--joy-palette-primary-500) 10%, transparent)"
                        : "#e6e6e6",
                      transition: "all 0.15s",
                      "&:hover": {
                        bgcolor: "color-mix(in srgb, var(--joy-palette-primary-500) 12%, transparent)",
                        color: "primary.500",
                      },
                      "&:active": { transform: "scale(0.93)" },
                    }}
                  >
                    <StyleRoundedIcon sx={{ fontSize: 14 }} />
                    <Typography sx={{ fontSize: "9px", fontWeight: 600, color: "inherit", letterSpacing: "0.05em" }}>
                      Шаблон
                    </Typography>
                  </Box>
                )}

                <Typography
                  level="body-xs"
                  sx={{
                    textAlign: "center",
                    color: isActive ? "primary.500" : "text.tertiary",
                    textTransform: "uppercase", letterSpacing: "0.12em", fontSize: "10px",
                    fontWeight: isActive ? 700 : 400,
                    transition: "color 0.18s",
                  }}
                >
                  {label}
                </Typography>

                {/* Кнопка шаблон — правая страница */}
                {!isCover && !isLastSpread && (
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivePage(i);
                      setActiveSide("right");
                      if (isMobile) {
                        setSheetTemplates(true);
                      } else {
                        setSheetTemplates(true);
                      }
                    }}
                    sx={{
                      position: "absolute", right: 0,
                      display: "flex", alignItems: "center", gap: 0.5,
                      px: 1, py: 0.4,
                      borderRadius: "8px",
                      cursor: "pointer",
                      color: (isActive && activeSide === "right") ? "primary.500" : "text.tertiary",
                      bgcolor: (isActive && activeSide === "right")
                        ? "color-mix(in srgb, var(--joy-palette-primary-500) 10%, transparent)"
                        : "#e6e6e6",
                      transition: "all 0.15s",
                      "&:hover": {
                        bgcolor: "color-mix(in srgb, var(--joy-palette-primary-500) 12%, transparent)",
                        color: "primary.500",
                      },
                      "&:active": { transform: "scale(0.93)" },
                    }}
                  >
                    <StyleRoundedIcon sx={{ fontSize: 14 }} />
                    <Typography sx={{ fontSize: "9px", fontWeight: 600, color: "inherit", letterSpacing: "0.05em" }}>
                      Шаблон
                    </Typography>
                  </Box>
                )}

                {/* Кнопка удаления — только на мобиле и только для обычных разворотов */}
                {/* {isMobile && !isCover && !isEP && (
                  <IconButton
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm("Удалить этот разворот?")) deletePage(i);
                    }}
                    sx={{
                      position: "absolute",
                      right: 0,
                      minWidth: 22, minHeight: 22, p: 0,
                      bgcolor: "rgba(180,0,0,0.08)",
                      color: "danger.500",
                      borderRadius: "8px",
                      "&:hover": { bgcolor: "rgba(180,0,0,0.16)" },
                    }}
                  >
                    <DeleteRoundedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                )} */}
              </Box>

                
              {i === 0 ? (
                // ── Обложка: полная ширина (задняя + передняя) ──
                <Box sx={{ width: "100%", mx: "auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                  
                  {/* Подписи "Задняя часть" / "Передняя часть" + кнопка выбора макета */}
                  <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-start", px: 1 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
                      <Box
                        onClick={(e) => { e.stopPropagation(); onPickCoverLayout?.("back"); }}
                        sx={{
                          display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.4,
                          borderRadius: "8px", cursor: "pointer", color: "text.tertiary", bgcolor: "#e6e6e6",
                          transition: "all 0.15s",
                          "&:hover": { bgcolor: "color-mix(in srgb, var(--joy-palette-primary-500) 12%, transparent)", color: "primary.500" },
                          "&:active": { transform: "scale(0.93)" },
                        }}
                      >
                        <StyleRoundedIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: "9px", fontWeight: 600, color: "inherit", letterSpacing: "0.05em" }}>
                          Макет
                        </Typography>
                      </Box>
                      <Typography level="body-xs" sx={{
                        fontSize: "9px", fontWeight: 600, color: "text.tertiary",
                        textTransform: "uppercase", letterSpacing: "0.1em",
                      }}>
                        Задняя часть
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                      <Box
                        onClick={(e) => { e.stopPropagation(); onPickCoverLayout?.("front"); }}
                        sx={{
                          display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.4,
                          borderRadius: "8px", cursor: "pointer", color: "text.tertiary", bgcolor: "#e6e6e6",
                          transition: "all 0.15s",
                          "&:hover": { bgcolor: "color-mix(in srgb, var(--joy-palette-primary-500) 12%, transparent)", color: "primary.500" },
                          "&:active": { transform: "scale(0.93)" },
                        }}
                      >
                        <StyleRoundedIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: "9px", fontWeight: 600, color: "inherit", letterSpacing: "0.05em" }}>
                          Макет
                        </Typography>
                      </Box>
                      <Typography level="body-xs" sx={{
                        fontSize: "9px", fontWeight: 600, color: "text.tertiary",
                        textTransform: "uppercase", letterSpacing: "0.1em",
                      }}>
                        Передняя часть
                      </Typography>
                    </Box>
                  </Box>

                  <CoverBlock
                    coverDateMode={coverDateMode}
                    frontPhotoShape={frontPhotoShape}
                    backPhotoShape={backPhotoShape}
                    coverPageCount={Math.max(0, (pages.length - 2) * 2)}
                    frontLayoutTag={frontLayoutTag}
                    backLayoutTag={backLayoutTag}
                    bookSize={bookSize}
                    isActive={isActive}
                    coverText={coverText}
                    showYear={coverDateMode !== "none"}
                    coverYear={computeCoverDateDisplay({ mode: coverDateMode, year: coverYear, dateFrom: coverDateFrom, dateTo: coverDateTo })}
                    coverBackPhotos={coverBackPhotos}
                    onBackPhotoSelect={onBackPhotoSelect}
                    onBackPhotoRemove={onBackPhotoRemove}
                    onBackPhotoEdit={onBackPhotoEdit}
                    coverBgColor={coverBgColor}
                    coverTitleColor={coverTitleColor}
                    coverAccentColor={coverAccentColor}
                    coverTitleSize={coverTitleSize}
                    coverTitleBreak={coverTitleBreak}
                    coverStickerImage={coverStickerImage}
                    onStickerClick={() => { setActivePage(0); setShowCoverPicker(true); }}
                  />

                  {/* ─── Пикер цвета фона (горизонтальный скролл) ─── */}
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{ width: "100%" }}
                  >
                    <CoverBgPicker
                      colors={(() => {
                        // Если стоит стикер — убираем из палитры цвета, с которыми
                        // он не сочетается (excluded_bg_colors). Клиент их даже не
                        // увидит и не выберет случайно. Без стикера — палитра полная.
                        const excluded = (coverTemplateObj?.excluded_bg_colors || [])
                          .map(c => c.toUpperCase());
                        if (!excluded.length) return COVER_PALETTES.bg;
                        return COVER_PALETTES.bg.filter(
                          c => !excluded.includes(c.toUpperCase())
                        );
                      })()}
                      value={coverBgColor}
                      onChange={handleCoverBgColorChange}
                    />
                  </Box>

                  {/* Поле ввода текста + выбор года */}
                  <Stack
                    direction="column"
                    gap={1}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ width: "100%", position: "relative", flexWrap: "wrap" }}
                  >
                    {/* Текстовое поле + кнопка настроек */}
                    <Box sx={{ flex: 1, position: "relative" }}>
                      <Box
                        sx={{
                          display: "flex", alignItems: "center",
                          border: "1.5px dashed",
                          borderColor: coverText
                            ? "color-mix(in srgb, var(--joy-palette-primary-400) 60%, transparent)"
                            : "color-mix(in srgb, var(--joy-palette-neutral-400) 40%, transparent)",
                          borderRadius: "10px",
                          px: 1.5, py: 1,
                          transition: "border-color 0.2s",
                          "&:focus-within": {
                            borderColor: "primary.400",
                            boxShadow: "0 0 0 2px color-mix(in srgb, var(--joy-palette-primary-400) 15%, transparent)",
                          },
                        }}
                      >
                        <textarea
                          rows={2}
                          placeholder="Введите текст"
                          value={coverText}
                          onChange={(e) => {
                            // Сначала чистим, потом проверяем лимиты
                            const cleaned = sanitizeText(e.target.value, { allowNewlines: true });
                            const lines = cleaned.split("\n");
                            if (lines.length > 2) return;
                            if (lines.some(l => l.length > 16)) return;
                            saveCoverText(cleaned);
                          }}
                          onPaste={(e) => {
                            // Перехватываем вставку — чистим до того, как она попадёт в input
                            e.preventDefault();
                            const pasted = (e.clipboardData || window.clipboardData).getData("text");
                            const cleaned = sanitizeText(pasted, { allowNewlines: true });
                            // Склеиваем с тем что уже есть, если выделение — учитываем
                            const target = e.target;
                            const start = target.selectionStart ?? coverText.length;
                            const end   = target.selectionEnd ?? coverText.length;
                            const next  = coverText.slice(0, start) + cleaned + coverText.slice(end);
                            // Применяем те же лимиты что и в onChange
                            const lines = next.split("\n");
                            if (lines.length > 2) return;
                            if (lines.some(l => l.length > 16)) return;
                            saveCoverText(next);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              const lines = coverText.split("\n");
                              if (lines.length >= 2) e.preventDefault();
                            }
                          }}
                          style={{
                            flex: 1, border: "none", outline: "none",
                            background: "transparent", fontSize: "16px",
                            fontWeight: 500, textAlign: "center",
                            color: "inherit", fontFamily: "inherit",
                            minWidth: 0, resize: "none",
                            lineHeight: 1.3,
                            height: "auto",
                            overflow: "hidden",
                          }}
                        />
                        {/* Кнопка настроек заголовка */}
                        <Box
                          onClick={(e) => { e.stopPropagation(); setTitleTooltipOpen(v => !v); setYearTooltipOpen(false); }}
                          sx={{
                            ml: 0.5, flexShrink: 0,
                            width: 26, height: 26,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            borderRadius: "7px",
                            cursor: "pointer",
                            bgcolor: titleTooltipOpen ? "primary.100" : "rgba(0,0,0,0.05)",
                            color: titleTooltipOpen ? "primary.600" : "text.tertiary",
                            transition: "background 0.15s",
                            fontSize: "14px",
                            "&:hover": { bgcolor: "primary.100", color: "primary.600" },
                          }}
                        >
                          🎨
                        </Box>
                      </Box>
                      {/* Тултип настроек заголовка */}
                      <TextSettingsTooltip
                        open={titleTooltipOpen}
                        anchorEl={null}
                        onClose={() => setTitleTooltipOpen(false)}
                        type="title"
                        titleColor={coverTitleColor}
                        accentColor={coverAccentColor}
                        titleSize={coverTitleSize}
                        titleBreak={coverTitleBreak}
                        coverBgColor={coverBgColor}
                        onTitleColorChange={setCoverTitleColor}
                        onAccentColorChange={setCoverAccentColor}
                        onTitleSizeChange={setCoverTitleSize}
                        onTitleBreakChange={setCoverTitleBreak}
                      />
                      {coverTextError && (
                        <Typography sx={{ color: "danger.500", fontSize: "11px", fontWeight: 500, mt: 0.5, px: 0.5 }}>
                          {coverTextError}
                        </Typography>
                      )}
                    </Box>

                    {/* Формат даты обложки: год / дата / от-и-до / без даты */}
                    <Box sx={{ position: "relative", display: "flex", alignItems: "flex-start", gap: 1, width: "100%" }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <CoverDateControls
                          mode={coverDateMode}
                          year={coverYear}
                          dateFrom={coverDateFrom}
                          dateTo={coverDateTo}
                          yearOptions={YEAR_OPTIONS}
                          onModeChange={saveCoverDateMode}
                          onYearChange={saveCoverYear}
                          onDateFromChange={saveCoverDateFrom}
                          onDateToChange={saveCoverDateTo}
                          error={coverDateError}
                        />
                      </Box>
                      {/* Кнопка цвета года/даты (скрыта в режиме «без даты») */}
                      {coverDateMode !== "none" && (
                        <Box
                          onClick={() => setYearTooltipOpen(v => !v)}
                          sx={{
                            flexShrink: 0, width: 34, height: 34, mt: "1px",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            borderRadius: "8px", cursor: "pointer",
                            bgcolor: yearTooltipOpen ? "primary.100" : "rgba(0,0,0,0.05)",
                            color: yearTooltipOpen ? "primary.600" : "text.tertiary",
                            fontSize: "14px",
                            "&:hover": { bgcolor: "primary.100", color: "primary.600" },
                          }}
                        >
                          🎨
                        </Box>
                      )}
                      <TextSettingsTooltip
                        open={yearTooltipOpen}
                        anchorEl={null}
                        onClose={() => setYearTooltipOpen(false)}
                        type="year"
                        titleColor={coverTitleColor}
                        accentColor={coverAccentColor}
                        coverBgColor={coverBgColor}
                        onTitleColorChange={setCoverTitleColor}
                        onAccentColorChange={saveCoverAccentColor}
                      />
                    </Box>

                    {/* Готовые шаблоны обложки — применяют перед+зад сразу */}
                    <CoverPresetGallery
                      bookId={bookId}
                      onApplied={onCoverPresetApplied}
                    />
                  </Stack>
                </Box>
              ) : (
                // Разворот 
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: ar,
                    // borderRadius: "8px", 
                    overflow: "hidden",
                    boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                    transition: "box-shadow 0.2s",
                    position: "relative",
                  }}
                >
                  <Stack direction="row" sx={{ width: "100%", height: "100%" }}>
                    {/* Левая страница */}
                    <Box
                      onClick={(e) => { e.stopPropagation(); setActivePage(i); setActiveSide("left"); }}
                      sx={{
                        flex: 1, minWidth: 0, overflow: "hidden", position: "relative",
                        cursor: "pointer",
                        boxShadow: (isActive && activeSide === "left")
                          ? "inset 0 0 0 2.5px var(--joy-palette-primary-400)"
                          : "none",
                        transition: "box-shadow 0.2s",
                        // borderRadius: "8px 0 0 8px",
                      }}
                    >
                      {isEP ? (
                        // Форзац — левая страница всегда пустая белая
                        <Box sx={{ width: "100%", height: "100%", bgcolor: "#f8f8fa" }} />
                      ) : (
                        <BookPage layout={leftLayout} photos={pg.left}
                          onSlotFileSelect={(idx, data) => { setActivePage(i); handleSlotFileSelect("left", idx, data, i); }}
                          onSlotRemove={(idx) => { setActivePage(i); handleSlotRemove("left", idx, i); }}
                          onSlotFitChange={(idx, fit) => { setActivePage(i); handleSlotFitChange("left", idx, fit, i); }}
                          onSlotPositionChange={(idx, pos) => { setActivePage(i); handleSlotPositionChange("left", idx, pos, i); }}
                          pagePadding={pagePadding} cellGap={cellGap} cellRadius={cellRadius}
                          side="left"
                          fullBleed
                          pageWidthMm={bookSize?.widthMm}
                          pageHeightMm={bookSize?.heightMm}
                          pagesCount={Math.max(0, (pages.length - 2) * 2)}
                          onSlotEdit={(idx) => setPhotoEditor({
                            photo: pg.left[idx],
                            side: "left",
                            pageIndex: i,
                            slotIndex: idx,
                            layout: leftLayout,
                          })}
                        />
                      )}
                    </Box>

                    {/* Корешок */}
                    <Box sx={{ position: "absolute", left: "50%", top: 0, bottom: 0, transform: "translateX(-50%)", pointerEvents: "none", zIndex: 2, width: i === 0 ? "10px" : "7px", flexShrink: 0, background: "linear-gradient(90deg, #d1d3d6 0%, #f5f5f6 50%, #d1d3d6 100%)" }} />

                    {/* Правая страница */}
                    <Box
                      onClick={(e) => { e.stopPropagation(); setActivePage(i); setActiveSide("right"); }}
                      sx={{
                        flex: 1, minWidth: 0, overflow: "hidden", position: "relative",
                        cursor: "pointer",
                        boxShadow: (isActive && activeSide === "right")
                          ? "inset 0 0 0 2.5px var(--joy-palette-primary-400)"
                          : "none",
                        transition: "box-shadow 0.2s",
                        // borderRadius: "0 8px 8px 0",
                      }}
                    >
                      {isEP ? (
                        // Форзац — правая страница доступна для фото
                        <BookPage
                          layout={LAYOUTS.find(l => l.id === pg.rightLayoutId) ?? LAYOUTS.find(l => l.id === "single")}
                          photos={pg.right}
                          onSlotFileSelect={(idx, data) => { setActivePage(i); handleSlotFileSelect("right", idx, data, i); }}
                          onSlotRemove={(idx) => { setActivePage(i); handleSlotRemove("right", idx, i); }}
                          onSlotFitChange={(idx, fit) => { setActivePage(i); handleSlotFitChange("right", idx, fit, i); }}
                          onSlotPositionChange={(idx, pos) => { setActivePage(i); handleSlotPositionChange("right", idx, pos, i); }}
                          pagePadding={pagePadding} cellGap={cellGap} cellRadius={cellRadius}
                          side="right"
                          fullBleed
                          pageWidthMm={bookSize?.widthMm}
                          pageHeightMm={bookSize?.heightMm}
                          pagesCount={Math.max(0, (pages.length - 2) * 2)}
                          onSlotEdit={(idx) => setPhotoEditor({
                            photo: pg.right[idx],
                            side: "right",
                            pageIndex: i,
                            slotIndex: idx,
                            layout: LAYOUTS.find(l => l.id === pg.rightLayoutId) ?? LAYOUTS.find(l => l.id === "single"),
                          })}
                        />
                      ) : isLastSpread ? (
                        // Последний разворот — правая страница пустая (как левая у форзаца)
                        <Box sx={{ width: "100%", height: "100%", bgcolor: "#f8f8fa" }} />
                      ) : (
                        <BookPage layout={rightLayout} photos={pg.right}
                          onSlotFileSelect={(idx, data) => { setActivePage(i); handleSlotFileSelect("right", idx, data, i); }}
                          onSlotRemove={(idx) => { setActivePage(i); handleSlotRemove("right", idx, i); }}
                          onSlotFitChange={(idx, fit) => { setActivePage(i); handleSlotFitChange("right", idx, fit, i); }}
                          onSlotPositionChange={(idx, pos) => { setActivePage(i); handleSlotPositionChange("right", idx, pos, i); }}
                          pagePadding={pagePadding} cellGap={cellGap} cellRadius={cellRadius}
                          side="right"
                          fullBleed
                          pageWidthMm={bookSize?.widthMm}
                          pageHeightMm={bookSize?.heightMm}
                          pagesCount={Math.max(0, (pages.length - 2) * 2)}
                          onSlotEdit={(idx) => setPhotoEditor({
                            photo: pg.right[idx],
                            side: "right",
                            pageIndex: i,
                            slotIndex: idx,
                            layout: rightLayout,
                          })}
                        />
                      )}
                    </Box>
                  </Stack>
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

// ─── Миниатюра разворота для грида ───────────────────────────────────────────

function SpreadMiniGrid({ pageData, bookSize, pagesCount = 0, hideLeft = false, hideRight = false }) {
  const w = bookSize?.widthMm || 155, h = bookSize?.heightMm || 210;
  return <Box sx={{ display: "flex", width: "100%", aspectRatio: `${2 * w} / ${h}` }}>
    {["left", "right"].map(side => <Box key={side} sx={{ flex: 1, minWidth: 0, height: "100%", position: "relative" }}>
      <PageMiniPreview layout={LAYOUTS.find(l => l.id === pageData[`${side}LayoutId`])}
        photos={pageData[side]} side={side} pageWidthMm={w} pageHeightMm={h} pagesCount={pagesCount}
        empty={side === "left" ? hideLeft : hideRight} />
    </Box>)}
  </Box>;
}

{/* ── Прикреплённая панель вкладок — по центру ── */}
function PhotoBookHeader({ setCenterTab, centerTab }) {
  return (
    <Box
      sx={{
        flexShrink: 0, 
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 10, py: 1.5,
        position: "sticky",
        top: 0, mb: 1,
        // Подложка чтобы не наслаивался контент
        bgcolor: "transparent",
      }}
    >
      <Stack
        direction="row"
        sx={{
          // my: 1.25,
          bgcolor: "#adadad55",
          backdropFilter: "blur(10px)",
          "-webkit-backdrop-filter": "blur(10px)",
          borderRadius: 15,
          p: 0.5,
          gap: 1,
          
          justifyContent: "center",

          // position: "sticky",
          // top: 10,
          // left: 0,placeholder="Введите текст"
          // right: "17px",
          // width: "fit-content",
          // mx: "auto",

        }}
      >
        {[
          { id: "pages", label: "Редакт.", icon: (c) => <MenuBookRoundedIcon sx={{ fontSize: 18, color: c }} /> },
          { id: "order", label: "Порядок", icon: (c) => <GridViewRoundedIcon sx={{ fontSize: 18, color: c }} /> },
          { id: "preview", label: "Предпросмотр", icon: (c) => <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: c }} /> },
        ].map(tab => (
          <Box
            key={tab.id}
            onClick={() => setCenterTab(tab.id)}
            sx={{
              px: { xs: 1.5, sm: 2.5 }, py: "6px",
              borderRadius: 12,
              cursor: "pointer",
              display: "flex", alignItems: "center", gap: 0.75,
              userSelect: "none",
              transition: "background 0.18s, color 0.18s, box-shadow 0.18s",
              bgcolor: centerTab === tab.id ? "background.surface" : "transparent",
              color: centerTab === tab.id ? "primary.600" : "text.secondary",
              boxShadow: centerTab === tab.id ? "0 1px 4px rgba(0,0,0,0.10)" : "none",
              "&:hover": {
                color: centerTab === tab.id ? "primary.600" : "primary.500",
                bgcolor: centerTab === tab.id
                  ? "background.surface"
                  : "color-mix(in srgb, var(--joy-palette-primary-500) 6%, transparent)",
              },
            }}
          >
            {tab.icon(centerTab === tab.id ? 'primary.500' : 'neutral.500')}
            <Typography
              level="body-xs"
              sx={{
                fontWeight: centerTab === tab.id ? 700 : 500,
                fontSize: "12px",
                color: centerTab === tab.id ? 'primary.500' : 'neutral.500',
                display: { xs: "none", sm: "block" },
              }}
            >
              {tab.label}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}


function SpreadCard({ pg, index, isActive, onClick, pages, coverBook, onMove, bookSize, renderCoverThumbnail }) {
  const isCover    = index === 0;
  const isEP       = index === 1;
  const isLastPage = index === pages.length - 1;
  const isMovable  = !isCover && !isEP && !isLastPage;
  const label = getSpreadLabel(index, pages.length);

  const canMoveLeft  = isMovable && index > 2;
  const canMoveRight = isMovable && index < pages.length - 2;

  return (
    <Box
      onClick={onClick}
      sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, cursor: "pointer" }}
    >
      <Box sx={{ width: "100%", position: "relative" }}>

        {/* Кнопки влево/вправо */}
        {isMovable && (
          <>
            <Box
              onClick={e => { e.stopPropagation(); if (canMoveLeft) onMove(index, -1); }}
              sx={{
                position: "absolute", top: "50%", left: 6, transform: "translateY(-50%)",
                zIndex: 3, width: 20, height: 20, borderRadius: "5px",
                bgcolor: canMoveLeft ? "rgba(255,255,255,0.92)" : "rgba(200,200,200,0.45)",
                backdropFilter: "blur(4px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                cursor: canMoveLeft ? "pointer" : "default",
                transition: "opacity .15s",
                opacity: canMoveLeft ? 1 : 0.35,
              }}
            >
              <Typography sx={{ fontSize: 10, lineHeight: 1, userSelect: "none" }}><ArrowBackIosRoundedIcon /></Typography>
            </Box>

            <Box
              onClick={e => { e.stopPropagation(); if (canMoveRight) onMove(index, 1); }}
              sx={{
                position: "absolute", top: "50%", right: 2, transform: "translateY(-50%)",
                zIndex: 3, width: 20, height: 20, borderRadius: "5px",
                bgcolor: canMoveRight ? "rgba(255,255,255,0.92)" : "rgba(200,200,200,0.45)",
                backdropFilter: "blur(4px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                cursor: canMoveRight ? "pointer" : "default",
                transition: "opacity .15s",
                opacity: canMoveRight ? 1 : 0.35,
              }}
            >
              <Typography sx={{ fontSize: 10, lineHeight: 1, userSelect: "none" }}><ArrowForwardIosRoundedIcon /></Typography>
            </Box>
          </>
        )}

        <Box sx={{
          width: "100%", borderRadius: "8px", overflow: "hidden",
          border: isActive
            ? "2px solid var(--joy-palette-primary-500)"
            : "2px solid var(--joy-palette-divider)",
          boxShadow: isActive ? "0 0 0 1px color-mix(in srgb, var(--joy-palette-primary-400) 30%, transparent)" : "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
          "&:hover": { borderColor: "color-mix(in srgb, var(--joy-palette-primary-400) 55%, transparent)" },
        }}>
          {isCover ? renderCoverThumbnail?.() : (
            <SpreadMiniGrid pageData={pg} bookSize={bookSize} pagesCount={Math.max(0, (pages.length - 2) * 2)}
              hideLeft={isEP} hideRight={isLastPage} />
          )}
        </Box>
      </Box>

      <Typography level="body-xs" sx={{
        fontSize: "10px", color: isActive ? "primary.500" : "text.tertiary",
        fontWeight: isActive ? 700 : 400,
        userSelect: "none",
      }}>
        {label}
      </Typography>
    </Box>
  );
}

// ─── Определяет, касается ли слот корешка ────────────────────────────────────
function doesSlotTouchSpine(layout, slotIndex, side) {
  if (!layout?.rows?.length) return true; // single — всегда касается

  let si = 0;
  for (const row of layout.rows) {
    const slotsInRow = [];
    for (const slot of row.slots) {
      if (slot.columnSlots) {
        for (let c = 0; c < slot.columnSlots.length; c++) {
          slotsInRow.push({ globalIndex: si, isFirstInRow: slot === row.slots[0], isLastInRow: slot === row.slots[row.slots.length - 1] });
          si++;
        }
      } else {
        slotsInRow.push({ globalIndex: si, isFirstInRow: slot === row.slots[0], isLastInRow: slot === row.slots[row.slots.length - 1] });
        si++;
      }
    }

    for (const s of slotsInRow) {
      if (s.globalIndex === slotIndex) {
        // На левой странице корешок справа → касается если слот последний в строке
        if (side === "left") return s.isLastInRow;
        // На правой странице корешок слева → касается если слот первый в строке
        if (side === "right") return s.isFirstInRow;
        return false;
      }
    }
  }
  return true; // fallback
}

// ─── Считает aspect ratio конкретного слота в layout ─────────────────────────
function getSlotAspectRatio(layout, slotIndex, pageWidthMm, pageHeightMm, pagesCount) {
  const fixed = pdfPageCells(layout);
  if (fixed?.[slotIndex]) return fixed[slotIndex].aspect;
  if (!layout?.rows?.length) return pageWidthMm / pageHeightMm;

  // framed: ячейка живёт в рабочей зоне = видимая страница минус белое поле 10 мм.
  // Это ровно та геометрия, что печатает воркер → рамка редактора == ячейке.
  let baseW = pageWidthMm;
  let baseH = pageHeightMm;
  if (layout?.framed) {
    const work = getFrameWorkSize(pageWidthMm, pageHeightMm, pagesCount);
    baseW = work.w;
    baseH = work.h;
  }

  let si = 0;
  for (const row of layout.rows) {
    const rowHeightFrac = parseFloat(row.height) / 100;
    const rowH = rowHeightFrac * baseH;
    const rowTotalFlex = row.slots.reduce((s, sl) => s + (sl.flex ?? 1), 0);

    for (const slot of row.slots) {
      const slotW = ((slot.flex ?? 1) / rowTotalFlex) * baseW;

      if (slot.columnSlots) {
        const colTotalFlex = slot.columnSlots.reduce((s, c) => s + (c.flex ?? 1), 0);
        for (const col of slot.columnSlots) {
          const colH = ((col.flex ?? 1) / colTotalFlex) * rowH;
          if (si === slotIndex) return slotW / colH;
          si++;
        }
      } else {
        if (si === slotIndex) return slotW / rowH;
        si++;
      }
    }
  }
  return baseW / baseH;
}


// ── Есть ли в книге хотя бы одно фото (для показа подсказки предпросмотра) ──
function bookHasPhotos(pages) {
  return (pages || []).some(
    (p) =>
      (p?.left && Object.keys(p.left).length > 0) ||
      (p?.right && Object.keys(p.right).length > 0)
  );
}

// ─── Главный компонент ────────────────────────────────────────────────────────

export default function PhotoBookEditor() {
  const { bookSize, setBookSize, bookId, savedBookIds, setSavedBookIds, setBookId, currentBook, addSavedBookId, setCurrentBook, showSizeModal, setShowSizeModal, showExportModal, setShowExportModal, leftPanelOpen, setLeftPanelOpen, setSidebarOpen, showCartModal, setShowCartModal, cartItems, setCartItems, showMyBooksModal, setShowMyBooksModal, selectedBookIds, setSelectedBookIds } = useContext(LayoutContext);

  const isMobile = useIsMobile();

  const [activePage, setActivePage]   = useState(0);
  const [pagePadding, setPagePadding] = useState(0);
  const [cellGap, setCellGap]         = useState(0);
  const [cellRadius, setCellRadius]   = useState(0);

  const previewRefs = useRef([]);

  const [showGiftModal, setShowGiftModal] = useState(false);

  const [coverText, setCoverText] = useState("");
  const [coverYear, setCoverYear] = useState(String(new Date().getFullYear()));

  // Тултипы настроек обложки
  const [titleTooltipOpen, setTitleTooltipOpen] = useState(false);
  const [yearTooltipOpen,  setYearTooltipOpen]  = useState(false);

  // Стиль обложки
  const [coverBgColor,    setCoverBgColor]    = useState(COVER_PALETTES.bg[25]);   // синий по умолчанию
  const [coverTitleColor, setCoverTitleColor] = useState(COVER_PALETTES.title[0]); // белый
  const [coverAccentColor,setCoverAccentColor]= useState(COVER_PALETTES.accent[0]);// белый
  const [coverTitleSize,  setCoverTitleSize]  = useState(COVER_CONFIG.titleFontSizeMax);
  const [coverTitleBreak, setCoverTitleBreak] = useState("word"); // "word" | "letter"
  // Стикер на передней части обложки
  const [coverStickerImage, setCoverStickerImage] = useState(null);
  // Полный объект выбранного стикера (нужен его excluded_bg_colors,
  // чтобы проверять сочетаемость при смене цвета фона).
  const [coverTemplateObj, setCoverTemplateObj] = useState(null);
  // Редактор фото для задней части обложки (слоты 0-3)
  const [coverBackPhotoEditor, setCoverBackPhotoEditor] = useState(null); // { index, photo, aspectRatio }
  const [coverPhotoSaving, setCoverPhotoSaving] = useState(false);
  const coverPhotoSavingRef = useRef(false);
  const coverEditorBookRef = useRef(bookId);
  coverEditorBookRef.current = bookId;
  
  // Фото задней части обложки (4 слота)
  const [coverBackPhotos, setCoverBackPhotos] = useState({});

  const [sheetMyBooks, setSheetMyBooks] = useState(false);

  // Загрузка существующей книги
  const [isLoadingBook, setIsLoadingBook] = useState(false);
  const [loadProgress, setLoadProgress] = useState({ label: "" });

  const [cartInitialStep, setCartInitialStep] = useState("cart");

  // Фото, ожидающее подтверждения через редактор (при первом выборе из файлов)
  const [pendingPhoto, setPendingPhoto] = useState(null);

  // Создание книги — прогресс
  const [isCreatingBook, setIsCreatingBook] = useState(false);
  const [createProgress, setCreateProgress] = useState({ current: 0, total: 0, label: "" });

  // Отслеживание загрузок: ключ = "pageIndex-side-slotIndex", значение = AbortController
  const uploadingSlots = useRef({});
  // Таймаут загрузки (мс)
  const UPLOAD_TIMEOUT = 30_000;

  const [photoEditor, setPhotoEditor] = useState(null);
  const [activeSide, setActiveSide] = useState(null); // "left" | "right" | null

  const spreadIds = useRef([]);
  const photoIds = useRef([]);

  const {
    syncFailedRef, hasSyncError, savingCount, saveErrorInfo, saveErrors,
    saveErrorOpen, showSaveError, closeSaveError, markSyncFailed, runSync,
    resetSyncForBook, getSyncState,
  } = useBookSync(bookId);
  const [pagesBook, setPagesBook] = useState(null);
  const pagesOpeningRef = useRef(false);

  const layoutBusyRef = useRef({});          // spreadId -> идёт смена макета (антидабл-клик)
  const [layoutSaving, setLayoutSaving] = useState({});  // pageIdx -> side, для индикатора

  const [sheetTemplates, setSheetTemplates]   = useState(false);
  const [sheetSettings,  setSheetSettings]    = useState(false);
  const [showCoverPicker, setShowCoverPicker] = useState(false);
  const [layoutPickerSide, setLayoutPickerSide] = useState(null); // "front" | "back" | null

  // Вкладка в центральной панели: "pages" | "order" | "preview"
  const [centerTab, setCenterTab] = useState("pages");
  // Разовая (на книгу) подсказка при первом входе в предпросмотр
  const [showPreviewHint, setShowPreviewHint] = useState(false);
  // Видимая ошибка оформления (alert в мобильных браузерах не показывается)
  const [checkoutError, setCheckoutError] = useState(null);

  useEffect(() => {
    if ((centerTab === "order" || showCartModal) && bookId) {
      bookApi.getBook(bookId).then(fresh => setCurrentBook(fresh)).catch(() => {});
    }
  }, [centerTab, showCartModal]);

  // Подсказка «редактор vs предпросмотр»: один раз на КНИГУ, при первом заходе
  // в предпросмотр ЗАПОЛНЕННОЙ книги. Флаг — в localStorage по bookId, поэтому в
  // другой (новой/старой) книге подсказка покажется снова.
  useEffect(() => {
    if (centerTab !== "preview" || !bookId) return;
    const key = `mb_preview_hint_seen:${bookId}`;
    let seen = false;
    try { seen = localStorage.getItem(key) === "1"; } catch (e) {}
    if (seen) return;
    if (!bookHasPhotos(pages)) return;   // только для заполненной книги
    setShowPreviewHint(true);
    try { localStorage.setItem(key, "1"); } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerTab, bookId]);
  
  // Секции левой панели
  const [sections, setSections] = useState({ layouts: true, paddings: true });

  // Экспорт
  const [isExporting, setIsExporting]         = useState(false);
  const [exportProgress, setExportProgress]   = useState(0);

  // pages[0] = обложка, pages[1] = форзац (пустой), pages[2+] = развороты
  const [pages, setPages] = useState([
    { leftLayoutId: "single", rightLayoutId: "single", left: {}, right: {} }, // обложка
    { leftLayoutId: "empty", rightLayoutId: "single", left: {}, right: {}, isEndpaper: true }, // форзац
  ]);

  const [validationProgress, setValidationProgress] = useState(0);
  const [validationStatus, setValidationStatus] = useState(""); // "" | "checking" | "done" | "error"
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [coverDateError, setCoverDateError] = useState(false);



  useEffect(() => {
    if (!isMobile) {
      // Переходим на десктоп — закрываем шторки и мобильные оверлеи
      setSheetTemplates(false);
      setSheetSettings(false);
      setLeftPanelOpen(false);
      setSidebarOpen(false);
    }
  }, [isMobile]);

  // ── Незавершённый заказ: показать модалку «оплатить/отменить» при возврате ──
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await bookApi.getMyOrders();
        if (cancelled) return;
        const restored = res?.returned_book_ids || [];
        if (restored.length) setSavedBookIds(prev => [...new Set([...prev, ...restored])]);
        const order = res?.pending_order;
        if (!order) return;
        // Решение запоминаем для конкретного заказа, а не навсегда для вкладки.
        if (sessionStorage.getItem("pendingOrderHandled") === String(order.id)) return;

        const action = await pendingOrderModal(order);

        if (action === "pay") {
          try {
            const { payment_url } = await bookApi.payOrder(order.id);
            if (payment_url) {
              window.location.href = payment_url;   // уходим на оплату
              return;
            }
          } catch (e) {
            console.error("Ошибка получения ссылки оплаты:", e?.message);
            alert("Не удалось открыть оплату. Обновите страницу и попробуйте снова.");
          }
        } else if (action === "cancel") {
          try {
            await bookApi.cancelOrder(order.id);
            sessionStorage.setItem("pendingOrderHandled", String(order.id));
            // Книги вернулись в корзину на сервере. Перезагружаем страницу —
            // это гарантированно подтянет их во все списки (корзина, «Мои книги»)
            // без риска рассинхрона локального стейта.
            window.location.reload();
          } catch (e) {
            if (e?.status === 409) {
              alert("Заказ уже оплачен — отменить нельзя.");
            } else {
              console.error("Ошибка отмены заказа:", e?.message);
              alert("Не удалось отменить заказ. Обновите страницу и попробуйте снова.");
            }
          }
        } else {
          // Закрыли без действия — не показываем снова в этой вкладке.
          sessionStorage.setItem("pendingOrderHandled", String(order.id));
        }
      } catch (e) {
        // Молча: проверка незавершённого заказа не должна ломать загрузку.
        console.debug("pending order check skipped:", e?.message);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  
  const coverTextTimeout = useRef(null);
  const [coverTextError, setCoverTextError] = useState(null);
  const saveCoverText = (text) => {
    // Чистим ввод от эмодзи, битых символов и невидимых
    const cleaned = sanitizeText(text, { allowNewlines: true, maxLength: 32 });
    if (cleaned !== coverText) {
      logEvent({
        source: "ui", event: "editor.cover_text.input", book_id: bookId,
        message: "Изменил текст обложки в редакторе",
        payload: { field: "cover_text", before: coverText, after: cleaned,
                   stage: "local_input", persisted: false },
      });
    }
    setCoverText(cleaned);
    setCoverTextError(null);
    setPages(prev => {
      const next = [...prev];
      next[0] = { ...next[0], coverText: cleaned };
      return next;
    });
    if (coverTextTimeout.current) clearTimeout(coverTextTimeout.current);
    coverTextTimeout.current = setTimeout(() => {
      if (bookId) {
        runSync(() => bookApi.updateBook(bookId, { coverText: cleaned }), { what: "текст обложки" })
          .then(() => { setCoverTextError(null); })
          .catch(err => {
            if (err.data?.cover_text) {
              setCoverTextError(Array.isArray(err.data.cover_text) ? err.data.cover_text[0] : err.data.cover_text);
            } else {
              console.error("Ошибка сохранения текста обложки:", err.message);
            }
          });
      }
    }, 600);
  };

  const coverYearTimeout = useRef(null);
  const saveCoverYear = (year) => {
    const mode = resolveCoverDateMode(coverDateMode, year);
    setCoverYear(year);
    setCoverDateMode(mode);
    setPages(prev => {
      const next = [...prev];
      next[0] = { ...next[0], coverYear: year };
      return next;
    });
    if (coverYearTimeout.current) clearTimeout(coverYearTimeout.current);
    // Значение и режим идут одним PATCH через существующую очередь.
    saveCoverStyle({ coverYear: year, coverDateMode: mode });
  };

  const [coverDateMode, setCoverDateMode] = useState("year");
  const [coverDateFrom, setCoverDateFrom] = useState("");
  const [coverDateTo,   setCoverDateTo]   = useState("");

  const saveCoverDateMode = (m) => {
    const patch = coverDateModePatch(m, {
      mode: coverDateMode, year: coverYear, dateFrom: coverDateFrom, dateTo: coverDateTo,
    });
    if (!patch) return;
    if (coverYearTimeout.current) clearTimeout(coverYearTimeout.current);
    // Сохраняем и текущее одиночное значение, чтобы отменённый debounce его не потерял.
    if (patch.coverYear === undefined) patch.coverYear = coverYear;
    setCoverDateMode(m);
    if (patch.coverDateFrom !== undefined) setCoverDateFrom(patch.coverDateFrom);
    if (patch.coverDateTo !== undefined) setCoverDateTo(patch.coverDateTo);
    setCoverYear(patch.coverYear);
    setPages(prev => prev.map((p, i) => i === 0 ? { ...p, coverYear: patch.coverYear } : p));
    setCoverDateError(false);
    saveCoverStyle(patch);
  };
  const saveCoverDateFrom = (v) => { setCoverDateFrom(v); setCoverDateError(false); saveCoverStyle({ coverDateFrom: v }); };
  const saveCoverDateTo   = (v) => { setCoverDateTo(v);   setCoverDateError(false); saveCoverStyle({ coverDateTo: v }); };
  const saveCoverAccentColor = (c) => {
    c = normalizeCoverColor(c);
    if (!isColorAllowed(coverBgColor, c)) return;
    setCoverAccentColor(c);
    saveCoverStyle({ coverAccentColor: c });
  };

  // Настройки обложки: объединяем быстрые изменения и сохраняем по ID книги.
  const coverStyleTimeout = useRef(null);
  const pendingCoverStyle = useRef(new Map());
  const coverStyleQueue = useRef(Promise.resolve());
  const flushCoverStyle = () => {
    if (coverStyleTimeout.current) clearTimeout(coverStyleTimeout.current);
    coverStyleTimeout.current = null;
    const batches = [...pendingCoverStyle.current.entries()];
    pendingCoverStyle.current.clear();
    for (const [targetBookId, updates] of batches) {
      coverStyleQueue.current = coverStyleQueue.current.then(async () => {
        try {
          await runSync(() => bookApi.updateBook(targetBookId, updates), {
            what: "настройки обложки", key: `cover-style:${Object.keys(updates).sort().join(",")}`, targetBookId,
          });
        } catch (err) {
          pendingCoverStyle.current.set(targetBookId, {
            ...updates, ...pendingCoverStyle.current.get(targetBookId),
          });
          console.error("Ошибка сохранения стиля обложки:", err.message);
        }
      });
    }
    return coverStyleQueue.current;
  };
  const saveCoverStyle = (updates) => {
    if (!bookId) return;
    const beforeValues = {
      coverBgColor, coverTitleColor, coverAccentColor, coverTitleSize,
      coverTitleBreak, coverYear, coverDateMode, coverDateFrom, coverDateTo,
    };
    const changes = Object.fromEntries(Object.entries(updates)
      .filter(([field, value]) => Object.hasOwn(beforeValues, field) && beforeValues[field] !== value)
      .map(([field, value]) => [field, { before: beforeValues[field], after: value }]));
    if (Object.keys(changes).length) {
      logEvent({ source: "ui", event: "editor.cover_style.input", book_id: bookId,
        message: "Изменил настройки обложки в редакторе",
        payload: { changes, stage: "local_input", persisted: false } });
    }
    pendingCoverStyle.current.set(bookId, {
      ...pendingCoverStyle.current.get(bookId), ...updates,
    });
    if (coverStyleTimeout.current) clearTimeout(coverStyleTimeout.current);
    coverStyleTimeout.current = setTimeout(flushCoverStyle, 600);
  };
  // При переключении книги/закрытии редактора не оставляем пакет в таймере.
  // Каждый пакет уже содержит ID своей книги; запросы выполняются по порядку.
  useEffect(() => () => { flushCoverStyle(); }, [bookId]);

  const openCoverPhotoEditor = (index, photo, isNew) => {
    if (coverPhotoSavingRef.current || photo?.uploading) return;
    const shape = normalizeCoverShape(index === 4
      ? currentBook?.front_cover_photo_shape : currentBook?.back_cover_photo_shape);
    const single = index === 4 || index === 5;
    const box = single ? coverPhotoGeometry(bookSize, shape) : null;
    setCoverBackPhotoEditor({ index, photo: { ...photo }, isNew, shape: single ? shape : undefined,
      exactCoverAspect: !single,
      aspectRatio: box?.aspect || (single ? getCoverCropAspects(bookSize).front : coverContentLayout(bookSize, "grid").cellAspect) });
  };
  const handleBackPhotoSelect = (index, data) => openCoverPhotoEditor(index, data, true);
  const handleBackPhotoEdit = (index) => {
    if (coverBackPhotos[index]) openCoverPhotoEditor(index, coverBackPhotos[index], false);
  };
  const handleBackPhotoRemove = async (index) => {
    const photo = coverBackPhotos[index];
    // Убираем из локального стейта сразу
    setCoverBackPhotos(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    // Удаляем с сервера — иначе после перезагрузки фото вернётся
    const serverId = photo?.serverId;
    if (serverId) {
      try {
        await runSync(() => bookApi.deleteCoverPhoto(serverId), { what: "удаление фотографии обложки", key: `cover-delete:${serverId}` });
      } catch (err) {
        console.error("Ошибка удаления фото обложки с сервера:", err.message);
      }
    }
  };

  // Пересобирает локальное состояние фото обложки из свежей книги сервера.
  // Нужно после смены макета/пресета: сервер удаляет часть cover_photos, и
  // локальный coverBackPhotos обязан это отразить (иначе в UI «висят» удалённые).
  const syncCoverPhotosFromBook = (book) => {
    const restored = {};
    (book?.cover_photos || []).forEach(cp => {
      restored[cp.grid_index] = {
        url: cp.image_url,
        fit: "cover",
        position: "center",
        serverId: cp.id,
        originalUrl: cp.original_url,
        cropData: cp.crop_data,
        isCropped: true,
      };
    });
    setCoverBackPhotos(restored);
  };

  const handleCoverBgColorChange = (color) => {
    color = normalizeCoverColor(color);
    if (!color) return;
    const title = isColorAllowed(color, coverTitleColor)
      ? normalizeCoverColor(coverTitleColor) : pickCoverTextColor(color, COVER_PALETTES.title);
    const accent = isColorAllowed(color, coverAccentColor)
      ? normalizeCoverColor(coverAccentColor) : pickCoverTextColor(color, COVER_PALETTES.accent);
    if (!title || !accent) return;

    setCoverBgColor(color);
    setCoverTitleColor(title);
    setCoverAccentColor(accent);
    // Один согласованный PATCH: новый фон и оба цвета текста вместе.
    const updates = { coverBgColor: color, coverTitleColor: title, coverAccentColor: accent };

    // Стикер: если текущий стикер не сочетается с новым фоном — убираем его.
    // (excluded_bg_colors хранится в объекте выбранного стикера.)
    const excluded = (coverTemplateObj?.excluded_bg_colors || []).map(c => c.toUpperCase());
    if (excluded.length && excluded.includes(color.toUpperCase())) {
      setCoverStickerImage(null);
      setCoverTemplateObj(null);
      updates.coverTemplateId = null;   // сбрасываем стикер и на сервере
      // Точное имя поля — как в других вызовах saveCoverStyle/updateBook для стикера.
      // Если сервер ждёт coverTemplateId=null для снятия стикера — оставить так.
    }

    saveCoverStyle(updates);
  };

  const handleCoverTitleColorChange = (color) => {
    color = normalizeCoverColor(color);
    if (!isColorAllowed(coverBgColor, color)) return;
    setCoverTitleColor(color);
    saveCoverStyle({ coverTitleColor: color });
  };

  const handleCoverAccentColorChange = (color) => {
    color = normalizeCoverColor(color);
    if (!isColorAllowed(coverBgColor, color)) return;
    setCoverAccentColor(color);
    saveCoverStyle({ coverAccentColor: color });
  };

  const handleCoverTitleSizeChange = (size) => {
    setCoverTitleSize(size);
    saveCoverStyle({ coverTitleSize: size });
  };

  const handleCoverTitleBreakChange = (brk) => {
    setCoverTitleBreak(brk);
    saveCoverStyle({ coverTitleBreak: brk });
  };


  const handleMoveSpread = (index, direction) => {
    const lastIdx = pages.length - 1;
    const newIndex = index + direction;

    // Нельзя двигать обложку (0), форзац (1), последнюю страницу
    if (index <= 1 || index === lastIdx) return;
    // Нельзя переместить на место форзаца или за пределы
    if (newIndex <= 1 || newIndex === lastIdx || newIndex < 0 || newIndex >= pages.length) return;

    setPages(prev => arrayMove(prev, index, newIndex));
    spreadIds.current = arrayMove(spreadIds.current, index, newIndex);
    if (photoIds.current.length > 0) {
      photoIds.current = arrayMove(photoIds.current, index, newIndex);
    }

    setActivePage(newIndex);

    if (bookId && spreadIds.current.length > 0) {
      runSync(() => bookApi.reorderSpreads(bookId, spreadIds.current), { what: "порядок разворотов" }).catch(() => {});
    }
  };


  const handleBookSizeConfirm = async (size) => {
    const totalPages = size.pages ?? 0;                           // контентные страницы (стороны)
    const spreadsToCreate = Math.max(1, Math.floor(totalPages / 2)); // контентных разворотов
    const targetPagesLen = spreadsToCreate + 2;                   // + обложка + форзац

    // ════════════════════════════════════════════════════════════════
    // СЛУЧАЙ A: книга УЖЕ создана — меняем размер БЕЗ сброса контента
    // ════════════════════════════════════════════════════════════════
    if (bookId) {
      setShowSizeModal(false);
      await handleChangeBookSize(currentBook);
      return;
    }

    // ════════════════════════════════════════════════════════════════
    // СЛУЧАЙ B: книги ещё нет — создаём с нуля (прежнее поведение)
    // ════════════════════════════════════════════════════════════════
    setBookSize(size);
    setShowSizeModal(false);
    setIsCreatingBook(true);
    setCreateProgress({ current: 0, total: spreadsToCreate + 2, label: "Создаём книгу…" });

    let createdBookId = null;

    try {
      // Шаг 1: Создание книги
      const sheetsCount = Math.floor((size.pages ?? 0) / 2);
      setCreateProgress({ current: 0, total: sheetsCount + 2, label: "Создаём книгу…" });

      const book = await bookApi.createBook({
        bookSizeId:   String(size.id),
        widthMm:      size.widthMm,
        heightMm:     size.heightMm,
        pagePadding:  0,
        cellGap:      0,
        cellRadius:   0,
        bookFormatId: size.id,
        pagesCount:   size.pages ?? 0,
      });
      setBookId(book.id);
      setCurrentBook(book);
      addSavedBookId(book.id);
      // Год обложки: значение по умолчанию (текущий год) показывается в UI, но
      // при создании книги клиент его не менял. Явно сохраняем его на сервере,
      // чтобы проверка оформления не ругалась «не указан год на обложке».
      if (!book.cover_year) {
        bookApi.updateBook(book.id, { coverYear }).catch(err =>
          console.error("Ошибка сохранения года обложки:", err.message)
        );
      }
      if (book.spreads?.[0]) spreadIds.current[0] = book.spreads[0].id;
      setCreateProgress(p => ({ ...p, current: 1, label: "Создаём форзац…" }));
      createdBookId = book.id;

      // Шаг 2: Форзац
      const endpaper = await bookApi.addSpread(book.id, {
        leftLayoutId: "empty",
        rightLayoutId: "single",
      });
      spreadIds.current[1] = endpaper.id;
      setCreateProgress(p => ({ ...p, current: 2, label: "Добавляем страницы…" }));

      // Шаг 3: Контентные страницы
      const newPages = [
        { leftLayoutId: "single", rightLayoutId: "single", left: {}, right: {} },
        { leftLayoutId: "empty",  rightLayoutId: "single", left: {}, right: {}, isEndpaper: true },
      ];

      for (let i = 0; i < spreadsToCreate; i++) {
        const spread = await bookApi.addSpread(book.id, {
          leftLayoutId: "single",
          rightLayoutId: "single",
        });
        spreadIds.current[2 + i] = spread.id;
        newPages.push({ leftLayoutId: "single", rightLayoutId: "single", left: {}, right: {} });
        setCreateProgress(p => ({
          ...p,
          current: 3 + i,
          label: `Добавляем развороты… ${i + 1} из ${spreadsToCreate}`,
        }));
      }

      setPages(newPages);
    } catch (err) {
      console.error("Ошибка создания книги:", err.message);
      alert("Не удалось создать книгу. Попробуйте ещё раз.");
      setShowSizeModal(true);
    } finally {
      setIsCreatingBook(false);
    }
  };

  const handleGoBack = async () => {
    // Если на предпросмотре — возвращаемся на редактирование
    if (centerTab === "preview" || centerTab === "order") {
        setCenterTab("pages");
        return;
    }

    if (!bookId) {
        setShowSizeModal(true);
        return;
    }

    // Информационная модалка: "ok" (выйти) | "cancel" (остаться)
    const choice = await confirmLeaveBookModal();
    if (choice !== "ok") return;   // закрыли/тапнули мимо — остаёмся в редакторе
    // Книга уже автосохранена и лежит в «Моих книгах» — просто выходим (сброс ниже)

    // Сбрасываем состояние
    setBookId(null);
    setCurrentBook(null);
    setBookSize(null);
    spreadIds.current = [];
    photoIds.current = [];
    setCoverText("");
    setCoverYear(String(new Date().getFullYear()));
    setTitleTooltipOpen(false);
    setYearTooltipOpen(false);
    setCoverBackPhotos({});
    setCoverStickerImage(null);
    setCoverBgColor(COVER_PALETTES.bg[25]);
    setCoverTitleColor(COVER_PALETTES.title[0]);
    setCoverAccentColor(COVER_PALETTES.accent[0]);
    setCoverTitleSize(COVER_CONFIG.titleFontSizeMax);
    setCoverTitleBreak("word");
    setActivePage(0);
    setCenterTab("pages");
    setPages([
        { leftLayoutId: "single", rightLayoutId: "single", left: {}, right: {} },
        { leftLayoutId: "empty", rightLayoutId: "single", left: {}, right: {}, isEndpaper: true },
    ]);

    // Открываем модалку выбора формата
    setShowSizeModal(true);
  };

  const handleCreateNewBook = () => {
    // Закрываем модалку корзины
    setShowCartModal(false);
    setCartInitialStep("cart");
    // Сбрасываем текущую книгу (но не корзину!)
    setBookId(null);
    setCurrentBook(null);
    setBookSize(null);
    spreadIds.current = [];
    photoIds.current = [];
    setCoverText("");
    setCoverYear(String(new Date().getFullYear()));
    setTitleTooltipOpen(false);
    setYearTooltipOpen(false);
    setCoverBackPhotos({});
    setCoverStickerImage(null);
    setCoverBgColor(COVER_PALETTES.bg[25]);
    setCoverTitleColor(COVER_PALETTES.title[0]);
    setCoverAccentColor(COVER_PALETTES.accent[0]);
    setCoverTitleSize(COVER_CONFIG.titleFontSizeMax);
    setCoverTitleBreak("word");
    setActivePage(0);
    setCenterTab("pages");
    setPages([
        { leftLayoutId: "single", rightLayoutId: "single", left: {}, right: {} },
        { leftLayoutId: "empty", rightLayoutId: "single", left: {}, right: {}, isEndpaper: true },
    ]);
    // Открываем модалку выбора формата
    setShowSizeModal(true);
  };

  const handleRemoveFromCart = async (idx) => {
    const item = cartItems[idx];
    if (!item) return;

    // Удаляем CartItem на сервере если есть
    if (item.cartItemId) {
      try {
        await bookApi.removeFromCart(item.cartItemId);
      } catch (err) {
        console.warn("Ошибка удаления CartItem:", err.message);
      }
    }

    // Сбрасываем in_cart на сервере
    if (item.bookId) {
      bookApi.updateBook(item.bookId, { inCart: false }).catch(err =>
        console.warn("Ошибка сброса in_cart:", err.message)
      );
    }

    // Обновляем локальное состояние
    setCartItems(prev => prev.filter((_, i) => i !== idx));
    setSelectedBookIds(prev => prev.filter(id => id !== item.bookId));
  };

  const handleChangeFormat = () => {
      // Контент НЕ сбрасываем. Реальное изменение размера выполнит
      // handleBookSizeConfirm после выбора формата в модалке:
      //  • больше страниц — допишет развороты, фото и обложка сохранятся;
      //  • меньше страниц — предупредит и срежет лишние развороты с конца.
      setShowSizeModal(true);
  };


  const handleExport = async ({ quality, includeBleed, colorSpace, filename }) => {
    if (!bookSize || !bookId) return;
    setIsExporting(true); setExportProgress(10);
    try {
      setExportProgress(30);
      await bookApi.downloadPdf(bookId, {
        quality, includeBleed, colorSpace, filename,
        onDownloadProgress: (e) => {
          if (e.total) setExportProgress(Math.min(Math.round(30 + (e.loaded / e.total) * 65), 95));
        },
      });
      setExportProgress(100);
      setShowExportModal(false);
    } catch (err) {
      console.error("Export error:", err);
      alert("Ошибка при экспорте: " + err.message);
    } finally {
      setIsExporting(false); setExportProgress(0);
    }
  };

  // ── Принудительный сброс всех дебаунсов обложки ──
  const flushCoverSaves = async () => {
    const promises = [];
    if (coverTextTimeout.current) {
      clearTimeout(coverTextTimeout.current);
      coverTextTimeout.current = null;
      if (bookId) promises.push(runSync(
        () => bookApi.updateBook(bookId, { coverText }), { what: "текст обложки" }));
    }
    if (coverYearTimeout.current) {
      clearTimeout(coverYearTimeout.current);
      coverYearTimeout.current = null;
      if (bookId) promises.push(runSync(
        () => bookApi.updateBook(bookId, { coverYear }), { what: "год обложки" }));
    }
    promises.push(flushCoverStyle());
    if (syncSettingsTimeout.current) {
      clearTimeout(syncSettingsTimeout.current);
      syncSettingsTimeout.current = null;
      if (bookId) promises.push(runSync(() => bookApi.updateBook(bookId, {
        pagePadding, cellGap, cellRadius,
      }), { what: "настройки страниц" }));
    }
    await Promise.allSettled(promises);
  };

  const handleAddToCart = async () => {
    // Если книга уже в корзине — просто открываем корзину
    const alreadyInCart = bookId && cartItems.some(item => item.bookId === bookId);
    if (alreadyInCart) {
      setShowCartModal(true);
      return;
    }

    const { isValid, notUploaded, emptyPages } = validateBook(pages);
    if (!isValid) {
      const labels = notUploaded.map(p => p.label).join(", ");
      alert(`Некоторые фото не загрузились. Откройте эти страницы и добавьте фото заново:\n\n${labels}`);
      return; // оставить прежний return, который шёл после alert
    }
    if (emptyPages.length > 0) {
      const labels = emptyPages.map(p => p.label).join(", ");
      const ok = window.confirm(
        `Внимание: часть страниц пустая и напечатается белой:\n\n${labels}\n\nПродолжить?`
      );
      if (!ok) return;
    }

    // ── Принудительно сбрасываем все дебаунсы и ждём сохранения ──
    await flushCoverSaves();
    if (syncFailedRef.current) { showSaveError(); return; }
    if (getSyncState().pending > 0) { alert("Дождитесь сохранения изменений."); return; }

    // Запрашиваем актуальные данные книги с сервера
    let price = currentBook?.price ?? null;
    let bookData = currentBook;
    if (bookId) {
      try {
        const freshBook = await bookApi.getBook(bookId);
        price = freshBook.price ?? null;
        bookData = freshBook;
        setCurrentBook(freshBook);
      } catch (err) {
        console.warn("Не удалось получить данные книги:", err.message);
      }
    }

    const coverUrl = pages[0]?.left?.[0]?.url ?? null;
    const newItem = {
      title: coverText || currentBook?.title || "Фотокнига",
      pagesCount: pages.length - 2,
      size: bookSize ? `${bookSize.widthMm}×${bookSize.heightMm} мм` : "—",
      coverUrl,
      price,
      bookId,
      inCart: true,
      pages: pages,
      bookFormatId: currentBook?.book_format ?? null,
      widthMm: bookSize?.widthMm ?? null,
      heightMm: bookSize?.heightMm ?? null,
      bookData,
    };
    setCartItems(prev => [...prev, newItem]);

    bookApi.updateBook(bookId, { inCart: true }).catch(err =>
      console.error("Ошибка обновления in_cart:", err.message)
    );

    // setShowCartModal(true);
  };

  const handleSelectBook = async (book) => {
    if (book === null) {
        setBookId(null);
        setCurrentBook(null);
        setBookSize(null);
        spreadIds.current = [];
        photoIds.current = [];
        setCoverText("");
        setCoverYear(String(new Date().getFullYear()));
        setTitleTooltipOpen(false);
        setYearTooltipOpen(false);
        setCoverBackPhotos({});
        setCoverStickerImage(null);
        setCoverBgColor(COVER_PALETTES.bg[25]);
        setCoverTitleColor(COVER_PALETTES.title[0]);
        setCoverAccentColor(COVER_PALETTES.accent[0]);
        setCoverTitleSize(COVER_CONFIG.titleFontSizeMax);
        setCoverTitleBreak("word");
        setActivePage(0);
        setPages([
            { leftLayoutId: "single", rightLayoutId: "single", left: {}, right: {} },
            { leftLayoutId: "empty", rightLayoutId: "single", left: {}, right: {}, isEndpaper: true },
        ]);
        setShowSizeModal(true);
        return;
    }

    setIsLoadingBook(true);
    setLoadProgress({ label: "Получаем информацию о книге…" });

    try {
        const fullBook = await bookApi.getBook(book.id);

        setLoadProgress({ label: "Восстанавливаем страницы…" });

        setBookId(fullBook.id);
        setCurrentBook(fullBook);
        setBookSize({
            id: fullBook.book_size_id,
            widthMm: fullBook.width_mm,
            heightMm: fullBook.height_mm,
        });

        setCoverText(fullBook.cover_text || "");
        setCoverYear(fullBook.cover_year || String(new Date().getFullYear()));
        setCoverDateMode(resolveCoverDateMode(fullBook.cover_date_mode, fullBook.cover_year));
        setCoverDateFrom(fullBook.cover_date_from || "");
        setCoverDateTo(fullBook.cover_date_to || "");
        setCoverTitleColor(normalizeCoverColor(fullBook.cover_title_color) || COVER_PALETTES.title[0]);
        setCoverTitleSize(fullBook.cover_title_size || COVER_CONFIG.titleFontSizeMax);
        setCoverTitleBreak(fullBook.cover_title_break || "word");
        setCoverAccentColor(normalizeCoverColor(fullBook.cover_accent_color) || COVER_PALETTES.accent[0]);
        setCoverBgColor(normalizeCoverColor(fullBook.cover_bg_color) || COVER_PALETTES.bg[25]);

        // Восстанавливаем стикер из cover_template_data
        if (fullBook.cover_template_data?.image_url) {
          setCoverTemplateObj(fullBook.cover_template_data);
          setCoverStickerImage(fullBook.cover_template_data.thumbnail_url);
        } else {
          setCoverStickerImage(null);
        }

        setPagePadding(fullBook.page_padding ?? 0);
        setCellGap(fullBook.cell_gap ?? 0);
        setCellRadius(fullBook.cell_radius ?? 0);

        spreadIds.current = [];
        photoIds.current = [];

        const spreads = (fullBook.spreads || []).slice().sort((a, b) => a.order - b.order);
        const restoredPages = [];

        // ── Получаем URL обложки из cover_template_data ──
        const coverTemplateData = fullBook.cover_template_data;
        const coverImageUrl = coverTemplateData?.thumbnail_url ?? null;

        setLoadProgress({ label: `Загружаем ${spreads.length} разворотов…` });
        
        // ── Восстанавливаем фото задней обложки ──
        const restoredCoverPhotos = {};
        if (fullBook.cover_photos && fullBook.cover_photos.length > 0) {
          fullBook.cover_photos.forEach(cp => {
            restoredCoverPhotos[cp.grid_index] = {
              url: cp.image_url,
              fit: "cover",
              position: "center",
              serverId: cp.id,
              // Фото задней обложки с сервера уже обрезано под слот
              isCropped: true,
              originalUrl: cp.original_url,   // НОВАЯ ВЕРСИЯ: оригинал для переоткрытия
              cropData: cp.crop_data,          // НОВАЯ ВЕРСИЯ: восстановление зума/области
            };
          });
        }
        setCoverBackPhotos(restoredCoverPhotos);

        spreads.forEach((spread, i) => {
            spreadIds.current[i] = spread.id;

            const leftPhotos = {};
            const rightPhotos = {};

            (spread.photos || []).forEach(photo => {
                const target = photo.side === "left" ? leftPhotos : rightPhotos;
                target[photo.slot_index] = {
                    url: photo.image_url,
                    fit: photo.fit || "cover",
                    position: photo.position || "center",
                    serverUrl: photo.image_url,
                    originalUrl: photo.original_url,   // НОВАЯ ВЕРСИЯ: оригинал для переоткрытия
                    cropData: photo.crop_data,          // НОВАЯ ВЕРСИЯ: восстановление выреза
                    // Фото с сервера уже обрезано под слот — рисуем 1:1, без доводки
                    isCropped: true,
                    rotation: 0,
                    scale: 1,
                    offsetX: 0,
                    offsetY: 0,
                };

                if (!photoIds.current[i]) photoIds.current[i] = {};
                if (!photoIds.current[i][photo.side]) photoIds.current[i][photo.side] = {};
                photoIds.current[i][photo.side][photo.slot_index] = photo.id;
            });

            const isFirstSpread = i === 0;
            const isEndpaper = i === 1;

            // ── Обложка: если это первый spread и есть cover_template — вставляем ──
            if (isFirstSpread && coverImageUrl) {
                leftPhotos[0] = {
                    url: coverImageUrl,
                    fit: "cover",
                    position: "center",
                    serverUrl: coverImageUrl,
                    // Обложка с сервера уже обрезана под слот — рисуем 1:1, без доводки
                    isCropped: true,
                    rotation: 0,
                    scale: 1,
                    offsetX: 0,
                    offsetY: 0,
                };
            }

            restoredPages.push({
                leftLayoutId: spread.left_layout_id || "single",
                rightLayoutId: spread.right_layout_id || "single",
                left: leftPhotos,
                right: rightPhotos,
                ...(isEndpaper ? { isEndpaper: true } : {}),
                ...(isFirstSpread ? {
                    coverText: fullBook.cover_text || "",
                    coverTemplate: coverTemplateData ?? null,
                } : {}),
            });
        });

        setPages(restoredPages.length > 0 ? restoredPages : [
            { leftLayoutId: "single", rightLayoutId: "single", left: {}, right: {} },
            { leftLayoutId: "empty", rightLayoutId: "single", left: {}, right: {}, isEndpaper: true },
        ]);

        setActivePage(0);
        setShowSizeModal(false);
        setLoadProgress({ label: "Готово!" });
        resetSyncForBook(fullBook.id);
    } catch (err) {
        console.error("Ошибка загрузки книги:", err.message);
        alert("Не удалось загрузить книгу: " + err.message);
    } finally {
        setIsLoadingBook(false);
    }
  };

  // Сменить размер/кол-во страниц выбранной книги из «Мои книги».
  // Если книга не открыта в редакторе — сначала загружаем её (станет текущей),
  // затем открываем выбор формата. Само изменение выполнит handleBookSizeConfirm.
  const handleChangeBookSize = async (book) => {
    if (!book?.id || pagesOpeningRef.current) return;
    pagesOpeningRef.current = true;
    try {
      await flushCoverSaves();
      if (syncFailedRef.current) { showSaveError(); return; }
      if (getSyncState().pending > 0 || coverPhotoSavingRef.current ||
          Object.keys(uploadingSlots.current).length ||
          Object.values(layoutBusyRef.current).some(Boolean)) {
        alert("Дождитесь окончания сохранения и загрузки фотографий.");
        return;
      }
      setShowSizeModal(false);
      setPagesBook(book);
    } finally { pagesOpeningRef.current = false; }
  };

  const handlePagesChanged = async (fresh) => {
    if (String(fresh.id) === String(bookId)) {
      setCurrentBook(fresh);
      setBookSize(prev => ({ ...prev, pages: fresh.pages_count,
        widthMm: fresh.width_mm, heightMm: fresh.height_mm }));
      await reloadBookIntoEditor(fresh);
    }
    setCartItems(prev => prev.map(item => String(item.bookId) === String(fresh.id)
      ? { ...item, price: fresh.price, pagesCount: fresh.pages_count / 2, bookData: fresh }
      : item));
    // Оба списка «Мои книги» перезапросят данные, сохраняя открытое окно.
    setSavedBookIds(prev => [...prev]);
  };

  // Перестраивает состояние редактора из объекта книги (ответ сервера).
  // ВАЖНО: развороты сортируются по order (защита от перемешивания после resize),
  // spreadIds/photoIds пересобираются строго из серверных данных. Это убирает
  // рассинхрон, из-за которого книга могла «опустеть» после изменения размера.
  const reloadBookIntoEditor = async (book) => {
    const spreads = (book.spreads || []).slice().sort((a, b) => a.order - b.order);

    const newSpreadIds = [];
    const newPhotoIds = [];
    const coverImageUrl = book.cover_template_data?.thumbnail_url ?? null;

    const restored = spreads.map((spread, i) => {
      newSpreadIds[i] = spread.id;

      const left = {};
      const right = {};
      (spread.photos || []).forEach(photo => {
        const target = photo.side === "left" ? left : right;
        target[photo.slot_index] = {
          url: photo.image_url,
          fit: photo.fit || "cover",
          position: photo.position || "center",
          serverUrl: photo.image_url,
          originalUrl: photo.original_url,
          cropData: photo.crop_data,
          isCropped: true,
          rotation: 0, scale: 1, offsetX: 0, offsetY: 0,
        };
        if (!newPhotoIds[i]) newPhotoIds[i] = {};
        if (!newPhotoIds[i][photo.side]) newPhotoIds[i][photo.side] = {};
        newPhotoIds[i][photo.side][photo.slot_index] = photo.id;
      });

      const isFirstSpread = i === 0;
      const isEndpaper = i === 1;

      if (isFirstSpread && coverImageUrl) {
        left[0] = {
          url: coverImageUrl, fit: "cover", position: "center",
          serverUrl: coverImageUrl, isCropped: true,
          rotation: 0, scale: 1, offsetX: 0, offsetY: 0,
        };
      }

      return {
        leftLayoutId: spread.left_layout_id || "single",
        rightLayoutId: spread.right_layout_id || "single",
        left, right,
        ...(isEndpaper ? { isEndpaper: true } : {}),
        ...(isFirstSpread ? {
          coverText: book.cover_text || "",
          coverTemplate: book.cover_template_data ?? null,
        } : {}),
      };
    });

    spreadIds.current = newSpreadIds;
    photoIds.current = newPhotoIds;
    setPages(restored.length > 0 ? restored : [
      { leftLayoutId: "single", rightLayoutId: "single", left: {}, right: {} },
      { leftLayoutId: "empty", rightLayoutId: "single", left: {}, right: {}, isEndpaper: true },
    ]);
    setActivePage(p => Math.min(p, Math.max(0, restored.length - 1)));
  };

  const buildPagesFromBook = (book) => {
    if (!book?.spreads) return [];
    return book.spreads.map(sp => {
      const left = {};
      const right = {};
      (sp.photos || []).forEach(p => {
        if (p.side === "left")  left[p.slot ?? 0]  = { url: p.image_url };
        if (p.side === "right") right[p.slot ?? 0] = { url: p.image_url };
      });
      return {
        leftLayoutId:  sp.left_layout_id  ?? sp.leftLayoutId  ?? null,
        rightLayoutId: sp.right_layout_id ?? sp.rightLayoutId ?? null,
        left,
        right,
      };
    });
  };

  // ВРЕМЕННО: без проверок — сразу открываем окно оформления.
  // Серверная валидация остаётся только в createOrder (внутри ModalCart).
  // const handleCheckout = async () => {
  //   // Если открыта книга и её ещё нет в локальной корзине — добавляем локально,
  //   // чтобы в окне оформления было что заказывать.
  //   if (bookId && !cartItems.some(i => i.bookId === bookId)) {
  //     const coverUrl = pages[0]?.left?.[0]?.url ?? null;
  //     setCartItems(prev =>
  //       prev.some(i => i.bookId === bookId)
  //         ? prev
  //         : [...prev, {
  //             title: coverText || currentBook?.title || "Фотокнига",
  //             pagesCount: pages.length - 2,
  //             size: bookSize ? `${bookSize.widthMm}×${bookSize.heightMm} мм` : "—",
  //             coverUrl,
  //             price: currentBook?.price ?? null,
  //             bookId,
  //             bookFormatId: currentBook?.book_format ?? null,
  //             widthMm: bookSize?.widthMm ?? null,
  //             heightMm: bookSize?.heightMm ?? null,
  //             bookData: currentBook ?? null,
  //           }]
  //     );
  //     setSelectedBookIds(prev => (prev.includes(bookId) ? prev : [...prev, bookId]));

  //     // Синхронизация корзины на сервере — в ФОНЕ, НЕ блокируя открытие окна.
  //     bookApi.addToCart(bookId).catch((e) => console.warn("addToCart (temp, не критично):", e));
  //     bookApi.updateBook(bookId, { inCart: true }).catch(() => {});
  //   }

  //   // Сразу на оформление.
  //   setCartInitialStep("checkout");
  //   setShowCartModal(true);
  // };

  const handleCheckout = async () => {
    setCheckoutError(null);
    await flushCoverSaves();
    if (getSyncState().pending > 0) { alert("Дождитесь сохранения изменений."); return; }
    // Защита от рассинхрона: если часть изменений не сохранилась из-за сети — не пускаем в оформление.
    if (syncFailedRef.current) { showSaveError(); return; }
    logUi("click_checkout", "Нажал «Оформить»", {
      book_id: bookId || null,
      selected: selectedBookIds ? selectedBookIds.length : 0,
      cart_items: cartItems ? cartItems.length : 0,
    });
    try {
        // 1) Если есть открытая книга и она не в корзине — добавляем (без открытия модалки)
        if (bookId && !cartItems.some(i => i.bookId === bookId)) {
            // Валидируем текущую книгу
            const { isValid, notUploaded, emptyPages } = validateBook(pages);
            if (!isValid) {
                // Настоящая ошибка: фото выбрано, но не догрузилось на сервер.
                const labels = notUploaded.map(p => p.label).join(", ");
                logEvent({ level: "warn", source: "ui", event: "checkout_blocked", message: "Есть недогруженные фото — оформление остановлено", payload: { notUploaded: labels }, book_id: bookId || null });
                alert(`Некоторые фото не загрузились. Откройте эти страницы и добавьте фото заново:\n\n${labels}`);
                return; // как и раньше — останавливаемся
            }
            if (emptyPages.length > 0) {
                // Пустые страницы — не ошибка, но предупреждаем осознанно.
                const labels = emptyPages.map(p => p.label).join(", ");
                const ok = window.confirm(
                    `Внимание: часть страниц не заполнена и напечатается пустой (белой):\n\n${labels}\n\n` +
                    `Оформить заказ как есть?`
                );
                if (!ok) return; // клиент отменил — даём дозаполнить
            }
            await flushCoverSaves();
            if (syncFailedRef.current) { showSaveError(); return; }
            if (getSyncState().pending > 0) { alert("Дождитесь сохранения изменений."); return; }
            // Добавляем в корзину на сервере
            try {
                await bookApi.addToCart(bookId);
                await bookApi.updateBook(bookId, { inCart: true });
                
                let bookData = currentBook;
                try {
                    bookData = await bookApi.getBook(bookId);
                    setCurrentBook(bookData);
                } catch (e) {}
                
                const coverUrl = pages[0]?.left?.[0]?.url ?? null;
                const newItem = {
                    title: coverText || currentBook?.title || "Фотокнига",
                    pagesCount: pages.length - 2,
                    size: bookSize ? `${bookSize.widthMm}×${bookSize.heightMm} мм` : "—",
                    coverUrl,
                    price: bookData?.price ?? null,
                    bookId,
                    bookFormatId: currentBook?.book_format ?? null,
                    widthMm: bookSize?.widthMm ?? null,
                    heightMm: bookSize?.heightMm ?? null,
                    bookData,
                };
                setCartItems(prev => {
                    if (prev.some(i => i.bookId === bookId)) return prev;
                    return [...prev, newItem];
                });
                setSelectedBookIds(prev => {
                    if (prev.includes(bookId)) return prev;
                    return [...prev, bookId];
                });
            } catch (err) {
                console.error("Ошибка добавления в корзину:", err);
                alert("Не удалось добавить книгу в корзину");
                return;
            }
        }

        // 2) Серверная валидация + синхронизация
        const result = await bookApi.validateCart({
          bookId: bookId,
          bookIds: selectedBookIds,
        });

        if (!result || !result.ok) {
            const problems = result?.problems || [];

            // Отдельно — книга уже оформлена в активном заказе.
            const ordered = problems.find(p => p.code === "already_in_active_order");
            if (ordered) {
                logEvent({ level: "warn", source: "ui", event: "checkout_already_ordered",
                    message: "Книга уже в активном заказе",
                    payload: { order_number: ordered.order_number, status: ordered.order_status } });
                if (!ordered.order_paid) {
                    // Неоплаченный заказ — предлагаем вернуться к оплате.
                    // Ссылка берётся у существующего заказа, либо сервер сгенерирует новую
                    // (эндпоинт POST /api/orders/<id>/pay/).
                    await confirmRetryPaymentModal({
                        title: ordered.title || "Книга",
                        orderNumber: ordered.order_number,
                        status: ordered.order_status,
                        onRetry: async () => {
                            const { payment_url } = await bookApi.payOrder(ordered.order_id);
                            if (!payment_url) throw new Error("Не удалось получить ссылку на оплату.");
                            window.location.href = payment_url;
                        },
                    });
                } else {
                    alert(
                        `«${ordered.title || "Книга"}» уже оформлена в заказе №${ordered.order_number} ` +
                        `(статус: ${ordered.order_status}).\n\nЗаказ оплачен — повторно оформлять не нужно.`
                    );
                }
                return;
            }

            // Прочие проблемы — обычный список.
            const shown = problems.slice(0, 8).map(p => `• ${p.message}`).join("\n");
            const extra = problems.length > 8 ? `\n\n…и ещё ${problems.length - 8}` : "";
            logEvent({ level: "warn", source: "ui", event: "checkout_validate_failed", message: "Серверная валидация не пройдена", payload: { problems } });
            alert(`Не удаётся оформить заказ:\n\n${shown || "Проверьте заполнение книги и попробуйте снова."}${extra}`);
            return;
        }

        // 3) Обновляем локальную корзину из ответа сервера (если cart вернулся)
        if (result.cart?.items) {
            const serverItems = result.cart.items.map(item => ({
                title: item.book_title || "Фотокнига",
                pagesCount: item.book_pages_count ?? 0,
                size: item.book_width_mm && item.book_height_mm
                    ? `${item.book_width_mm}×${item.book_height_mm} мм` : "—",
                coverUrl: item.book_cover_url ?? null,
                price: item.book_price ?? null,
                bookId: item.book_id,
                bookFormatId: item.book_format ?? null,
                widthMm: item.book_width_mm ?? null,
                heightMm: item.book_height_mm ?? null,
                cartItemId: item.id,
                bookData: null,
            }));
            setCartItems(serverItems);
            setSelectedBookIds(serverItems.map(i => i.bookId).filter(Boolean));
        }

        // 4) Подтверждение макета
        const confirmed = await confirmLayoutModal();
        if (!confirmed) return;

        // 5) Открываем корзину
        logUi("checkout_open_modal", "Открыл модалку оформления");
        setShowCartModal(true);
    } catch (err) {
        console.error("Ошибка проверки заказа:", err);
        setCheckoutError("Ошибка оформления: " + (err?.status ? `HTTP ${err.status} — ` : "") + (err?.message || String(err)));
    }
  };

  const syncCartFromServer = async () => {
    // Подгружаем все сохранённые книги и добавляем в корзину те, у которых in_cart=true
    try {
      const results = [];
      for (const id of savedBookIds) {
        try {
          const book = await bookApi.getBook(id);
          results.push(book);
        } catch (err) {
          // Книга не найдена — пропускаем
        }
      }
      const booksInCart = results.filter(b => b.in_cart);
      
      const newItems = [];
      setCartItems(prev => {
        const existingIds = new Set(prev.map(item => item.bookId));
        const items = booksInCart
          .filter(book => !existingIds.has(book.id))
          .map(book => {
            const coverPhoto = book.spreads?.[0]?.photos?.find(p => p.side === "left");
            const coverUrl = coverPhoto?.image_url ?? book.cover_template_data?.thumbnail_url ?? null;
            return {
              title: book.title || "Фотокнига",
              pagesCount: (book.spreads?.length ?? 2) - 2,
              size: `${book.width_mm}×${book.height_mm} мм`,
              coverUrl,
              price: book.price ?? null,
              bookId: book.id,
              bookFormatId: book.book_format ?? null,
              widthMm: book.width_mm,
              heightMm: book.height_mm,
              bookData: book,
            };
          });
        newItems.push(...items);
        return [...prev, ...items];
      });
      
      return booksInCart; // возвращаем загруженные книги
    } catch (err) {
      console.error("Ошибка синхронизации корзины:", err.message);
      return [];
    }
  };

  const handleCheckoutFromPreview = async () => {
    setShowValidationModal(true);
    setValidationStatus("checking");
    setValidationProgress(0);

    // Имитируем проверку с прогресс-баром
    const steps = [
        { progress: 20, label: "Проверяем обложку..." },
        { progress: 50, label: "Проверяем страницы..." },
        { progress: 80, label: "Проверяем фотографии..." },
        { progress: 100, label: "Проверка завершена" },
    ];

    for (const step of steps) {
        await new Promise(r => setTimeout(r, 400));
        setValidationProgress(step.progress);
    }

    // Реальная проверка
    const { isValid, notUploaded, emptyPages } = validateBook(pages);

    // Проверяем обложку — С УЧЁТОМ выбранных макетов сторон.
    // Держим в синхроне с бэком main/views.py::_validate_book.
    const frontTag = currentBook?.front_cover_layout_data?.tag || "sticker";
    const backTag  = currentBook?.back_cover_layout_data?.tag  || "classic";

    const hasCoverText = !!coverText;
    // Фото считаем ТОЛЬКО реально сохранённые на сервере (есть serverId и не в
    // процессе загрузки), иначе в заказ уйдёт неполная обложка, пока фото догружается.
    const coverSavedAt = (idx) => {
      const p = coverBackPhotos[idx];
      return !!(p && p.serverId && !p.uploading);
    };

    // Дата «От и до»: обе даты должны стоять
    const dateRangeIncomplete =
      coverDateMode === "range" &&
      (!(coverDateFrom || "").trim() || !(coverDateTo || "").trim());

    const problems = [];

    // Текст обложки нужен всегда.
    if (!hasCoverText) problems.push("Не указан текст на обложке");

    // Передняя сторона: "photo" → нужно фото (слот 4); иначе → нужен стикер.
    if (frontTag === "photo") {
      if (!coverSavedAt(4)) problems.push("Не добавлено фото на передней обложке");
    } else if (!coverStickerImage) {
      problems.push("Не выбран стикер обложки");
    }

    // Задняя сторона по макету.
    if (backTag === "photo") {
      if (!coverSavedAt(5)) problems.push("Не добавлено фото на задней обложке");
    } else if (backTag === "text" || backTag === "minimal") {
      // без фото — ничего не требуем
    } else {
      // classic / grid_photo — сетка 2×2 (слоты 0..3)
      const gridCount = [0, 1, 2, 3].filter(coverSavedAt).length;
      if (gridCount !== 4) problems.push(`Загружено ${gridCount} из 4 фото на задней обложке`);
    }
    if (dateRangeIncomplete) {
      problems.push("Не заполнена дата «от и до» на обложке");
      setCoverDateError(true);   // подсветить поля даты
    }
    if (!isValid) {
      // только недогруженные фото — это блокирующая проблема
      if (notUploaded.length > 0) {
        const labels = notUploaded.map(p => p.label).join(", ");
        problems.push(`Не загрузились фото: ${labels}`);
      }
      // пустые страницы НЕ добавляем в блокирующие problems — их разрешаем
    }

    if (problems.length > 0) {
        setValidationStatus("error");
        await new Promise(r => setTimeout(r, 500));
        setShowValidationModal(false);
        setValidationStatus("");
        alert("Книга не готова к оформлению:\n\n" + problems.join("\n"));
        return;
    }

    setValidationStatus("done");
    await new Promise(r => setTimeout(r, 300));
    setShowValidationModal(false);
    setValidationStatus("");

    // Подтверждение
    const confirmed = await confirmLayoutModal();
    if (!confirmed) return;

    // Добавляем в корзину если еще не там
    const alreadyInCart = bookId && cartItems.some(item => item.bookId === bookId);
    if (!alreadyInCart) {
        // Ждём завершения всех дебаунсов
        await flushCoverSaves();
        if (syncFailedRef.current) { showSaveError(); return; }
        if (getSyncState().pending > 0) { alert("Дождитесь сохранения изменений."); return; }

        const coverUrl = pages[0]?.left?.[0]?.url ?? null;
        let price = currentBook?.price ?? null;
        let bookData = currentBook;
        if (bookId) {
          try {
            const freshBook = await bookApi.getBook(bookId);
            price = freshBook.price ?? null;
            bookData = freshBook;
            setCurrentBook(freshBook);
          } catch (err) {
            console.warn("Не удалось получить данные книги:", err.message);
          }
        }
        const newItem = {
            title: coverText || currentBook?.title || "Фотокнига",
            pagesCount: pages.length - 2,
            size: bookSize ? `${bookSize.widthMm}×${bookSize.heightMm} мм` : "—",
            coverUrl,
            price,
            bookId,
            pages: pages,
            bookFormatId: currentBook?.book_format ?? null,
            widthMm: bookSize?.widthMm ?? null,
            heightMm: bookSize?.heightMm ?? null,
            bookData,
        };
        setCartItems(prev => [...prev, newItem]);
    }

    await syncCartFromServer();

    // Открываем модальное окно оформления
    setShowCartModal(true);
    setCartInitialStep("checkout");
  };

  const handleCoverConfirm = (coverTemplate) => {
    setCoverTemplateObj(coverTemplate);
    setCoverStickerImage(coverTemplate.thumbnail_url);
    // Если текущий фон не сочетается с новым стикером — переключаем фон на
    // первый сочетаемый цвет палитры (иначе применён «запрещённый» фон).
    const exc = (coverTemplate.excluded_bg_colors || []).map(c => c.toUpperCase());
    if (exc.length && exc.includes((coverBgColor || "").toUpperCase())) {
      const firstOk = COVER_PALETTES.bg.find(c => !exc.includes(c.toUpperCase()));
      if (firstOk) handleCoverBgColorChange(firstOk);
    }
    if (bookId) {
      runSync(() => bookApi.updateBook(bookId, { coverTemplateId: coverTemplate.id }), { what: "шаблон обложки" })
        .then(() => bookApi.getBook(bookId))
        .then(fresh => setCurrentBook(fresh))
        .catch(err => console.error("Ошибка сохранения обложки:", err.message));
    }
  };

  const page = pages[activePage];

  const setPageLayout = async (side, layoutId) => {
    logEvent({ source: "ui", event: "editor.layout.selection_attempt", book_id: bookId,
      message: "Выбирает макет страницы",
      payload: { side, page_index: activePage, spread_id: spreadIds.current[activePage] || null,
        before: pages[activePage]?.[`${side}LayoutId`] || "", after: layoutId,
        stage: "selection_attempt", persisted: false } });

    const pageIdx = activePage;
    const spreadId = spreadIds.current[pageIdx];

    // Без spreadId (обложка/форзац/ещё не создан на сервере) — старое поведение:
    // сервера нет, меняем локально и выходим.
    const newLayout = LAYOUTS.find(l => l.id === layoutId);
    const oldLayoutId = pages[pageIdx]?.[`${side}LayoutId`] || "";
    const resetsFixed = oldLayoutId !== layoutId &&
      (oldLayoutId.startsWith("pdf-") || layoutId.startsWith("pdf-"));
    if (resetsFixed && Object.values(pages[pageIdx]?.[side] || {}).some(Boolean) &&
        !window.confirm("У нового макета другие размеры ячеек. Фото этой страницы будут сброшены; их нужно загрузить и кадрировать заново. Продолжить?")) return;
    const newSlots = (layoutId && layoutId !== "empty" && newLayout) ? countSlots(newLayout) : 0;
    if (!spreadId) {
      setPages(prev => {
        const next = [...prev];
        const cur = next[pageIdx] || {};
        const oldSide = cur[side] || {};
        const keptSide = {};
        Object.keys(oldSide).forEach(k => { if (!resetsFixed && Number(k) < newSlots) keptSide[k] = oldSide[k]; });
        next[pageIdx] = { ...cur, [`${side}LayoutId`]: layoutId, [side]: keptSide };
        return next;
      });
      return;
    }

    // ── ПЕССИМИСТИЧНО: сначала сервер, потом UI ──
    // Не трогаем состояние, пока сервер не подтвердил. Иначе при сбое сети/
    // перезапуска UI показал бы новый макет, а в БД остался старый (баг 002081).
    if (layoutBusyRef.current[spreadId]) return;   // защита от даблкликов по макету
    layoutBusyRef.current[spreadId] = true;
    setLayoutSaving(s => ({ ...s, [pageIdx]: side }));   // индикатор на время запроса

    const update = side === "left" ? { leftLayoutId: layoutId } : { rightLayoutId: layoutId };

    const applyFromServer = (spread) => {
      // Применяем состояние из ОТВЕТА сервера — точная картина оставшихся фото.
      const kept = {};
      const idMapNew = {};
      (spread.photos || [])
        .filter(p => p.side === side)
        .forEach(p => {
          const si = p.slot_index;
          kept[si] = { url: p.image_url, cropData: p.crop_data, id: p.id };
          idMapNew[si] = p.id;
        });
      setPages(prev => {
        const next = [...prev];
        const cur = next[pageIdx] || {};
        next[pageIdx] = {
          ...cur,
          leftLayoutId: spread.left_layout_id,
          rightLayoutId: spread.right_layout_id,
          [side]: kept,
        };
        return next;
      });
      if (!photoIds.current[pageIdx]) photoIds.current[pageIdx] = {};
      photoIds.current[pageIdx][side] = idMapNew;
    };

    try {
      const spread = await runSync(async () => {
        try {
          return await bookApi.updateSpread(spreadId, update);
        } catch (e1) {
          await new Promise(r => setTimeout(r, 1200));   // сеть могла моргнуть — один повтор
          return await bookApi.updateSpread(spreadId, update);
        }
      }, { what: side === "left" ? "макет левой страницы" : "макет правой страницы", key: `layout:${spreadId}:${side}` });
      applyFromServer(spread);   // состояние из ответа сервера — UI до этого не трогали
    } catch (err) {
      // не удалось — UI НЕ меняли, состояние прежнее и совпадает с БД.
      // Модалку показал runSync (errorModal:true).
    } finally {
      delete layoutBusyRef.current[spreadId];
      setLayoutSaving(s => { const n = { ...s }; delete n[pageIdx]; return n; });
    }
  };

  const handleSlotFileSelect = async (side, idx, data, pageIndex) => {
    const targetPage = pageIndex !== undefined ? pageIndex : activePage;

    // Диагностика (только лог): что за файл дал клиент. Считается в фоне,
    // открытие редактора не блокирует.
    if (data.file) {
      (async () => {
        try {
          const fm = fileMeta(data.file);
          const im = await imageMeta(data.file);
          logEvent({
            level: "info", source: "ui", event: "photo_selected",
            book_id: bookId || null,
            message: `Фото выбрано: ${fm.file_name || "—"} ${fm.file_kb}KB ${im.nat_w || "?"}x${im.nat_h || "?"} (${im.megapixels || "?"}MP) тип ${fm.file_type || "?"} EXIF-поворот=${im.exif_rotated} края[L${im.edge_left ?? "-"} R${im.edge_right ?? "-"} T${im.edge_top ?? "-"} B${im.edge_bottom ?? "-"}]`,
            payload: { ...fm, ...im, page: targetPage, side, slot_index: idx, location: "page" },
          });
        } catch (e) { /* диагностика не влияет на загрузку */ }
      })();
    }

    // Если есть файл — не вставляем сразу, а открываем редактор для кадрирования
    if (data.file) {
      const layout = (() => {
        const pg = pages[targetPage];
        const layoutId = side === "left" ? pg.leftLayoutId : pg.rightLayoutId;
        return LAYOUTS.find(l => l.id === layoutId) ?? LAYOUTS.find(l => l.id === "single");
      })();

      setPendingPhoto({
        photo: { ...data },
        side,
        pageIndex: targetPage,
        slotIndex: idx,
        layout,
      });
      return;
    }

    // Без файла (например, обложка) — вставляем как раньше
    setPages(prev => {
      const next = [...prev];
      const pg = { ...next[targetPage] };
      pg[side] = { ...pg[side], [idx]: { ...data } };
      next[targetPage] = pg;
      return next;
    });
  };

  // Достаёт из ошибки понятную причину от сервера (а не общую заглушку).
  const photoUploadErrorText = (err) => {
    const d = err?.data;
    if (d && typeof d.detail === "string") return d.detail;                       // общий текст ошибки
    if (d && Array.isArray(d.image) && d.image.length) return d.image.join(" ");  // ошибка по файлу (напр. расширение)
    if (d && typeof d === "object") {                                             // прочие ошибки полей
      const parts = [];
      for (const k of Object.keys(d)) {
        const v = d[k];
        if (Array.isArray(v)) parts.push(v.join(" "));
        else if (typeof v === "string") parts.push(v);
      }
      if (parts.length) return parts.join(" ");
    }
    return err?.message || "Не удалось загрузить фото. Попробуйте ещё раз.";
  };

  const commitPhoto = async (side, idx, data, pageIndex, allowDuplicate = false) => {
    const slotKey = `${pageIndex}-${side}-${idx}`;

    // Вставляем фото в стейт с индикатором загрузки
    setPages(prev => {
      const next = [...prev];
      const pg = { ...next[pageIndex] };
      pg[side] = { ...pg[side], [idx]: { ...data, isUploading: true } };
      next[pageIndex] = pg;
      return next;
    });

    const spreadId = spreadIds.current[pageIndex];
    if (!spreadId) return;
    if (!data.file && !data.cropData) {
      // Ни обрезанного файла (legacy), ни координат (новая версия) — нечего загружать.
      // Файл кропа не создан (часто tainted canvas: исходник с медиа-домена без CORS →
      // canvas.toBlob() вернул null). Нельзя оставлять фото как будто оно загружено.
      setCheckoutError("Не удалось подготовить фото к загрузке (страница " + pageIndex + "). Удалите фото из слота и добавьте файл заново.");
      setPages(prev => {
        const next = [...prev];
        const pg = { ...next[pageIndex] };
        const s = { ...pg[side] };
        delete s[idx];
        pg[side] = s;
        next[pageIndex] = pg;
        return next;
      });
      return;
    }

    // Отмена предыдущей загрузки в этот же слот (если есть)
    if (uploadingSlots.current[slotKey]) {
      uploadingSlots.current[slotKey].abort();
    }

    const controller = new AbortController();
    uploadingSlots.current[slotKey] = controller;

    // Отличаем таймаут от отмены при замене/удалении фотографии.
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, UPLOAD_TIMEOUT);

    try {
      // Макет стороны для сервера. Если сторона локально ещё "empty" (например,
      // дописанный ресайзом разворот, который рендерится как single), НЕ шлём
      // "empty" вместе с фото — иначе локальный layout останется "empty" и
      // разойдётся с сервером (тот выставит "single"). Нормализуем к "single".
      const rawLayoutId = pages[pageIndex]?.[side === "left" ? "leftLayoutId" : "rightLayoutId"];
      const sideLayoutId = (rawLayoutId && rawLayoutId !== "empty") ? rawLayoutId : "single";

      // Синхронно поднимаем локальный layout, чтобы редактор совпадал с сервером.
      if (rawLayoutId !== sideLayoutId) {
        setPages(prev => {
          const next = [...prev];
          const pg = { ...next[pageIndex] };
          pg[side === "left" ? "leftLayoutId" : "rightLayoutId"] = sideLayoutId;
          next[pageIndex] = pg;
          return next;
        });
      }

      const photo = await runSync(() => bookApi.uploadPhoto(spreadId, side, idx, data.file, {
        fit: data.fit ?? "cover",
        position: data.position ?? "center",
        layoutId: sideLayoutId,
        allowDuplicate,
        sourceHash: data.sourceHash,
        original: data.originalFile,   // НОВАЯ ВЕРСИЯ: оригинал (или undefined при пере-сохранении)
        cropData: data.cropData,        // НОВАЯ ВЕРСИЯ: координаты выреза
        signal: controller.signal,
      }), { what: "загрузка фотографии", key: `upload:${spreadId}:${side}:${idx}`, silent: true });

      clearTimeout(timeout);
      delete uploadingSlots.current[slotKey];

      if (!photoIds.current[pageIndex]) photoIds.current[pageIndex] = {};
      if (!photoIds.current[pageIndex][side]) photoIds.current[pageIndex][side] = {};
      photoIds.current[pageIndex][side][idx] = photo.id;

      // Диагностика (только лог): что реально загрузилось.
      try {
        const mode = (data.cropData && data.originalFile) ? "original+crop"
                   : (data.cropData ? "crop_only(resave)" : "legacy_image");
        logEvent({
          level: "info", source: "ui", event: "photo_uploaded",
          book_id: bookId || null,
          message: `Фото загружено: разворот ${pageIndex} ${side} слот ${idx}, режим ${mode}, оригинал ${data.originalFile ? Math.round(data.originalFile.size / 1024) + "KB" : "—"}, server_id ${photo.id}`,
          payload: {
            page: pageIndex, side, slot_index: idx, mode,
            server_photo_id: photo.id,
            original_bytes: data.originalFile ? data.originalFile.size : null,
            converted: !!data.converted, layout_id: sideLayoutId,
          },
        });
      } catch (e) { /* диагностика не критична */ }

      // Лог: фото было сконвертировано из HEIC (для последующего отслеживания)
      if (data.converted) {
        logEvent({
          level: "info", source: "ui", event: "heic_converted",
          message: `HEIC сконвертирован: книга ${bookId}, разворот ${pageIndex}, ${side}, слот ${idx}, файл ${data.name || "—"}`,
          payload: { book_id: bookId, page: pageIndex, side, slot_index: idx, filename: data.name || "" },
          book_id: bookId || null,
        });
      }

      // Успех. Сначала ДОГРУЖАЕМ серверный (кадрированный) кадр в кэш и только
      // потом снимаем блюр — иначе на 1–3 сек мигает исходное фото, пока
      // серверная картинка качается. Ошибка загрузки блюр не держит (resolve).
      await preloadImage(photo.image_url);

      // Убираем флаг загрузки, ставим серверный URL
      setPages(prev => {
        const next = [...prev];
        const pg = { ...next[pageIndex] };
        const s = { ...pg[side] };
        if (s[idx]) {
          s[idx] = { ...s[idx], isUploading: false, url: photo.image_url, serverUrl: photo.image_url,
                     originalUrl: photo.original_url, cropData: photo.crop_data };
        }
        pg[side] = s;
        next[pageIndex] = pg;
        return next;
      });
    } catch (err) {
      clearTimeout(timeout);
      if (controller.signal.aborted && !timedOut) return;
      if (uploadingSlots.current[slotKey] === controller) delete uploadingSlots.current[slotKey];
      if (timedOut) err.code = "ETIMEDOUT";

      const isTimeout = err.name === "AbortError" || err.message?.includes("abort");
      const isDuplicate = err.status === 409 && err.data?.code === "duplicate_photo";

      // Откат — убираем фото из слота (при любой ошибке)
      setPages(prev => {
        const next = [...prev];
        const pg = { ...next[pageIndex] };
        const s = { ...pg[side] };
        delete s[idx];
        pg[side] = s;
        next[pageIndex] = pg;
        return next;
      });

      // Повтор фото — спрашиваем «Оставить / Заменить».
      if (isDuplicate && !allowDuplicate) {
        const keep = await confirmDuplicatePhotoModal(err.data?.detail);
        // «Оставить» → повторяем загрузку с флагом allow_duplicate.
        // «Заменить» → ничего не вставляем (слот уже очищен выше).
        if (keep) commitPhoto(side, idx, data, pageIndex, true);
        return;
      }

      markSyncFailed("загрузка фотографии", err, {
        key: `upload:${spreadId}:${side}:${idx}`,
      });
    }
  };

  // Один запрос сохраняет кадр и форму; предыдущий кадр остаётся до успеха.
  const uploadCoverBackPhoto = async (index, photo, photoShape) => {
    if (coverPhotoSavingRef.current) return false;
    const targetBookId = bookId;
    coverPhotoSavingRef.current = true;
    setCoverPhotoSaving(true);
    const sameBook = () => coverEditorBookRef.current === targetBookId;
    try {
      let allowDuplicate = false;
      let saved;
      for (;;) {
        try {
          saved = await runSync(() => bookApi.uploadCoverPhoto(targetBookId, index, photo.file, {
            allowDuplicate, sourceHash: photo.sourceHash,
            original: photo.originalFile, cropData: photo.cropData, photoShape,
          }), { what: "фотография обложки", key: `cover-upload:${index}`, targetBookId, silent: true });
          break;
        } catch (err) {
          if (err.status === 409 && err.data?.code === "duplicate_photo" && !allowDuplicate) {
            if (await confirmDuplicatePhotoModal(err.data?.detail)) { allowDuplicate = true; continue; }
            return false;
          }
          throw err;
        }
      }
      if (!sameBook()) return true;
      setCoverBackPhotos(prev => ({ ...prev,
        [index]: { url: saved.image_url, fit: "cover", position: "center", serverId: saved.id,
          originalUrl: saved.original_url, cropData: saved.crop_data, isCropped: true },
      }));
      if (photoShape !== undefined) {
        const field = index === 4 ? "front_cover_photo_shape" : "back_cover_photo_shape";
        setCurrentBook(prev => prev?.id === targetBookId ? { ...prev, [field]: saved.photo_shape ?? photoShape } : prev);
      }
      if (photo.converted) logEvent({ level: "info", source: "ui", event: "heic_converted",
        message: `HEIC сконвертирован: книга ${targetBookId}, обложка, ячейка ${index}`,
        payload: { book_id: targetBookId, location: "cover", grid_index: index }, book_id: targetBookId });
      return true;
    } catch (err) {
      markSyncFailed("фотография обложки", err, {
        key: `cover-upload:${index}`, targetBookId,
      });
      return false;
    } finally {
      coverPhotoSavingRef.current = false;
      setCoverPhotoSaving(false);
    }
  };

  const handleSlotRemove = async (side, idx, pageIndex) => {
    const targetPage = pageIndex !== undefined ? pageIndex : activePage;
    const slotKey = `${targetPage}-${side}-${idx}`;

    // Отменить текущую загрузку если идёт
    if (uploadingSlots.current[slotKey]) {
      uploadingSlots.current[slotKey].abort();
      delete uploadingSlots.current[slotKey];
    }

    // Убираем из локального стейта сразу
    setPages(prev => {
      const next = [...prev];
      const pg = { ...next[targetPage] };
      const s = { ...pg[side] };
      delete s[idx];
      pg[side] = s;
      next[targetPage] = pg;
      return next;
    });

    // Удаляем с сервера
    const photoId = photoIds.current[targetPage]?.[side]?.[idx];
    if (photoId) {
      try {
        await runSync(() => bookApi.deletePhoto(photoId), {
          what: "удаление фотографии", key: `photo-delete:${photoId}`,
        });
        delete photoIds.current[targetPage][side][idx];
      } catch (err) {
        // Сообщение уже открыл runSync.
      }
    }
  };

  const handleSlotFitChange = (side, idx, fit, pageIndex) => {
    const targetPage = pageIndex !== undefined ? pageIndex : activePage;
    setPages(prev => {
      const next = [...prev]; const pg = { ...next[targetPage] }; const s = { ...pg[side] };
      if (s[idx]) s[idx] = { ...s[idx], fit }; pg[side] = s; next[targetPage] = pg; return next;
    });
    const photoId = photoIds.current[targetPage]?.[side]?.[idx];
    if (photoId) runSync(() => bookApi.updatePhoto(photoId, { fit }), { what: "размещение фотографии", key: `photo-fit:${photoId}` }).catch(() => {});
  };

  const handleSlotPositionChange = (side, idx, position, pageIndex) => {
    const targetPage = pageIndex !== undefined ? pageIndex : activePage;
    setPages(prev => {
      const next = [...prev]; const pg = { ...next[targetPage] }; const s = { ...pg[side] };
      if (s[idx]) s[idx] = { ...s[idx], position }; pg[side] = s; next[targetPage] = pg; return next;
    });
    const photoId = photoIds.current[targetPage]?.[side]?.[idx];
    if (photoId) runSync(() => bookApi.updatePhoto(photoId, { position }), { what: "позиция фотографии", key: `photo-position:${photoId}` }).catch(() => {});
  };

  const addPage = async () => {
    const newIndex = pages.length;
    setPages(prev => [...prev, { leftLayoutId: "single", rightLayoutId: "single", left: {}, right: {} }]);
    setActivePage(newIndex);
    if (bookId) {
      try {
        const spread = await runSync(
          () => bookApi.addSpread(bookId, { leftLayoutId: "single", rightLayoutId: "single" }),
          { what: "добавление разворота" });
        spreadIds.current[newIndex] = spread.id;
      } catch (err) { /* индикатор показал ошибку */ }
    }
  };

  const deletePage = async (index) => {
    if (index <= 1) return; // нельзя удалить обложку и форзац
    const spreadId = spreadIds.current[index];
    if (spreadId) {
      runSync(() => bookApi.deleteSpread(spreadId), { what: "удаление разворота", key: `spread-delete:${spreadId}` }).catch(() => {});
      spreadIds.current.splice(index, 1);
    }
    if (photoIds.current[index]) photoIds.current.splice(index, 1);
    setPages(prev => prev.filter((_, i) => i !== index));
    setActivePage(prev => {
      if (index < prev) return prev - 1;
      if (index === prev) return Math.max(0, prev - 1);
      return prev;
    });
  };


  useEffect(() => {
    if (showSizeModal && bookId) {
      setShowSizeModal(false);
      handleChangeBookSize(currentBook);
    }
  }, [showSizeModal, bookId]);

  const syncSettingsTimeout = useRef(null);
  const syncSettings = useCallback((newPadding, newGap, newRadius) => {
    if (!bookId) return;
    clearTimeout(syncSettingsTimeout.current);
    syncSettingsTimeout.current = setTimeout(() => {
      runSync(() => bookApi.updateBook(bookId, { pagePadding: newPadding, cellGap: newGap, cellRadius: newRadius }), { what: "настройки страниц" })
        .catch(err => console.error("Sync settings error:", err.message));
    }, 600);
  }, [bookId]);

  

  return (
    <>
      <AnimatePresence>
        {isLoadingBook && (
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 20000,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.65)",
              backdropFilter: "blur(6px)",
              pt: { xs: "30vh", sm: "25vh" },
            }}
          >
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2.5,
                px: 4, py: 3.5,
                borderRadius: "20px",
                bgcolor: "background.surface",
                boxShadow: "0 12px 48px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
                border: "1px solid",
                borderColor: "divider",
                minWidth: 260,
              }}
            >
              <CircularProgress size="lg" sx={{
                "--CircularProgress-size": "56px",
                "--CircularProgress-trackThickness": "4px",
                "--CircularProgress-progressThickness": "4px",
              }} />
              <Box sx={{ textAlign: "center" }}>
                <Typography sx={{ fontWeight: 700, fontSize: "15px", mb: 0.5 }}>
                  Загрузка книги
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                  {loadProgress.label}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isCreatingBook && (
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 20000,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.65)",
              backdropFilter: "blur(6px)",
              pt: { xs: "30vh", sm: "25vh" },
            }}
          >
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2.5,
                px: 4,
                py: 3.5,
                borderRadius: "20px",
                bgcolor: "background.surface",
                boxShadow: "0 12px 48px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
                border: "1px solid",
                borderColor: "divider",
                minWidth: 260,
              }}
            >
              {/* Прогресс-бар */}
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress
                  size="lg"
                  determinate
                  value={
                    createProgress.total > 0
                      ? Math.round((createProgress.current / createProgress.total) * 100)
                      : undefined
                  }
                  sx={{
                    "--CircularProgress-size": "56px",
                    "--CircularProgress-trackThickness": "4px",
                    "--CircularProgress-progressThickness": "4px",
                  }}
                />
                <Box sx={{
                  position: "absolute", inset: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "primary.600" }}>
                    {createProgress.total > 0
                      ? `${Math.round((createProgress.current / createProgress.total) * 100)}%`
                      : "…"}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <Typography sx={{ fontWeight: 700, fontSize: "15px", mb: 0.5 }}>
                  Подготовка книги
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                  {createProgress.label}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {showValidationModal && (
            <Box
                component={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                sx={{
                    position: "fixed", inset: 0, zIndex: 20000,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: "rgba(255,255,255,0.65)",
                    backdropFilter: "blur(6px)",
                }}
            >
                <Box
                    component={motion.div}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    sx={{
                        display: "flex", flexDirection: "column", alignItems: "center",
                        gap: 2.5, px: 4, py: 3.5, borderRadius: "20px",
                        bgcolor: "background.surface",
                        boxShadow: "0 12px 48px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
                        border: "1px solid", borderColor: "divider", minWidth: 280,
                    }}
                >
                    <Box sx={{ position: "relative", display: "inline-flex" }}>
                        <CircularProgress
                            size="lg"
                            determinate
                            value={validationProgress}
                            color={validationStatus === "error" ? "danger" : "primary"}
                            sx={{
                                "--CircularProgress-size": "56px",
                                "--CircularProgress-trackThickness": "4px",
                                "--CircularProgress-progressThickness": "4px",
                            }}
                        />
                        <Box sx={{
                            position: "absolute", inset: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "primary.600" }}>
                                {validationProgress}%
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "15px", mb: 0.5 }}>
                            Проверяем книгу
                        </Typography>
                        <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                            {validationStatus === "checking" ? "Пожалуйста, подождите..." : validationStatus === "done" ? "Всё в порядке!" : "Есть проблемы..."}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        )}
      </AnimatePresence>

      <ModalBookPages
        key={pagesBook?.id || "closed"}
        book={pagesBook}
        onClose={() => setPagesBook(null)}
        onChanged={handlePagesChanged}
      />
      <ModalBookSize
        open={showSizeModal && !bookId}
        onConfirm={handleBookSizeConfirm}
        onClose={() => setShowSizeModal(false)}
      />

      <ModalExport
        open={showExportModal}
        onClose={() => !isExporting && setShowExportModal(false)}
        onExport={handleExport}
        bookSize={bookSize}
        pagesCount={pages.length}
        isExporting={isExporting}
        exportProgress={exportProgress}
      />

      <ModalGift
        open={showGiftModal}
        onClose={() => setShowGiftModal(false)}
      />

      {checkoutError && (
        <Box onClick={() => setCheckoutError(null)}
          sx={{ position:"fixed", left:12, right:12, bottom:96, zIndex:99999,
                bgcolor:"#c30013", color:"#fff", p:1.5, borderRadius:"12px",
                fontSize:13, lineHeight:1.4, whiteSpace:"pre-wrap",
                boxShadow:"0 6px 24px rgba(0,0,0,0.35)" }}>
          {checkoutError}{"\n"}<span style={{opacity:.8}}>(нажмите, чтобы скрыть)</span>
        </Box>
      )}

      <ModalCart
        open={showCartModal}
        onClose={() => setShowCartModal(false)}
        cartItems={cartItems}
        onRefreshItems={(items) => setCartItems(items)}
        onRemoveItem={handleRemoveFromCart}
        onCreateNewBook={handleCreateNewBook}
      />

      <Stack direction="column" sx={{ height: "100dvh", overflow: "hidden", bgcolor: "background.body" }}>

        <Header
          handleCheckout={handleCheckout}
          onAddToCart={handleAddToCart}
          onChangeFormat={handleChangeFormat}
          onMyBooks={() => setShowMyBooksModal(true)}
          onGift={() => setShowGiftModal(true)}
          onBack={handleGoBack}
        />

        <Stack direction="row" sx={{ flex: 1, overflow: "hidden" }}>

          {/* <Sidebar
            activePage={activePage}
            page={page}
            sections={sections}
            setSections={setSections}
            setPageLayout={setPageLayout}
            pagePadding={pagePadding}
            setPagePadding={setPagePadding}

            cellGap={cellGap}
            setCellGap={setCellGap}
            cellRadius={cellRadius}
            setCellRadius={setCellRadius}

            bookId={bookId}
          /> */}

          {/* LeftPanel: на десктопе всегда, на мобиле — слайд поверх */}
          {bookId && (
            <Stack direction="column" sx={{ flexShrink: 0, maxHeight: "100dvh" }}>
              <BottomPagePanelMini
                renderCoverThumbnail={() => (
                  <CoverBlock coverDateMode={coverDateMode} bookSize={bookSize} coverPageCount={Math.max(0, (pages.length - 2) * 2)}
                    frontLayoutTag={currentBook?.front_cover_layout_data?.tag}
                    backLayoutTag={currentBook?.back_cover_layout_data?.tag}
                    frontPhotoShape={currentBook?.front_cover_photo_shape}
                    backPhotoShape={currentBook?.back_cover_photo_shape}
                    coverText={coverText} showYear={coverDateMode !== "none"}
                    coverYear={computeCoverDateDisplay({ mode: coverDateMode, year: coverYear, dateFrom: coverDateFrom, dateTo: coverDateTo })}
                    coverBackPhotos={coverBackPhotos} coverBgColor={coverBgColor}
                    coverTitleColor={coverTitleColor} coverAccentColor={coverAccentColor}
                    coverStickerImage={coverStickerImage} readOnly />
                )}
                frontPhotoShape={currentBook?.front_cover_photo_shape}
                backPhotoShape={currentBook?.back_cover_photo_shape}
                pages={pages}
                activePage={activePage}
                setActivePage={setActivePage}
                activeSide={activeSide}
                setActiveSide={setActiveSide}
                centerTab={centerTab}
                setCenterTab={setCenterTab}
                addPage={addPage}
                deletePage={deletePage}
                isMobileOpen={leftPanelOpen}
                onCloseMobile={() => setLeftPanelOpen(false)}
                bookSize={bookSize}
                coverBgColor={coverBgColor}
                coverStickerImage={coverStickerImage}
                coverTitleColor={coverTitleColor}
                coverAccentColor={coverAccentColor}
                coverText={coverText}
                coverYear={computeCoverDateDisplay({ mode: coverDateMode, year: coverYear, dateFrom: coverDateFrom, dateTo: coverDateTo })}
                coverBackPhotos={coverBackPhotos}
                frontLayoutTag={currentBook?.front_cover_layout_data?.tag}
                backLayoutTag={currentBook?.back_cover_layout_data?.tag}
              />
            </Stack>
          )}

          {/* ── Центральная область ── */}
          <Stack direction="column" sx={{ flex: 1, overflow: "hidden", position: 'relative' }}>

            {/* Таймер срока хранения книги */}
            {bookId && <BookLifetimeChip createdAt={currentBook?.created_at} />}
            {bookId && <SaveStatusChip saving={savingCount > 0} error={hasSyncError} onClick={showSaveError} />}

            {!bookId ? (
              /* ── Заглушка: нет книги ── */
              <Box sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                p: 4,
              }}>
                <Box
                  onClick={() => setShowSizeModal(true)}
                  sx={{
                    width: 120, height: 120,
                    borderRadius: "24px",
                    border: "2.5px dashed",
                    borderColor: "color-mix(in srgb, var(--joy-palette-primary-400) 45%, transparent)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    bgcolor: "color-mix(in srgb, var(--joy-palette-primary-500) 6%, transparent)",
                    "&:hover": {
                      bgcolor: "color-mix(in srgb, var(--joy-palette-primary-500) 12%, transparent)",
                      borderColor: "primary.400",
                      transform: "scale(1.04)",
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 48, color: "primary.400", lineHeight: 1 }}>+</Typography>
                </Box>
                <Typography level="title-md" sx={{ fontWeight: 700, color: "text.primary" }}>
                  Создать книгу
                </Typography>
                <Typography level="body-sm" sx={{ color: "text.tertiary", textAlign: "center", maxWidth: 280 }}>
                  Выберите формат и начните добавлять фотографии
                </Typography>
                <Button
                  variant="outlined"
                  color="neutral"
                  size="sm"
                  onClick={() => isMobile ? setSheetMyBooks(true) : setShowMyBooksModal(true)}
                  sx={{ mt: 1, borderRadius: "12px", fontWeight: 600 }}
                >
                  Или откройте сохранённую книгу
                </Button>
              </Box>
            ) : (
              <>
                {/* ── Вкладка "Страницы": боковые миниатюры + вертикальный скролл ── */}
                {centerTab === "pages" && (
                  <Stack direction="column" sx={{ flex: 1, overflow: "hidden" }}>

                    {/* <PhotoBookHeader
                      setCenterTab={setCenterTab}
                      centerTab={centerTab}
                    /> */}

                    {/* Вертикально скроллируемый канвас */}
                    <PagesScrollCanvas
                      frontPhotoShape={currentBook?.front_cover_photo_shape}
                      backPhotoShape={currentBook?.back_cover_photo_shape}
                      bookId={bookId}
                      onPickCoverLayout={(side) => setLayoutPickerSide(side)}
                      onCoverPresetApplied={(updated) => { setCurrentBook(updated); syncCoverPhotosFromBook(updated); }}
                      frontLayoutTag={currentBook?.front_cover_layout_data?.tag}
                      backLayoutTag={currentBook?.back_cover_layout_data?.tag}
                      pages={pages}
                      setPhotoEditor={setPhotoEditor}
                      activeSide={activeSide}
                      setActiveSide={setActiveSide}
                      activePage={activePage}
                      setActivePage={setActivePage}
                      bookSize={bookSize}
                      pagePadding={pagePadding}
                      cellGap={cellGap}
                      isMobile={isMobile}
                      deletePage={deletePage}
                      cellRadius={cellRadius}
                      setShowCoverPicker={setShowCoverPicker}
                      handleSlotFileSelect={handleSlotFileSelect}
                      setSheetTemplates={setSheetTemplates}
                      handleSlotRemove={handleSlotRemove}
                      handleSlotFitChange={handleSlotFitChange}
                      handleSlotPositionChange={handleSlotPositionChange}
                      coverText={coverText}
                      saveCoverText={saveCoverText}
                      coverYear={coverYear}
                      saveCoverYear={saveCoverYear}
                      coverDateMode={coverDateMode}
                      coverDateFrom={coverDateFrom}
                      coverDateTo={coverDateTo}
                      saveCoverDateMode={saveCoverDateMode}
                      saveCoverDateFrom={saveCoverDateFrom}
                      saveCoverDateTo={saveCoverDateTo}
                      coverBackPhotos={coverBackPhotos}
                      onBackPhotoSelect={handleBackPhotoSelect}
                      onBackPhotoRemove={handleBackPhotoRemove}
                      onBackPhotoEdit={handleBackPhotoEdit}
                      coverBgColor={coverBgColor} setCoverBgColor={handleCoverBgColorChange}
                      coverTitleColor={coverTitleColor} setCoverTitleColor={handleCoverTitleColorChange}
                      saveCoverAccentColor={saveCoverAccentColor} coverDateError={coverDateError}
                      coverAccentColor={coverAccentColor} setCoverAccentColor={handleCoverAccentColorChange}
                      coverTitleSize={coverTitleSize}   setCoverTitleSize={handleCoverTitleSizeChange}
                      coverTitleBreak={coverTitleBreak} setCoverTitleBreak={handleCoverTitleBreakChange}
                      coverStickerImage={coverStickerImage}
                      titleTooltipOpen={titleTooltipOpen}
                      setTitleTooltipOpen={setTitleTooltipOpen}
                      yearTooltipOpen={yearTooltipOpen}
                      setYearTooltipOpen={setYearTooltipOpen}
                      coverTextError={coverTextError}
                      coverTemplateObj={coverTemplateObj}
                      handleCoverBgColorChange={handleCoverBgColorChange}
                    >
                      <PhotoBookHeader
                        setCenterTab={setCenterTab}
                        centerTab={centerTab}
                      />
                    </PagesScrollCanvas>
                  </Stack>
                )}

                {/* ── Вкладка "Порядок": полноэкранный грид миниатюр ── */}
                {centerTab === "order" && (
                  <Stack direction="column" sx={{ flex: 1, overflow: "hidden" }}>
                    <Box sx={{ flex: 1, overflowY: "auto", p: 3, pt: 0, pb: 13 }}>
                      <PhotoBookHeader setCenterTab={setCenterTab} centerTab={centerTab} />

                      <Typography level="body-xs" sx={{ color: "text.secondary", pt: 1, mb: 3, textAlign: "center", letterSpacing: "0.06em" }}>
                        Используйте стрелки ‹ › для изменения порядка разворотов
                      </Typography>

                      {/* Обложка — отдельно, не сортируется */}
                      <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
                        <Box sx={{ width: "calc(50% - 8px)", minWidth: 100, maxWidth: 200 }}>
                          <SpreadCard
                            pg={pages[0]} index={0}
                            isActive={activePage === 0}
                            onClick={() => { setActivePage(0); setCenterTab("pages"); }}
                            pages={pages}
                            bookSize={bookSize}
                            renderCoverThumbnail={() => (
                              <CoverBlock coverDateMode={coverDateMode} bookSize={bookSize} coverPageCount={Math.max(0, (pages.length - 2) * 2)}
                                frontLayoutTag={currentBook?.front_cover_layout_data?.tag}
                                backLayoutTag={currentBook?.back_cover_layout_data?.tag}
                                frontPhotoShape={currentBook?.front_cover_photo_shape}
                                backPhotoShape={currentBook?.back_cover_photo_shape}
                                coverText={coverText} showYear={coverDateMode !== "none"}
                                coverYear={computeCoverDateDisplay({ mode: coverDateMode, year: coverYear, dateFrom: coverDateFrom, dateTo: coverDateTo })}
                                coverBackPhotos={coverBackPhotos} coverBgColor={coverBgColor}
                                coverTitleColor={coverTitleColor} coverAccentColor={coverAccentColor}
                                coverStickerImage={coverStickerImage} readOnly />
                            )}
                            coverBook={currentBook}
                            onMove={handleMoveSpread}
                          />
                        </Box>
                      </Box>

                      {/* Форзац + развороты + последний форзац */}
                      <Box sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                        gap: 2,
                      }}>
                        {pages.slice(1).map((pg, i) => {
                          const realIdx = i + 1;
                          return (
                            <SpreadCard
                              bookSize={bookSize}
                              key={realIdx}
                              pg={pg}
                              index={realIdx}
                              isActive={activePage === realIdx}
                              onClick={() => { setActivePage(realIdx); setActiveSide("left"); setCenterTab("pages"); }}
                              pages={pages}
                              onMove={handleMoveSpread}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  </Stack>
                )}

                {/* ── Вкладка "Предпросмотр" ── */}
                {centerTab === "preview" && (
                  <Stack direction="column" sx={{ flex: 1, overflow: "hidden" }}>

                    {/* <PhotoBookHeader setCenterTab={setCenterTab} centerTab={centerTab} /> */}

                    <Box sx={{ flex: 1, overflowY: "auto", bgcolor: "#e8e8ed", pb: 4 }}>
                      <PhotoBookHeader
                        setCenterTab={setCenterTab}
                        centerTab={centerTab}
                      />
                      
                      <Stack direction="column" alignItems="center" gap={0} sx={{ pt: 1, pb: 5, px: 3 }}>

                        {pages.map((pg, i) => {
                          const isCover  = i === 0;
                          const isEP     = i === 1;
                          const isActive = activePage === i;

                          // Пропорции одной страницы
                          const singleAr = bookSize
                            ? `${bookSize.widthMm} / ${bookSize.heightMm}`
                            : "1 / 1.3";

                          // Пропорции разворота = ВИДИМАЯ область страниц
                          // (без корешка и вылетов под обрез)
                          const visPagePrev = getVisiblePageSize(
                            bookSize?.widthMm ?? 155,
                            bookSize?.heightMm ?? 210,
                            Math.max(0, (pages.length - 2) * 2),
                          );
                          const spreadAr = `${visPagePrev.w * 2} / ${visPagePrev.h}`;

                          const label = isCover ? "Обложка"
                            : isEP ? null
                            : getSpreadLabel(i, pages.length);

                          const shadow = isActive
                            ? "0 0 0 2.5px var(--joy-palette-primary-400), 0 12px 40px rgba(0,0,0,0.22)"
                            : "0 4px 24px rgba(0,0,0,0.14)";

                          // ── Обложка: полная ширина в предпросмотре ──
                          if (isCover) {
                            return (
                              <Box
                                key={i}
                                onClick={() => { setActivePage(i); setCenterTab("pages"); }}
                                sx={{ width: "100%", maxWidth: 600, mb: 6, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center" }}
                              >
                                <Typography level="body-xs" sx={{
                                  mb: 1, fontSize: "10px", textTransform: "uppercase",
                                  letterSpacing: "0.12em", fontWeight: isActive ? 700 : 400,
                                  color: isActive ? "primary.400" : "neutral.500",
                                }}>
                                  {label}
                                </Typography>
                                <CoverBlock
                                  coverDateMode={coverDateMode}
                                  frontPhotoShape={currentBook?.front_cover_photo_shape}
                                  backPhotoShape={currentBook?.back_cover_photo_shape}
                                  coverPageCount={Math.max(0, (pages.length - 2) * 2)}
                                  showYear={coverDateMode !== "none"}
                                  bookSize={bookSize}
                                  isActive={isActive}
                                  frontLayoutTag={currentBook?.front_cover_layout_data?.tag}
                                  backLayoutTag={currentBook?.back_cover_layout_data?.tag}
                                  coverText={coverText}
                                  coverYear={computeCoverDateDisplay({ mode: coverDateMode, year: coverYear, dateFrom: coverDateFrom, dateTo: coverDateTo })}
                                  coverBackPhotos={coverBackPhotos}
                                  onBackPhotoSelect={() => {}}
                                  onBackPhotoRemove={() => {}}
                                  onBackPhotoEdit={() => {}}
                                  coverBgColor={coverBgColor}
                                  coverTitleColor={coverTitleColor}
                                  coverAccentColor={coverAccentColor}
                                  coverTitleSize={coverTitleSize}
                                  coverTitleBreak={coverTitleBreak}
                                  coverStickerImage={coverStickerImage}
                                  onStickerClick={() => { setActivePage(0); setCenterTab("pages"); }}
                                  readOnly
                                />
                              </Box>
                            );
                          }

                          // ── Форзац: разворот, левая пустая белая, правая с фото ──
                          if (isEP) {
                            return (
                              <Box
                                key={i}
                                ref={el => previewRefs.current[i] = el}
                                onClick={() => { setActivePage(i); setCenterTab("pages"); }}
                                sx={{ width: "100%", maxWidth: 600, mb: 5, cursor: "pointer" }}
                              >
                                <Box sx={{
                                  position: "relative", width: "100%", aspectRatio: spreadAr,
                                  borderRadius: 0, overflow: "hidden",
                                  boxShadow: shadow, transition: "box-shadow 0.2s",
                                }}>
                                  <Stack direction="row" sx={{ width: "100%", height: "100%" }}>
                                    {/* Левая — пустая */}
                                    <Box sx={{ flex: 1, bgcolor: "#f8f8fa" }} />
                                    <Box sx={{ position: "absolute", left: "50%", top: 0, bottom: 0, transform: "translateX(-50%)", pointerEvents: "none", zIndex: 2, width: "5px", flexShrink: 0, background: "linear-gradient(90deg, #d1d3d6 0%, #f5f5f6 50%, #d1d3d6 100%)" }} />
                                    {/* Правая — фото если есть */}
                                    <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
                                      <BookPage
                                        layout={LAYOUTS.find(l => l.id === pg.rightLayoutId) ?? LAYOUTS.find(l => l.id === "single")}
                                        photos={pg.right}
                                        onSlotFileSelect={(idx, data) => { setActivePage(i); handleSlotFileSelect("right", idx, data, i); }}
                                        onSlotRemove={(idx) => { setActivePage(i); handleSlotRemove("right", idx, i); }}
                                        onSlotFitChange={(idx, fit) => { setActivePage(i); handleSlotFitChange("right", idx, fit, i); }}
                                        onSlotPositionChange={(idx, pos) => { setActivePage(i); handleSlotPositionChange("right", idx, pos, i); }}
                                        pagePadding={0} cellGap={0} cellRadius={0}
                                        side="right"
                                        pageWidthMm={bookSize?.widthMm}
                                        pageHeightMm={bookSize?.heightMm}
                                        pagesCount={Math.max(0, (pages.length - 2) * 2)}
                                        onSlotEdit={(idx) => setPhotoEditor({ 
                                          photo: pg.right[idx], 
                                          side: "right", 
                                          pageIndex: i, 
                                          slotIndex: idx, 
                                          layout: LAYOUTS.find(l => l.id === pg.rightLayoutId) ?? LAYOUTS.find(l => l.id === "single"),
                                        })}
                                      />
                                    </Box>
                                  </Stack>
                                </Box>
                              </Box>
                            );
                          }

                          // ── Обычный разворот ──
                          const leftLayout  = LAYOUTS.find(l => l.id === pg.leftLayoutId)  ?? LAYOUTS[0];
                          const rightLayout = LAYOUTS.find(l => l.id === pg.rightLayoutId) ?? LAYOUTS[0];

                          return (
                            <Box
                              key={i}
                              ref={el => previewRefs.current[i] = el}
                              sx={{ width: "100%", maxWidth: 600, mb: 5, cursor: "pointer" }}
                            >
                              {label && (
                                <Typography level="body-xs" sx={{
                                  mb: 1, textAlign: "center", fontSize: "10px",
                                  textTransform: "uppercase", letterSpacing: "0.12em",
                                  fontWeight: isActive ? 700 : 400,
                                  color: isActive ? "primary.400" : "rgba(255,255,255,0.45)",
                                  transition: "color 0.18s",
                                }}>
                                  {label}
                                </Typography>
                              )}
                              <Box sx={{
                                position: "relative", width: "100%", aspectRatio: spreadAr,
                                borderRadius: 0, overflow: "hidden",
                                boxShadow: shadow, transition: "box-shadow 0.2s",
                              }}>
                                <Stack direction="row" sx={{ width: "100%", height: "100%" }}>
                                  {/* Левая */}
                                  <Box
                                    onClick={(e) => { e.stopPropagation(); setActivePage(i); setActiveSide("left"); setCenterTab("pages"); }}
                                    sx={{
                                      flex: 1, minWidth: 0, overflow: "hidden", bgcolor: "#f8f8fa",
                                      position: "relative",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <BookPage
                                      layout={leftLayout}
                                      photos={pg.left}
                                      onSlotFileSelect={(idx, data) => { setActivePage(i); handleSlotFileSelect("left", idx, data, i); }}
                                      onSlotRemove={(idx) => { setActivePage(i); handleSlotRemove("left", idx, i); }}
                                      onSlotFitChange={(idx, fit) => { setActivePage(i); handleSlotFitChange("left", idx, fit, i); }}
                                      onSlotPositionChange={(idx, pos) => { setActivePage(i); handleSlotPositionChange("left", idx, pos, i); }}
                                      pagePadding={pagePadding} cellGap={cellGap} cellRadius={cellRadius} readOnly
                                      side="left"
                                      pageWidthMm={bookSize?.widthMm}
                                      pageHeightMm={bookSize?.heightMm}
                                      pagesCount={Math.max(0, (pages.length - 2) * 2)}
                                    />
                                  </Box>
                                  {/* Корешок */}
                                  <Box sx={{ position: "absolute", left: "50%", top: 0, bottom: 0, transform: "translateX(-50%)", pointerEvents: "none", zIndex: 2, width: "7px", flexShrink: 0, background: "linear-gradient(90deg, #d1d3d6 0%, #f5f5f6 50%, #d1d3d6 100%)" }} />
                                  {/* Правая */}
                                  {i === pages.length - 1 ? (
                                    /* Последний разворот — правая пустая (форзац) */
                                    <Box sx={{ flex: 1, bgcolor: "#f8f8fa" }} />
                                  ) : (
                                    <Box
                                      onClick={(e) => { e.stopPropagation(); setActivePage(i); setActiveSide("right"); setCenterTab("pages"); }}
                                      sx={{
                                        flex: 1, minWidth: 0, overflow: "hidden", bgcolor: "#f8f8fa",
                                        position: "relative",
                                        cursor: "pointer",
                                      }}
                                    >
                                      <BookPage
                                        layout={rightLayout}
                                        photos={pg.right}
                                        onSlotFileSelect={(idx, data) => { setActivePage(i); handleSlotFileSelect("right", idx, data, i); }}
                                        onSlotRemove={(idx) => { setActivePage(i); handleSlotRemove("right", idx, i); }}
                                        onSlotFitChange={(idx, fit) => { setActivePage(i); handleSlotFitChange("right", idx, fit, i); }}
                                        onSlotPositionChange={(idx, pos) => { setActivePage(i); handleSlotPositionChange("right", idx, pos, i); }}
                                        pagePadding={pagePadding} cellGap={cellGap} cellRadius={cellRadius} readOnly
                                        side="right"
                                        pageWidthMm={bookSize?.widthMm}
                                        pageHeightMm={bookSize?.heightMm}
                                        pagesCount={Math.max(0, (pages.length - 2) * 2)}
                                      />
                                    </Box>
                                  )}
                                </Stack>
                              </Box>
                            </Box>
                          );
                        })}

                      </Stack>
                    </Box>
                  </Stack>
                )}
              </>
            )}

            {/* {isMobile && ( */}
              <Box sx={{
                position: "absolute",
                bottom: 16,
                left: 0,
                right: 0,
                zIndex: 500,
                display: "flex",
                justifyContent: "center",
                pointerEvents: "none",
              }}>
                <Box sx={{ pointerEvents: "auto" }}>
                  <BottomNav
                    onAddPage={addPage}
                    onTemplates={() => setSheetTemplates(true)}
                    onPageSettings={() => setSheetSettings(true)}
                    onAddToCart={handleAddToCart}
                    onChangeFormat={handleChangeFormat}
                    onMyBooks={() => isMobile ? setSheetMyBooks(true) : setShowMyBooksModal(true)}
                    onPreview={() => setCenterTab("preview")}
                    onCheckout={handleCheckout}
                    isPreviewMode={centerTab === "preview"}
                    hasBook={!!bookId}
                    savedBooksCount={savedBookIds.length}
                    onBackToEditor={() => setCenterTab("pages")}
                  />
                </Box>
              </Box>
            {/* )} */}

          </Stack>

        </Stack>
       </Stack>
      
      <Modal
        open={saveErrorOpen}
        onClose={closeSaveError}
        sx={{ zIndex: 10040 }}
      >
        <ModalDialog
          sx={{
            maxWidth: 400,
            borderRadius: "20px",
            p: 3,
            textAlign: "center",
            boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
          }}
        >
          <IconButton
            aria-label="Закрыть сообщение об ошибке"
            size="sm"
            variant="plain"
            color="neutral"
            onClick={closeSaveError}
            sx={{ position: "absolute", top: 12, right: 12 }}
          >
            <CloseRoundedIcon />
          </IconButton>

          <Box sx={{
            width: 56, height: 56, borderRadius: "50%", mx: "auto", mb: 2,
            bgcolor: "color-mix(in srgb, var(--joy-palette-danger-500) 10%, transparent)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <ErrorOutlineRoundedIcon sx={{ fontSize: 28, color: "danger.500" }} />
          </Box>

          <Typography level="title-lg" sx={{ fontWeight: 800, mb: 0.5 }}>
            Не удалось подтвердить сохранение
          </Typography>
          <Box sx={{ maxHeight: "45dvh", overflowY: "auto", mb: 2 }}>
            {saveErrors.map((info, index) => (
              <Box key={index} sx={{ mb: 1.5 }}>
                <Typography level="body-sm" sx={{ fontWeight: 700 }}>
                  Не удалось подтвердить сохранение: {info.what}.
                </Typography>
                <Typography level="body-sm">{info.message}</Typography>
              </Box>
            ))}
            <Typography level="body-sm">
              Перед оформлением проверьте книгу. Закрытие этого окна не сохраняет изменения.
            </Typography>
          </Box>

          <Button
            fullWidth
            color="neutral"
            variant="solid"
            onClick={closeSaveError}
            sx={{ borderRadius: "12px", fontWeight: 700 }}
          >
            Понятно
          </Button>
        </ModalDialog>
      </Modal>

      <ModalPreviewHint
        open={showPreviewHint}
        onClose={() => setShowPreviewHint(false)}
      />

      <ModalPhotoEditor
        open={!!photoEditor || !!pendingPhoto}
        photo={photoEditor?.photo ?? pendingPhoto?.photo ?? null}
        logContext={(() => {
          const ctx = photoEditor ?? pendingPhoto;
          if (!ctx) return {};
          return {
            bookId,
            location: "page",
            side: ctx.side ?? null,
            slotIndex: ctx.slotIndex ?? null,
            pageIndex: ctx.pageIndex ?? null,
            photoId: photoIds.current?.[ctx.pageIndex]?.[ctx.side]?.[ctx.slotIndex] ?? null,
          };
        })()}
        side={(() => {
          const ctx = photoEditor ?? pendingPhoto;
          if (!ctx) return null;
          const touches = doesSlotTouchSpine(ctx.layout, ctx.slotIndex, ctx.side);
          return touches ? ctx.side : null;
        })()}
        bookSize={bookSize}
        spineFraction={(() => {
          const ctx = photoEditor ?? pendingPhoto;
          if (!ctx) return undefined;
          if (ctx.layout?.framed) return 0;   // framed: без корешка — фото 1:1 в блок
          return getSpineFraction(
            ctx.layout,
            ctx.slotIndex,
            bookSize?.widthMm,
            Math.max(0, (pages.length - 2) * 2),
          );
        })()}
        {...(() => {
          const ctx = photoEditor ?? pendingPhoto;
          if (!ctx) return {};
          if (ctx.layout?.framed) return { bleedFractionX: 0, bleedFractionY: 0 };  // framed: без вылетов
          const bf = getSlotBleedFractions(ctx.layout, ctx.slotIndex, bookSize?.widthMm, bookSize?.heightMm);
          return { bleedFractionX: bf.x, bleedFractionY: bf.y };
        })()}
        slotAspectRatio={
          (photoEditor || pendingPhoto) && bookSize
            ? getSlotAspectRatio(
                (photoEditor ?? pendingPhoto).layout,
                (photoEditor ?? pendingPhoto).slotIndex,
                bookSize.widthMm,
                bookSize.heightMm,
                Math.max(0, (pages.length - 2) * 2),
              )
            : undefined
        }
        slotEdges={(() => {
          const ctx = photoEditor ?? pendingPhoto;
          if (!ctx) return null;
          return getSlotEdges(ctx.layout, ctx.slotIndex);
        })()}
        onClose={() => {
          // Крестик — отменяем
          setPhotoEditor(null);
          setPendingPhoto(null);
        }}
        onConfirm={async (updatedPhoto) => {
          if (pendingPhoto) {
            const { side, pageIndex, slotIndex } = pendingPhoto;
            setPendingPhoto(null);
            await commitPhoto(side, slotIndex, updatedPhoto, pageIndex);
            return;
          }
          if (photoEditor) {
            const { side, pageIndex, slotIndex } = photoEditor;
            setPhotoEditor(null);
            await commitPhoto(side, slotIndex, updatedPhoto, pageIndex);
          }
        }}
        onDelete={() => {
          if (pendingPhoto) {
            // При первом выборе "удалить" = отмена
            setPendingPhoto(null);
            return;
          }
          if (!photoEditor) return;
          const { side, pageIndex, slotIndex } = photoEditor;
          handleSlotRemove(side, slotIndex, pageIndex);
          setPhotoEditor(null);
        }}
        onReplace={(file) => {
          if (pendingPhoto) {
            const url = URL.createObjectURL(file);
            setPendingPhoto(prev => ({
              ...prev,
              photo: { url, name: file.name, fit: "cover", position: "center", file },
            }));
            return;
          }
          if (!photoEditor) return;
          const { side, pageIndex, slotIndex } = photoEditor;

          // Удаляем старое фото с сервера
          const oldPhotoId = photoIds.current[pageIndex]?.[side]?.[slotIndex];
          if (oldPhotoId) {
            bookApi.deletePhoto(oldPhotoId).catch(err =>
              console.error("Ошибка удаления старого фото:", err.message)
            );
            delete photoIds.current[pageIndex][side][slotIndex];
          }

          // Открываем редактор с новым фото (через pending)
          const url = URL.createObjectURL(file);
          setPhotoEditor(null);
          setPendingPhoto({
            photo: { url, name: file.name, fit: "cover", position: "center", file },
            side,
            pageIndex,
            slotIndex,
            layout: photoEditor.layout,
          });
        }}
      />

      {/* ── Редактор фото задней части обложки ── */}
      <ModalPhotoEditor
        key={`cover-${bookId}-${coverBackPhotoEditor?.index}-${coverBackPhotoEditor?.shape}`}
        busy={coverPhotoSaving}
        exactCoverAspect={coverBackPhotoEditor?.exactCoverAspect}
        coverShape={coverBackPhotoEditor?.shape}
        previewBackground={coverBgColor ?? "#834A90"}
        open={!!coverBackPhotoEditor}
        photo={coverBackPhotoEditor?.photo ?? null}
        logContext={coverBackPhotoEditor ? {
          bookId,
          location: "cover",
          gridIndex: coverBackPhotoEditor.index ?? null,
          photoId: coverBackPhotoEditor?.photo?.serverId ?? null,
        } : {}}
        side={null}           /* нет корешка у обложки */
        bookSize={null}       /* передаём через slotAspectRatio */
        slotAspectRatio={coverBackPhotoEditor?.aspectRatio ?? 1}
        isCoverBack           /* флаг — без красных линий и корешка */
        onClose={() => { if (!coverPhotoSavingRef.current) setCoverBackPhotoEditor(null); }}
        onConfirm={async (updatedPhoto) => {
          if (!coverBackPhotoEditor || coverPhotoSavingRef.current || !bookId) return;
          const draft = coverBackPhotoEditor;
          const ok = await uploadCoverBackPhoto(draft.index, updatedPhoto, draft.shape);
          if (ok) setCoverBackPhotoEditor(prev => prev === draft ? null : prev);
        }}
        onDelete={async () => {
          if (!coverBackPhotoEditor || coverPhotoSavingRef.current) return;
          const { index, isNew } = coverBackPhotoEditor;

          if (!isNew) {
            const photo = coverBackPhotos[index];
            // Удаляем с сервера если есть serverId
            if (photo?.serverId) {
              try {
                await runSync(() => bookApi.deleteCoverPhoto(photo.serverId), { what: "удаление фотографии обложки", key: `cover-delete:${photo.serverId}` });
              } catch (err) {
                console.warn("Ошибка удаления фото обложки с сервера:", err.message);
              }
            }
            setCoverBackPhotos(prev => {
              const next = { ...prev };
              delete next[index];
              return next;
            });
          }
          setCoverBackPhotoEditor(null);
        }}
        onReplace={(file) => {
          if (coverPhotoSavingRef.current) return;
          const url = URL.createObjectURL(file);
          setCoverBackPhotoEditor(prev => prev ? {
            ...prev,
            photo: { url, name: file.name, fit: "cover", position: "center", file },
          } : null);
        }}
      />
      
      {/* ── Пикер обложки ── */}
      {isMobile ? (
        <BottomSheetCoverPicker
          open={showCoverPicker}
          onClose={() => setShowCoverPicker(false)}
          onConfirm={handleCoverConfirm}
          bookId={bookId}
          coverBgColor={coverBgColor}
        />
      ) : (
        <ModalCoverPicker
          open={showCoverPicker}
          onClose={() => setShowCoverPicker(false)}
          onConfirm={handleCoverConfirm}
          bookId={bookId}
          coverBgColor={coverBgColor}
        />
      )}

      {/* ── Мои книги ── */}
      {isMobile ? (
        <BottomSheetMyBooks
          open={sheetMyBooks}
          onClose={() => setSheetMyBooks(false)}
          onSelectBook={handleSelectBook}
          onChangeSize={handleChangeBookSize}
          onCheckout={handleCheckout}
        />
      ) : (
        <ModalMyBooks
          open={showMyBooksModal}
          onClose={() => setShowMyBooksModal(false)}
          onSelectBook={handleSelectBook}
          onChangeSize={handleChangeBookSize}
          onCheckout={handleCheckout}
        />
      )}

      {/* ── Мобильные шторки ── */}
      {isMobile ? (
        <>
          <BottomSheetTemplates bookSize={bookSize}
            open={sheetTemplates}
            onClose={() => setSheetTemplates(false)}
            page={page}
            setPageLayout={setPageLayout}
            activePage={activePage}
            activeSide={activeSide}
          />

          <BottomSheetSettings
            open={sheetSettings}
            onClose={() => setSheetSettings(false)}
            activePage={activePage}
            pagePadding={pagePadding}   setPagePadding={setPagePadding}
            cellGap={cellGap}           setCellGap={setCellGap}
            cellRadius={cellRadius}     setCellRadius={setCellRadius}
            syncSettings={syncSettings}
          />
        </>
      ) : (
        <ModalTemplates bookSize={bookSize}
          open={sheetTemplates}
          onClose={() => setSheetTemplates(false)}
          page={page}
          setPageLayout={setPageLayout}
          activePage={activePage}
          activeSide={activeSide}
        />
      )}

      <CoverLayoutPicker
        bookSize={bookSize}
        currentPhotoShape={layoutPickerSide === "front" ? currentBook?.front_cover_photo_shape : currentBook?.back_cover_photo_shape}
        open={!!layoutPickerSide}
        side={layoutPickerSide}
        bookId={bookId}
        bgColor={coverBgColor}
        previewTitle={coverText}
        previewYear={computeCoverDateDisplay({ mode: coverDateMode, year: coverYear, dateFrom: coverDateFrom, dateTo: coverDateTo })}
        dateMode={coverDateMode}
        titleColor={coverTitleColor}
        yearColor={coverAccentColor}
        stickerImage={coverStickerImage}
        currentLayoutId={
          layoutPickerSide === "front"
            ? currentBook?.front_cover_layout
            : currentBook?.back_cover_layout
        }
        hasFrontPhoto={!!coverBackPhotos[4]}
        hasBackPhotos={[0, 1, 2, 3, 5].some(i => coverBackPhotos[i])}
        onClose={() => setLayoutPickerSide(null)}
        onApplied={(updated) => { setCurrentBook(updated); syncCoverPhotosFromBook(updated); }}
      />
    </>
  );
}